import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { useGenerationToastOptional } from '../contexts/GenerationToastContext';
import { useFFmpeg } from '../hooks/useFFmpeg';
import { useBoardHistory, type HistoryRecordOptions } from '../hooks/useBoardHistory';
import { nodeInflight } from '../lib/boardHistory';
import { toMediaUrl } from '../lib/mediaUrl';
import {
  pasteCanvasClipboard,
  readCanvasClipboard,
  readCanvasClipboardFromStorage,
  readCanvasClipboardFromText,
  snapshotCanvasSelection,
  writeCanvasClipboard,
} from '../lib/canvasClipboard';
import { filesFromClipboard } from '../lib/clipboardFiles';
import { isVideoAsset, renameMediaAssetsByUrl, uploadToGallery } from '../lib/mediaGallery';
import {
  estimateCredits,
  getVideoModelById,
  listVideoReferenceModels,
  preferredVideoReferenceModel,
  rankVideoModelsForRequest,
  rateForGeneratedAudio,
  supportsGeneratedAudio,
  videoReferenceSiblingId,
} from '../lib/videoModelCatalog';
import {
  defaultImageModel,
  isOrientationFlip,
  pickImageModel,
  pickImageModelForUseCase,
} from '../lib/imageModelCatalog';
import ImageModelPicker from '../components/image/ImageModelPicker';
import VideoModelPicker, { defaultPickerValue, type VideoModelPickerValue } from '../components/video/VideoModelPicker';
import GalleryMediaPicker from '../components/GalleryMediaPicker';
import CreditConfirmModal from '../components/fragmentador/CreditConfirmModal';
import BoardCanvas, { type BoardCanvasHandle, type NodeMove, type VideoMeta } from '../components/canvas/BoardCanvas';
import type { CanvasAction } from '../components/canvas/CanvasContextMenu';
import CanvasPrompt, { type CanvasPromptHandle, type PromptReference } from '../components/canvas/CanvasPrompt';
import { isCdnUploadEnabled } from '../lib/mediaUrl';
import { uploadViaCdn } from '../lib/uploadMedia';
import CanvasTutorialModal, { markTutorialSeen, tutorialSeen } from '../components/canvas/CanvasTutorialModal';
import CanvasMediaViewer from '../components/canvas/CanvasMediaViewer';
import CropPicker from '../components/canvas/CropPicker';
import CanvasGalleryTray, { loadTrayCollapsed, saveTrayCollapsed, type GalleryDragItem } from '../components/canvas/CanvasGalleryTray';
import CanvasProjectsPanel, { loadProjectsCollapsed, saveProjectsCollapsed } from '../components/canvas/CanvasProjectsPanel';
import CanvasGenerationsPanel, { loadGenerationsCollapsed, saveGenerationsCollapsed } from '../components/canvas/CanvasGenerationsPanel';
import AudioModelPicker, { type AudioModelPickerValue } from '../components/audio/AudioModelPicker';
import { audioModelsByKind, billableCharUnits, defaultAudioModel, pickAudioModel, quoteAudioCredits } from '../lib/audioModelCatalog';
import { generateBoardAudio, narrationCaptions } from '../lib/canvasAudio';
import { defaultCaptionsSettings } from '../types/editor';
import CanvasShareModal from '../components/canvas/CanvasShareModal';
import {
  deleteOwnProject,
  fetchOwnProject,
  fetchSharedProject,
  fetchSharedVersion,
  getShareSettings,
  listOwnProjects,
  listShareMembers,
  listSharedWithMe,
  mergeBoards,
  notifyCanvasShare,
  orgCanShare,
  ownMetaToLocal,
  sameBoard,
  saveSharedBoard,
  shareableChat,
  shareProject,
  subscribeSharedProject,
  upsertOwnProject,
  type ShareRole,
  type ShareSettings,
  type SharedChatMessage,
  type SharedWithMe,
} from '../lib/canvasShare';
import { CheckCircle2, Clapperboard, Copy, Eye, Lock, PlayCircle, Users } from 'lucide-react';
import {
  AUDIO_NODE_H,
  AUDIO_NODE_W,
  audioInputs,
  audioVersions,
  createAudioNode,
  createEditNode,
  editNodeForAudio,
  editNodeForVideo,
  isAudio,
  videoSeconds,
  withNewAudioVersion,
  type BoardAudioKind,
  type BoardAudioNode,
  type BoardVideoEdit,
  type BoardJoinTransition,
  IMAGE_NODE_H,
  IMAGE_NODE_W,
  NODE_GAP_X,
  VIDEO_NODE_H,
  VIDEO_NODE_W,
  boardHasSource,
  boardIsEmpty,
  boardSummaryForAgent,
  canConnect,
  chainForVideo,
  connect,
  layoutBoard,
  applyImageMode,
  connectImagesTo,
  createGalleryVideoNode,
  createCommentNode,
  createImageNode,
  isComment,
  createProjectMeta,
  createVideoNode,
  deleteProjectData,
  projectGeneratedVideoUrls,
  projectDataFromStorageEvent,
  projectsFromStorageEvent,
  disconnect,
  downstreamVideos,
  emptyBoard,
  ensureProjects,
  isImage,
  isVideo,
  loadBoard,
  loadChatBook,
  saveChatBook,
  ensureChatBook,
  activeChatMessages,
  chatHasContent,
  chatTabLabel,
  CHAT_TAB_MAX,
  openNewChat,
  selectChat,
  closeChat,
  replaceActiveChat,
  type CanvasChatBook,
  looseImages,
  readingOrder,
  newId,
  nodeById,
  projectMetaFromContent,
  readActiveProjectId,
  removeNodes,
  removeNodesClean,
  closestBoardAspect,
  isBoardAspect,
  BOARD_ASPECTS,
  saveActiveProjectId,
  saveBoard,
  saveProjects,
  scriptDurationTitle,
  upstreamVideos,
  finalVideoFor,
  flowGroupNodes,
  generationTargets,
  generatorHasImages,
  projectGenerations,
  videoForImage,
  narrowStandaloneImageEdit,
  plannedVideoForPhotos,
  bindStoryboardPhases,
  storyboardImages,
  imageCaption,
  storyScriptKey,
  appendRequest,
  videoSequence,
  videoResumable,
  joinUnits,
  joinNodeFor,
  videoVersions,
  withNewVideoVersion,
  imageVersions,
  withNewImageVersion,
  type Board,
  type CanvasProjectMeta,
  type BoardAspect,
  type BoardImageMode,
  storyboardEdges,
  ancestorImages,
  type BoardImageNode,
  type BoardVideoNode,
  type GeneratorSlot,
  type RemovedNode,
} from '../lib/canvasBoard';
import {
  blobOrDataToFile,
  estimateFlowVideoCredits,
  flowParts,
  flowSegments,
  flowSplitReason,
  flowPromptText,
  generateFlowVideo,
  generateReferenceClip,
  phasesFromGenerator,
  generateImageFromPrompt,
  editStill,
  uploadCanvasStill,
  rebasePhases,
  sceneImageRoles,
  type SceneImageRole,
  generateStill,
  resumeStillJob,
  isCreditError,
  outpaintStill,
  planFlow,
  recomposeStill,
  type NewScenesPosition,
  type PlannedFlow,
} from '../lib/canvasPipeline';
import { isVideoRunning, liveBoard, markVideoRunning, publishLiveBoard, subscribeLiveBoard } from '../lib/canvasLiveBoard';
import { allKeyframesReady, type FragmentadorSession, type FragmentadorSessionPhase } from '../lib/fragmentadorCompile';
import { DIRECT_VIDEO_NOTE, dropKeyframeAsk, keyframeAdviceDeclines, recommendedAnswers, resolveAskSubmission, reorderCanvasImages, sendCanvasAgentTurn, writeComposeImagePrompt, type CanvasChatMessage, type CanvasDebit, type CanvasIntent } from '../lib/canvasAgent';
import { templateLocks } from '../lib/exploreMatch';
import { remixTemplate } from '../lib/exploreTemplates';
import { quoteTemplateCredits } from '../lib/templateQuote';
import { attachTemplateStages, fillTemplateStagePlaceholders, placeTemplateGenerator } from '../lib/templateGenerator';
import { jobStatusLabel, waitForJobResultUrl } from '../lib/jobsApi';
import { CanvasExploreModal, type CanvasRemixResult } from './ExploreGallery';
import {
  keepsReferenceImages,
  keyframeChoice,
  keyframeSceneCount,
  REFERENCE_PHOTOS_NOTE,
  requestedImageMode,
  shouldBuildReferenceScript,
  withoutExtraFrameSuggestions,
  withoutFrameRecommendation,
} from '../lib/canvasImageMode';
import { applyVideoPerImage, isGenerateVideoRequest, isVideoPerImageRequest, quoteVideoIds } from '../lib/canvasPerImage';
import {
  askedAspect,
  composeSourceIds,
  isComposeImageRequest,
  orderComposeSources,
  placeComposedImage,
  verbatimImagePrompt,
  wantsPromptUntouched,
} from '../lib/canvasCompose';
import { startMicRecording, type MicSession } from '../lib/micRecorder';
import {
  compositeSubjectOnBackground,
  probeSolidBackground,
  probeSolidVideoClip,
  rgbToFfmpegHex,
} from '../lib/stillComposite';
import {
  containCanvas,
  cropStillToRatio,
  needsOutpaint,
  planNormalizedSizes,
  ratioFromLabel,
  readContentSize,
  readImageSize,
  resizeStill,
  sameSize,
  thumbnailBase64,
  cropStillRect,
  CENTER_CROP,
  type CropPick,
} from '../lib/stillNormalize';

type NormalizeJob = { nodeId: string; frame: { width: number; height: number }; outpaint: boolean };

type AspectFix = 'crop' | 'expand' | 'keep';
/**
 * `expand` keeps the original pixels and fills the borders; `recompose` redraws the whole frame in one call;
 * `crop` cuts the area the user placed, without AI.
 */
type OutpaintMode = 'expand' | 'recompose' | 'crop';
/** `width`/`height` are the real photo inside any baked bars; `baked` = the file has flat bars. */
type AspectCheck = {
  /** Video ids of the modal, comma-joined. */
  key: string;
  items: Array<{ nodeId: string; width: number; height: number; baked: boolean }> | null;
};

/** Off-aspect refs past this make video models pad the frame with gray bars. */
const ASPECT_TOLERANCE = 0.02;

type CreditModal =
  | null
  | { kind: 'image'; videoIds: string[] }
  | { kind: 'video'; videoIds: string[] }
  | {
      kind: 'outpaint';
      jobs: NormalizeJob[];
      aspect?: BoardAspect;
      summary: string;
      /** Landscape↔portrait: hide expand; only recompose/crop + edit models. */
      preferRecompose?: boolean;
    }
  | { kind: 'audio'; audioId: string }
  | { kind: 'gen-image'; at: { x: number; y: number } }
  /** `instruction` / `promptEn` as the agent wrote them; the modal text replaces both once the user edits it. */
  | {
      kind: 'edit-image';
      nodeIds: string[];
      instruction: string;
      promptEn?: string;
      referenceNodeIds?: string[];
      regenerateVideoId?: string;
    }
  /** New still from other stills. Sources stay as they are and link with the reference arrow. */
  | {
      kind: 'compose-image';
      nodeId: string;
      sourceNodeIds: string[];
      instruction: string;
      promptEn?: string;
    }
  | { kind: 'revise-template'; videoId: string; change: string };
type ImageStepModal = Extract<NonNullable<CreditModal>, { kind: 'outpaint' | 'edit-image' }>;

const DEBIT_LABEL: Record<NonNullable<CreditModal>['kind'], string> = {
  image: 'Confirmar débito das keyframes',
  video: 'Confirmar débito do vídeo',
  audio: 'Confirmar débito do som',
  'gen-image': 'Confirmar débito da imagem',
  'edit-image': 'Confirmar débito da edição',
  'compose-image': 'Confirmar débito da imagem',
  outpaint: 'Confirmar débito da proporção',
  'revise-template': 'Confirmar débito do modelo',
};

/** Every target video is in Referências. Extra stills are not generated in that mode. */
function referenceImageDebit(board: Board, videoIds: string[]): boolean {
  return videoIds.length > 0 && videoIds.every((id) => {
    const v = nodeById(board, id);
    return isVideo(v) && v.imageMode === 'reference';
  });
}

function debitFromModal(modal: NonNullable<CreditModal>, prompt = ''): CanvasDebit {
  const label = DEBIT_LABEL[modal.kind];
  if (modal.kind === 'image' || modal.kind === 'video') return { kind: modal.kind, label, videoIds: modal.videoIds };
  if (modal.kind === 'audio') return { kind: 'audio', label, audioId: modal.audioId };
  if (modal.kind === 'gen-image') return { kind: 'gen-image', label, at: modal.at, prompt };
  if (modal.kind === 'edit-image') {
    return {
      kind: 'edit-image',
      label,
      nodeIds: modal.nodeIds,
      instruction: modal.instruction,
      ...(modal.promptEn ? { promptEn: modal.promptEn } : {}),
      ...(modal.referenceNodeIds?.length ? { referenceNodeIds: modal.referenceNodeIds } : {}),
      ...(modal.regenerateVideoId ? { regenerateVideoId: modal.regenerateVideoId } : {}),
    };
  }
  if (modal.kind === 'compose-image') {
    return {
      kind: 'compose-image',
      label,
      nodeId: modal.nodeId,
      sourceNodeIds: modal.sourceNodeIds,
      instruction: modal.instruction,
      ...(modal.promptEn ? { promptEn: modal.promptEn } : {}),
    };
  }
  if (modal.kind === 'revise-template') return { kind: 'revise-template', label, videoId: modal.videoId, change: modal.change };
  return {
    kind: 'outpaint',
    label,
    jobs: modal.jobs,
    ...(modal.aspect ? { aspect: modal.aspect } : {}),
    summary: modal.summary,
    ...(modal.preferRecompose ? { preferRecompose: true } : {}),
  };
}

function modalFromDebit(debit: CanvasDebit): NonNullable<CreditModal> | null {
  if (debit.kind === 'image' || debit.kind === 'video') return debit.videoIds?.length ? { kind: debit.kind, videoIds: debit.videoIds } : null;
  if (debit.kind === 'audio') return debit.audioId ? { kind: 'audio', audioId: debit.audioId } : null;
  if (debit.kind === 'gen-image') return debit.at ? { kind: 'gen-image', at: debit.at } : null;
  if (debit.kind === 'edit-image') {
    return debit.nodeIds?.length
      ? {
          kind: 'edit-image',
          nodeIds: debit.nodeIds,
          instruction: debit.instruction || '',
          ...(debit.promptEn ? { promptEn: debit.promptEn } : {}),
          ...(debit.referenceNodeIds?.length ? { referenceNodeIds: debit.referenceNodeIds } : {}),
          ...(debit.regenerateVideoId ? { regenerateVideoId: debit.regenerateVideoId } : {}),
        }
      : null;
  }
  if (debit.kind === 'compose-image') {
    return debit.nodeId && debit.sourceNodeIds?.length
      ? {
          kind: 'compose-image',
          nodeId: debit.nodeId,
          sourceNodeIds: debit.sourceNodeIds,
          instruction: debit.instruction || '',
          ...(debit.promptEn ? { promptEn: debit.promptEn } : {}),
        }
      : null;
  }
  if (debit.kind === 'revise-template') {
    return debit.videoId && debit.change ? { kind: 'revise-template', videoId: debit.videoId, change: debit.change } : null;
  }
  return debit.jobs?.length
    ? {
        kind: 'outpaint',
        jobs: debit.jobs,
        summary: debit.summary || '',
        ...(isBoardAspect(debit.aspect) ? { aspect: debit.aspect } : {}),
        ...(debit.preferRecompose ? { preferRecompose: true } : {}),
      }
    : null;
}

const AUDIO_LABELS: Record<BoardAudioKind, { title: string; field: string; placeholder: string; confirm: string }> = {
  voice: { title: 'Gerar locução', field: 'Texto da locução', placeholder: 'O que a voz vai falar…', confirm: 'Gerar locução' },
  music: { title: 'Gerar música de fundo', field: 'Estilo da música', placeholder: 'Ex.: piano cinematográfico suave, sofisticado…', confirm: 'Gerar música' },
  sfx: { title: 'Gerar efeito sonoro', field: 'Som a gerar', placeholder: 'Ex.: ondas do mar, vento leve, cidade ao fundo…', confirm: 'Gerar som' },
};

/** Spoken Portuguese pace used to size narration to the video. */
const WORDS_PER_SECOND = 2.5;

/** Music model that plays on top of an existing voice track (Add Instrumental). */
function instrumentalPick(): AudioModelPickerValue | null {
  const m = audioModelsByKind('music').find((x) => x.requiresSourceAudio);
  return m ? { voiceModelId: m.id, voiceId: m.defaultVoice } : null;
}

// Nano Banana returns the gray borders untouched instead of outpainting.
const OUTPAINT_DEFAULT_MODEL = 'poyo-wan27-image';
// Wan 2.7 renders a fixed 1024×576 frame; Seedream's edit endpoint keeps the photo's shape.
const EDIT_DEFAULT_MODEL = 'poyo-seedream-lite';
// Large reframes (16:9 → 9:16): Seedream kept the scene and colors; Nano Banana and Kling O3 drifted.
const RECOMPOSE_DEFAULT_MODEL = 'poyo-seedream-lite';
/** Edited image whose shape drifts past this from the source is flagged to the user. */
const EDIT_ASPECT_TOLERANCE = 0.03;
const NORMALIZE_CONCURRENCY = 4;

type BoardPatchOpts = HistoryRecordOptions & {
  /** Drop the patch when the user already undid past the in-flight job. */
  ifInflight?: boolean;
};

const suggestionsFor = (aspect: BoardAspect) => [
  'Monte um roteiro com base nessas imagens',
  `Deixe todas as imagens em ${aspect}`,
  'Ajuste todas as imagens para a mesma resolução sem perder detalhes',
];

/** Docked conversation width. The drag handle clamps between these; keep the CSS fallback (`--tb-chat-w`) in sync. */
const CHAT_W_DEFAULT = 440;
/** Composer row: three 40px buttons plus a usable textarea. */
const CHAT_W_MIN = 320;
/** Past this the bubbles (already 92% of the panel) don't get easier to read, and the board loses too much. */
const CHAT_W_MAX = 640;
/** Canvas that stays visible beside the panel, so nodes aren't pushed fully off-screen. */
const BOARD_MIN = 480;
const CHAT_W_KEY = 'seu.video.telaEmBranco.chatWidth';

function chatWidthBounds(winW: number): { min: number; max: number } {
  const max = Math.max(CHAT_W_MIN, Math.min(CHAT_W_MAX, winW - BOARD_MIN));
  return { min: CHAT_W_MIN, max };
}

function clampChatWidth(w: number, winW: number): number {
  const { min, max } = chatWidthBounds(winW);
  return Math.round(Math.min(max, Math.max(min, w)));
}
const GENERATE_VIDEO_SUGGESTION = 'Gere o vídeo';
const GENERATE_FROM_IMAGES_SUGGESTION = 'Gere o vídeo com essas imagens';
const isGenerateSuggestion = (s: string) => /^ger(e|ar) (o |um )?v[ií]deo/i.test(s.trim());

/** A flow the user can still render: planned (or with images to plan from) and not generated or generating. */
function hasVideoToGenerate(board: Board): boolean {
  return board.nodes.filter(isVideo).some((v) =>
    !v.edit && !v.fromGallery && !v.clipUrl && v.status !== 'generating' && v.status !== 'joining'
    && (v.planned || chainForVideo(board, v.id).length > 0 || generatorHasImages(v)));
}

/** Generating the video is always the first recommendation while there is one to generate. */
function withGenerateFirst(list: string[], board: Board, willPlan = false): string[] {
  if (!willPlan && !hasVideoToGenerate(board)) return list;
  return [GENERATE_VIDEO_SUGGESTION, ...list.filter((s) => !isGenerateSuggestion(s))].slice(0, 3);
}

function perImageReply(created: number, updated: number): string {
  const quote = ' Confirme o débito na janela para gerar.';
  if (created && updated) {
    return `Separei um vídeo por imagem (${created} novo${created > 1 ? 's' : ''} e ${updated} atualizado${updated > 1 ? 's' : ''}). Sem selecionar o vídeo, a foto já diz qual fluxo muda.${quote}`;
  }
  if (updated) {
    return updated === 1
      ? `Atualizei o vídeo dessa imagem. Não precisa selecionar o vídeo: a foto já identifica o fluxo.${quote}`
      : `Atualizei o vídeo de cada imagem (${updated}). Sem selecionar o vídeo, cada foto indica o fluxo dela.${quote}`;
  }
  return created === 1
    ? `Criei um vídeo para essa imagem. O pedido está no gerador.${quote}`
    : `Criei ${created} vídeos, um para cada imagem. O pedido está no gerador de cada uma. Sem selecionar o vídeo, a foto já diz qual fluxo muda.${quote}`;
}

/** Short ask to tidy the canvas: handled on the client so it works without a new agent deploy. */
function isLayoutRequest(text: string): boolean {
  const t = text.trim().toLowerCase().replace(/[.!?…]+$/, '');
  if (/(roteiro|c[aâ]mera|ordem|cena|keyframe|gerar|propor|formato|juntar)/.test(t)) return false;
  return /(ajuste|ajustar|organiz[ae]|alinhe|alinhar|arrume|arrumar|reorganiz[ae]|auto[\s-]?layout).{0,24}(fluxo|quadro|layout|n[oó]s)/.test(t)
    || /^(auto[\s-]?layout|layout autom[aá]tico)$/.test(t);
}

/** Edit / retouch of existing stills — does not need the pre-turn vision captions used to order scenes. */
function looksLikeImageEdit(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (!t) return false;
  if (/(roteiro|keyframe|gerar (o |um )?v[ií]deo|monte um|orden|juntar|locu[cç]|m[uú]sica|coment[aá]ri)/.test(t)) return false;
  // Video color / B&W goes to edit_video — not still edit / captions.
  if (/\bv[ií]deo\b/.test(t)) return false;
  if (/(preto\s*e\s*branco|black\s*and\s*white|grayscale|em\s+pb\b)/.test(t) && !/\b(imagem|foto|logo|png)\b/.test(t)) {
    return false;
  }
  return /(remov|tir(e|ar)|apag|edit|fundo|background|troqu|substitu|outpaint|expand|recort|sem (o |a )?(logo|texto|pessoa|fundo)|deix(e|ar) .{0,40}(branco|preto|limpo|sem)|coloc|ponha|p[oõ]e\b|adicion|insir|inclu)/i.test(t);
}

/** The request that led to the keyframe question. */
function briefBeforeAsk(messages: CanvasChatMessage[], messageId: string): string {
  const idx = messages.findIndex((m) => m.id === messageId);
  for (let i = idx - 1; i >= 0; i--) {
    const text = messages[i]?.role === 'user' ? messages[i].text.trim() : '';
    if (text && !/^Respostas do formulário:/i.test(text)) return text.slice(0, 2000);
  }
  return 'Vídeo com as imagens da tela';
}

/**
 * Photos the keyframe question was about: the ones that still need a script.
 * Same pool the agent saw (selection, otherwise loose, otherwise every ready photo).
 */
function photosForNewVideo(board: Board, focusIds: string[]): string[] {
  const ready = board.nodes.filter(isImage).filter((n) => Boolean(n.phase.imageUrl) && n.phase.status === 'ready');
  const byPos = (p: BoardImageNode, q: BoardImageNode) => p.x - q.x || p.y - q.y;
  const selected = ready.filter((n) => focusIds.includes(n.id)).sort(byPos);
  const loose = looseImages(board).filter((n) => ready.includes(n));
  const pool = (selected.length >= 2 ? selected : loose.length ? loose : ready.sort(byPos))
    .filter((n) => !videoForImage(board, n.id)?.planned);
  const images = pool.length >= 2 ? pool : (selected.length >= 2 ? selected : loose.length ? loose : ready.sort(byPos));
  return images.slice(0, 10).map((n) => n.id);
}

/** Ready photos for a Referências script: the ones already linked, else the selection, else every photo. */
function referencePhotoIds(board: Board, anchorId: string | undefined, focusIds: string[]): string[] {
  const ready = (n: Board['nodes'][number]): n is BoardImageNode => isImage(n) && Boolean(n.phase.imageUrl);
  if (anchorId) {
    const linked = chainForVideo(board, anchorId).filter((n) => n.phase.imageUrl);
    if (linked.length >= 2) return linked.map((n) => n.id);
  }
  const byReading = (a: BoardImageNode, b: BoardImageNode) => a.y - b.y || a.x - b.x;
  const selected = board.nodes.filter(ready).filter((n) => focusIds.includes(n.id)).sort(byReading);
  if (selected.length >= 2) return selected.map((n) => n.id);
  return board.nodes.filter(ready).sort(byReading).map((n) => n.id);
}

/** Opening chips before the first message, by how far the board already is. */
function starterSuggestions(board: Board): string[] {
  const videos = board.nodes.filter(isVideo);
  if (videos.some((v) => v.edit && v.videoUrl)) {
    return ['Coloque legendas com o texto da locução', 'Adicione um fade rápido entre os vídeos', 'Aplique uma cor cinematográfica'];
  }
  if (videos.some((v) => v.videoUrl && upstreamVideos(board, v.id).length)) {
    return ['Gere uma locução de venda para o vídeo final', 'Coloque uma música de fundo suave', 'Adicione um fade rápido entre os vídeos'];
  }
  if (videos.filter((v) => v.videoUrl).length > 1) {
    return ['Junte todos os vídeos em um só', 'Gere uma locução de venda para o vídeo', 'Coloque uma música de fundo suave'];
  }
  if (hasVideoToGenerate(board)) return withGenerateFirst(suggestionsFor(board.aspectRatio), board);
  return board.nodes.some(isImage) ? [GENERATE_FROM_IMAGES_SUGGESTION, ...suggestionsFor(board.aspectRatio)].slice(0, 3) : [];
}

type ShareSync = { projectId: string; role: ShareRole; ownerName?: string };

const SHARE_PUSH_DELAY_MS = 1200;
/** Debounce for saving the owner's private/shared cloud copy (cross-device). */
const CLOUD_PUSH_DELAY_MS = 1800;
const SHARE_POLL_MS = 20000;

/**
 * Saves `local` over the shared copy. When someone saved first, merges their board in (3-way against `base`)
 * and tries again. Null when the project is no longer shared with the caller.
 */
async function saveSharedMerging(
  projectId: string,
  local: Board,
  base: Board,
  version: number,
  meta: CanvasProjectMeta | undefined,
  chat?: SharedChatMessage[] | null,
): Promise<{ saved: Board; version: number; chat?: SharedChatMessage[] } | null> {
  let board = local;
  let from = base;
  let at = version;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const v = await saveSharedBoard(projectId, board, meta, at, chat);
    if (v != null) return { saved: board, version: v, ...(chat ? { chat } : {}) };
    const remote = await fetchSharedProject(projectId);
    if (!remote) return null;
    if (remote.role === 'view') return { saved: remote.board, version: remote.version, ...(remote.chat ? { chat: remote.chat } : {}) };
    board = mergeBoards(from, board, remote.board);
    from = remote.board;
    at = remote.version;
  }
  throw new Error('Muitas pessoas editando ao mesmo tempo. Tente de novo em instantes.');
}

function applySharedChat(
  org: string,
  projectId: string,
  chat: SharedChatMessage[] | undefined,
  setMessages: (msgs: CanvasChatMessage[]) => void,
  messagesRef: { current: CanvasChatMessage[] },
) {
  if (!chat?.length) return;
  const thread = chat.map((m) => ({ ...m, busy: false })) as CanvasChatMessage[];
  messagesRef.current = thread;
  setMessages(thread);
  saveChatBook(org, projectId, replaceActiveChat(loadChatBook<CanvasChatMessage>(org, projectId), thread));
}

function fileTitle(f: File, fallback: string): string {
  return f.name.replace(/\.[^.]+$/, '') || fallback;
}

function readMediaSeconds(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('audio');
    const done = (v: number) => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(v) && v > 0 ? v : 0);
    };
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(el.duration);
    el.onerror = () => done(0);
    el.src = url;
  });
}

function phaseNeedsImage(p: FragmentadorSessionPhase): boolean {
  return !p.imageUrl || p.status === 'pending' || p.status === 'failed';
}

/**
 * What the user said in this round, oldest first: back to the last agent reply that asked nothing
 * (a clarifying question keeps the round open). Form-answer summaries are not requests.
 */
function turnUserTexts(thread: CanvasChatMessage[], replyId: string): string[] {
  const texts: string[] = [];
  for (let i = thread.length - 1; i >= 0; i--) {
    const m = thread[i];
    if (m.contextBreak) break;
    if (m.role === 'assistant') {
      if (m.id === replyId || m.ask) continue;
      break;
    }
    if (!m.formAnswer && m.text.trim()) texts.unshift(m.text.trim());
  }
  return texts;
}

function defaultVideoPick(): VideoModelPickerValue {
  const d = defaultPickerValue('i2v');
  const preferred = getVideoModelById('poyo-seedance25') || getVideoModelById('poyo-wan3');
  if (!preferred) return { ...d, durationSeconds: 8, generateAudio: false };
  return {
    ...d,
    videoModelId: preferred.id,
    resolutionId: preferred.resolutions.find((r) => r.id === '720p')?.id || preferred.resolutions[0]?.id || d.resolutionId,
    durationSeconds: Math.min(Math.max(8, preferred.durationMin), preferred.durationMax),
    generateAudio: false,
  };
}

export default function TelaEmBranco() {
  const { org, role, refreshOrg } = useOrg();
  const orgId = org?.id || '';
  const balance = Math.max(0, Number(org?.available_credits ?? 0));
  const ffmpeg = useFFmpeg();
  const generationToast = useGenerationToastOptional();
  const canvasRef = useRef<BoardCanvasHandle>(null);

  const [board, setBoard] = useState<Board>(emptyBoard);
  const boardRef = useRef<Board>(board);
  const { commit: commitHistory, undo: historyUndo, redo: historyRedo, reset: resetHistory, canUndo, canRedo } = useBoardHistory();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [messages, setMessages] = useState<CanvasChatMessage[]>([]);
  const [chatBook, setChatBook] = useState<CanvasChatBook<CanvasChatMessage>>({ activeId: '', chats: [] });
  const chatBookRef = useRef(chatBook);
  const [exploreModalId, setExploreModalId] = useState<string | null>(null);
  const messagesRef = useRef<CanvasChatMessage[]>(messages);
  /** Nodes deleted since the agent's last turn, so it drops them from its memory. */
  const removedRef = useRef<RemovedNode[]>([]);
  const promptRef = useRef<CanvasPromptHandle>(null);
  // Mirrored on <body> so the fixed top-right controls (Layout's avatar) can slide away from the chat panel.
  const [chatDocked, setChatDocked] = useState(false);
  const [chatWidth, setChatWidth] = useState(() => {
    const saved = Number(localStorage.getItem(CHAT_W_KEY));
    const w = clampChatWidth(Number.isFinite(saved) && saved > 0 ? saved : CHAT_W_DEFAULT, window.innerWidth);
    document.body.style.setProperty('--tb-chat-w', `${w}px`);
    return w;
  });
  const chatWidthRef = useRef(chatWidth);
  chatWidthRef.current = chatWidth;
  const setChatWidthClamped = useCallback((next: number) => {
    const clamped = clampChatWidth(next, window.innerWidth);
    const prev = chatWidthRef.current;
    if (clamped === prev) return;
    chatWidthRef.current = clamped;
    setChatWidth(clamped);
    try { localStorage.setItem(CHAT_W_KEY, String(clamped)); } catch { /* private mode */ }
    if (document.body.classList.contains('tb-chat-docked')) {
      canvasRef.current?.panBy(-(clamped - prev) / 2, 0);
    }
  }, []);
  const chatDockedPrevRef = useRef<boolean | null>(null);
  useEffect(() => {
    document.body.style.setProperty('--tb-chat-w', `${chatWidth}px`);
  }, [chatWidth]);
  useEffect(() => {
    const onResize = () => setChatWidthClamped(chatWidthRef.current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setChatWidthClamped]);
  useEffect(() => {
    const prev = chatDockedPrevRef.current;
    chatDockedPrevRef.current = chatDocked;
    const body = document.body;
    if (prev === null) {
      // First paint: no slide-in, so the initial fitView measures the final viewport width.
      body.classList.add('tb-no-anim');
      body.classList.toggle('tb-chat-docked', chatDocked);
      void body.offsetWidth;
      requestAnimationFrame(() => body.classList.remove('tb-no-anim'));
    } else if (prev !== chatDocked) {
      body.classList.toggle('tb-chat-docked', chatDocked);
      // The board shrinks/grows by the panel width; shift the camera by half so the content stays centered.
      const w = chatWidthRef.current;
      canvasRef.current?.panBy(chatDocked ? -w / 2 : w / 2, 0);
    }
  }, [chatDocked]);
  useEffect(() => () => {
    document.body.classList.remove('tb-chat-docked', 'tb-no-anim', 'tb-chat-dragging');
    document.body.style.removeProperty('--tb-chat-w');
  }, []);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const micRef = useRef<MicSession | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryAccept, setGalleryAccept] = useState<'image' | 'video' | 'both'>('both');
  const openGallery = useCallback((accept: 'image' | 'video' | 'both' = 'both') => {
    setGalleryAccept(accept);
    setGalleryOpen(true);
  }, []);
  const [imageModelId, setImageModelId] = useState(defaultImageModel().id);
  const [outpaintModelId, setOutpaintModelId] = useState(() => pickImageModelForUseCase('outpaint', OUTPAINT_DEFAULT_MODEL).id);
  const [editModelId, setEditModelId] = useState(() => pickImageModelForUseCase('edit', EDIT_DEFAULT_MODEL).id);
  const [outpaintMode, setOutpaintMode] = useState<OutpaintMode>('expand');
  /** Crop placement per image node for the `crop` mode of the open modal. */
  const [cropPicks, setCropPicks] = useState<Record<string, CropPick>>({});
  const [recomposeModelId, setRecomposeModelId] = useState(() => pickImageModelForUseCase('recompose', RECOMPOSE_DEFAULT_MODEL).id);
  const [editText, setEditText] = useState('');
  const [videoPick, setVideoPick] = useState<VideoModelPickerValue>(defaultVideoPick);
  const [creditModal, setCreditModal] = useState<CreditModal>(null);
  const creditModalRef = useRef<CreditModal>(null);
  useEffect(() => { creditModalRef.current = creditModal; }, [creditModal]);
  useEffect(() => {
    if (creditModal?.kind === 'outpaint' && creditModal.preferRecompose && outpaintMode === 'expand') {
      setOutpaintMode('recompose');
    }
  }, [creditModal, outpaintMode]);
  /** Flows whose keyframes just finished: shows the "generate the final video" card. */
  const [keyframesDone, setKeyframesDone] = useState<string[] | null>(null);
  /** Pixel overlay on a node before its status becomes `generating` (sync can take a while). */
  const [assemblingIds, setAssemblingIds] = useState<string[]>([]);
  const [genImagePrompt, setGenImagePrompt] = useState('');
  const genImagePromptRef = useRef(genImagePrompt);
  genImagePromptRef.current = genImagePrompt;
  /** Image/video node open in the full-screen viewer. */
  const [viewerId, setViewerId] = useState<string | null>(null);
  /** One sound modal at a time: sounds requested together open one after the other. */
  const audioModalOpenRef = useRef(false);
  const rememberDebitRef = useRef<(modal: NonNullable<CreditModal>, msgId?: string) => void>(() => undefined);
  const audioModalQueueRef = useRef<string[]>([]);
  /** Expand + edit asked together: the next confirmation waits until the previous step finishes. */
  const imageStepBusyRef = useRef(false);
  const imageStepRunningRef = useRef(false);
  const imageStepQueueRef = useRef<ImageStepModal[]>([]);
  const [audioPicks, setAudioPicks] = useState<Record<BoardAudioKind, AudioModelPickerValue>>(() => ({
    voice: { voiceModelId: defaultAudioModel('voice').id, voiceId: defaultAudioModel('voice').defaultVoice },
    music: { voiceModelId: defaultAudioModel('music').id, voiceId: defaultAudioModel('music').defaultVoice },
    sfx: { voiceModelId: defaultAudioModel('sfx').id, voiceId: defaultAudioModel('sfx').defaultVoice },
  }));
  const [audioText, setAudioText] = useState('');
  const [audioLoop, setAudioLoop] = useState(false);
  const [audioSourceId, setAudioSourceId] = useState('');
  const [aspectCheck, setAspectCheck] = useState<AspectCheck | null>(null);
  const [aspectFix, setAspectFix] = useState<AspectFix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedOrg, setLoadedOrg] = useState('');
  const loadedOrgRef = useRef(loadedOrg);
  loadedOrgRef.current = loadedOrg;
  const [tutorialOpen, setTutorialOpen] = useState(() => !tutorialSeen());
  const closeTutorial = useCallback(() => {
    markTutorialSeen();
    setTutorialOpen(false);
  }, []);
  const closeViewer = useCallback(() => setViewerId(null), []);
  const viewerNode = useMemo(() => {
    const n = viewerId ? nodeById(board, viewerId) : undefined;
    return isImage(n) || isVideo(n) ? n : undefined;
  }, [board, viewerId]);
  const [trayCollapsed, setTrayCollapsed] = useState(loadTrayCollapsed);
  const changeTrayCollapsed = useCallback((next: boolean) => {
    saveTrayCollapsed(next);
    setTrayCollapsed(next);
  }, []);
  const boardUrls = useMemo(
    () => new Set(
      board.nodes
        .map((n) => (isImage(n) ? n.phase.imageUrl : isVideo(n) && n.fromGallery ? n.clipUrl : undefined))
        .filter((u): u is string => Boolean(u)),
    ),
    [board.nodes],
  );
  const hasSource = useMemo(() => boardHasSource(board), [board]);

  const [projects, setProjects] = useState<CanvasProjectMeta[]>([]);
  const [activeProjectId, setActiveProjectId] = useState('');
  const activeProjectIdRef = useRef(activeProjectId);
  activeProjectIdRef.current = activeProjectId;
  const [projectsCollapsed, setProjectsCollapsed] = useState(loadProjectsCollapsed);
  const changeProjectsCollapsed = useCallback((next: boolean) => {
    saveProjectsCollapsed(next);
    setProjectsCollapsed(next);
  }, []);
  const [generationsCollapsed, setGenerationsCollapsed] = useState(loadGenerationsCollapsed);
  const changeGenerationsCollapsed = useCallback((next: boolean) => {
    saveGenerationsCollapsed(next);
    setGenerationsCollapsed(next);
  }, []);
  const generations = useMemo(() => projectGenerations(board), [board]);
  const busyVideoIds = useMemo(
    () => new Set(board.nodes.filter(isVideo).filter((n) => n.status === 'generating' || n.status === 'joining').map((n) => n.id)),
    [board.nodes],
  );
  /** False right after a project opens, so merely opening it doesn't bump "updated". */
  const touchProjectRef = useRef(false);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  const canShare = orgCanShare(org);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharedWithMe, setSharedWithMe] = useState<SharedWithMe[]>([]);
  const sharedWithMeRef = useRef(sharedWithMe);
  sharedWithMeRef.current = sharedWithMe;
  const [sync, setSyncState] = useState<ShareSync | null>(null);
  const syncRef = useRef<ShareSync | null>(null);
  /** Board and version last known to be on the server; local edits are measured against it. */
  const syncBaseRef = useRef<Board | null>(null);
  const syncVersionRef = useRef(0);
  const syncQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const syncPauseUntilRef = useRef(0);
  const readOnly = Boolean(sync && sync.projectId === activeProjectId && sync.role === 'view');
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const activeShared = sharedWithMe.find((s) => s.id === activeProjectId);
  const activeOwnMeta = projects.find((p) => p.id === activeProjectId);

  const setSync = useCallback((next: ShareSync | null) => {
    syncRef.current = next;
    if (!next) syncBaseRef.current = null;
    setSyncState(next);
  }, []);

  /** Sync steps run one at a time, so a pull never interleaves with a push. */
  const runSync = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const next = syncQueueRef.current.then(fn, fn);
    syncQueueRef.current = next.catch(() => undefined);
    return next;
  }, []);

  const projectTitleRef = useRef('');
  projectTitleRef.current = activeOwnMeta?.title || activeShared?.title || '';

  const applyProject = useCallback((org: string, projectId: string, override?: Board) => {
    // A generation started on this tab keeps writing after the page unmounts. Prefer that
    // board over the stored one, which would otherwise mark a not-yet-queued video as failed.
    const b = override || liveBoard(org, projectId) || loadBoard(org, projectId);
    boardRef.current = b;
    setBoard(b);
    resetHistory();
    if (b.imageModelId) setImageModelId(b.imageModelId);
    const book = loadChatBook<CanvasChatMessage>(org, projectId);
    chatBookRef.current = book;
    setChatBook(book);
    saveChatBook(org, projectId, book);
    const thread = activeChatMessages(book);
    messagesRef.current = thread;
    setMessages(thread);
    setSelectedIds([]);
    setViewerId(null);
    setCreditModal(null);
    audioModalOpenRef.current = false;
    audioModalQueueRef.current = [];
    imageStepBusyRef.current = false;
    imageStepRunningRef.current = false;
    imageStepQueueRef.current = [];
    removedRef.current = [];
    setKeyframesDone(null);
    touchProjectRef.current = false;
    setActiveProjectId(projectId);
    saveActiveProjectId(org, projectId);
    if (b.nodes.length) window.setTimeout(() => canvasRef.current?.fitView(), 60);
  }, [resetHistory]);

  /** Shared project that was open on the last visit or opened via ?p=; reopened once "shared with me" loads. */
  const pendingSharedRef = useRef('');

  useEffect(() => {
    if (!orgId || loadedOrg === orgId) return;
    const fromLink = new URLSearchParams(window.location.search).get('p')?.trim() || '';
    const wanted = fromLink || readActiveProjectId(orgId);
    const { projects: list, activeId } = ensureProjects(orgId);
    pendingSharedRef.current = wanted && wanted !== activeId ? wanted : '';
    setProjects(list);
    applyProject(orgId, activeId);
    setLoadedOrg(orgId);
    if (fromLink) {
      const url = new URL(window.location.href);
      url.searchParams.delete('p');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, [orgId, loadedOrg, applyProject]);

  /** Pull own projects from the DB and merge with localStorage (newer updatedAt wins). */
  useEffect(() => {
    if (!loadedOrg) return undefined;
    let cancelled = false;
    const org = loadedOrg;
    const projectHasLocalContent = (id: string) => {
      const b = loadBoard(org, id);
      const book = loadChatBook<CanvasChatMessage>(org, id);
      return !boardIsEmpty(b) || book.chats.some((c) => chatHasContent(c.messages));
    };
    void (async () => {
      try {
        const remoteList = await listOwnProjects(org);
        if (cancelled) return;
        const localList = projectsRef.current;
        const localById = new Map(localList.map((p) => [p.id, p]));
        const remoteById = new Map(remoteList.map((r) => [r.id, r]));
        const ids = Array.from(new Set([
          ...localList.map((p) => p.id),
          ...remoteList.map((r) => r.id),
        ]));
        const merged: CanvasProjectMeta[] = [];
        const uploadIds: string[] = [];
        const pulledActive = { id: '' };

        for (const id of ids) {
          if (cancelled) return;
          const local = localById.get(id);
          const remote = remoteById.get(id);
          if (local && remote) {
            if (remote.updatedAt > (local.updatedAt || 0)) {
              const full = await fetchOwnProject(id);
              if (cancelled) return;
              if (full) {
                saveBoard(org, id, full.board);
                if (full.ownerChat != null) {
                  saveChatBook(org, id, ensureChatBook<CanvasChatMessage>(full.ownerChat));
                }
                merged.push(ownMetaToLocal(full));
                if (id === activeProjectIdRef.current) pulledActive.id = id;
              } else {
                merged.push({ ...local, shared: remote.shared || local.shared });
              }
            } else {
              merged.push({
                ...local,
                shared: remote.shared || local.shared,
                updatedAt: Math.max(local.updatedAt || 0, remote.updatedAt),
              });
              if (projectHasLocalContent(id)) uploadIds.push(id);
            }
          } else if (remote) {
            const full = await fetchOwnProject(id);
            if (cancelled) return;
            if (!full) continue;
            saveBoard(org, id, full.board);
            if (full.ownerChat != null) {
              saveChatBook(org, id, ensureChatBook<CanvasChatMessage>(full.ownerChat));
            }
            merged.push(ownMetaToLocal(full));
            if (id === activeProjectIdRef.current) pulledActive.id = id;
          } else if (local) {
            // Drop empty local-only placeholders when the cloud already has real projects.
            if (!projectHasLocalContent(id) && remoteList.length) continue;
            merged.push(local);
            if (projectHasLocalContent(id)) uploadIds.push(id);
          }
        }

        if (cancelled) return;
        if (!merged.length) merged.push(createProjectMeta());
        merged.sort((a, b) => b.updatedAt - a.updatedAt);
        saveProjects(org, merged);
        projectsRef.current = merged;
        setProjects(merged);

        if (pulledActive.id && pulledActive.id === activeProjectIdRef.current) {
          applyProject(org, pulledActive.id);
        }

        for (const id of uploadIds) {
          if (cancelled) return;
          const meta = merged.find((p) => p.id === id);
          if (!meta) continue;
          try {
            await upsertOwnProject({
              projectId: id,
              orgId: org,
              meta,
              board: loadBoard(org, id),
              ownerChat: loadChatBook<CanvasChatMessage>(org, id),
            });
          } catch (err) {
            console.warn('upsert_own_canvas_project migrate', err);
          }
        }
      } catch (err) {
        console.warn('list_own_canvas_projects', err);
      }
    })();
    return () => { cancelled = true; };
  }, [loadedOrg, applyProject]);

  useEffect(() => {
    if (loadedOrg && activeProjectId) saveBoard(loadedOrg, activeProjectId, board);
  }, [board, loadedOrg, activeProjectId]);

  // Picks up progress written by a generation that outlived the previous visit to this page.
  useEffect(() => {
    if (!loadedOrg || !activeProjectId) return undefined;
    const unsubscribe = subscribeLiveBoard(loadedOrg, activeProjectId, (next) => {
      if (next === boardRef.current) return;
      boardRef.current = next;
      setBoard(next);
    });
    const current = liveBoard(loadedOrg, activeProjectId);
    if (current && current !== boardRef.current) {
      boardRef.current = current;
      setBoard(current);
    }
    return unsubscribe;
  }, [loadedOrg, activeProjectId]);

  useEffect(() => {
    if (!loadedOrg || !activeProjectId || !chatBookRef.current.activeId) return;
    const next = replaceActiveChat(
      chatBookRef.current,
      messages.map((m) => (m.busy ? { ...m, busy: false } : m)),
    );
    chatBookRef.current = next;
    setChatBook(next);
    saveChatBook(loadedOrg, activeProjectId, next);
  }, [messages, loadedOrg, activeProjectId]);

  useEffect(() => {
    if (!loadedOrg || !activeProjectId) return;
    const touch = touchProjectRef.current;
    touchProjectRef.current = true;
    const firstNamed = chatBookRef.current.chats
      .map((c) => c.messages.find((m) => m.role === 'user' && !m.formAnswer && m.text?.trim())?.text)
      .find((text) => Boolean(text?.trim()));
    const firstUserText = firstNamed || messages.find((m) => m.role === 'user')?.text;
    setProjects((list) => {
      const next = list.map((p) => (p.id === activeProjectId ? projectMetaFromContent(p, board, firstUserText, touch) : p));
      saveProjects(loadedOrg, next);
      return next;
    });
  }, [board, messages, chatBook, loadedOrg, activeProjectId]);

  useEffect(() => {
    if (!loadedOrg) return;
    const onStorage = (e: StorageEvent) => {
      const data = activeProjectId
        ? projectDataFromStorageEvent<CanvasChatMessage>(loadedOrg, activeProjectId, e)
        : null;
      if (data && 'board' in data) {
        boardRef.current = data.board;
        setBoard(data.board);
        return;
      }
      if (data && 'book' in data) {
        chatBookRef.current = data.book;
        setChatBook(data.book);
        messagesRef.current = data.thread;
        setMessages(data.thread);
        return;
      }
      const list = projectsFromStorageEvent(loadedOrg, e);
      if (!list) return;
      setProjects((cur) => {
        const active = cur.find((p) => p.id === activeProjectId);
        return !active || list.some((p) => p.id === activeProjectId) ? list : [active, ...list];
      });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [loadedOrg, activeProjectId]);

  // ── Company sharing (Empresarial) ────────────────────────────

  const refreshSharedWithMe = useCallback(() => {
    listSharedWithMe()
      .then((list) => {
        setSharedWithMe(list);
        const pending = pendingSharedRef.current;
        pendingSharedRef.current = '';
        if (pending && loadedOrg && list.some((s) => s.id === pending)) applyProject(loadedOrg, pending);
      })
      .catch(() => undefined);
  }, [loadedOrg, applyProject]);

  useEffect(() => {
    if (!loadedOrg) return;
    refreshSharedWithMe();
    const onFocus = () => refreshSharedWithMe();
    window.addEventListener('focus', onFocus);
    const t = window.setInterval(refreshSharedWithMe, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(t);
    };
  }, [loadedOrg, refreshSharedWithMe]);

  /** The project stopped being shared (or the user lost access) while open. */
  const lostShared = (s: ShareSync) => {
    setSync(null);
    if (s.role === 'owner') {
      setProjects((list) => {
        const next = list.map((p) => (p.id === s.projectId ? { ...p, shared: false } : p));
        saveProjects(loadedOrg, next);
        return next;
      });
      return;
    }
    setSharedWithMe((list) => list.filter((x) => x.id !== s.projectId));
    deleteProjectData(loadedOrg, s.projectId);
    setError('Este projeto não está mais compartilhado com você.');
    const fallback = [...projectsRef.current].sort((a, b) => b.updatedAt - a.updatedAt)[0];
    if (fallback) applyProject(loadedOrg, fallback.id);
  };

  /** Brings in what others saved; my unsaved edits win per node/edge (3-way merge). Returns the shared title. */
  const pullShared = async (): Promise<string | undefined> => {
    const s = syncRef.current;
    if (!s) return undefined;
    const remote = await fetchSharedProject(s.projectId);
    const cur = syncRef.current;
    if (!cur || cur.projectId !== s.projectId) return undefined;
    if (!remote) {
      lostShared(cur);
      return undefined;
    }
    if (remote.role !== cur.role) setSync({ ...cur, role: remote.role });
    const base = syncBaseRef.current;
    if (base && remote.version === syncVersionRef.current) return remote.title;
    const first = !base;
    const next = first || remote.role === 'view' ? remote.board : mergeBoards(base, boardRef.current, remote.board);
    syncBaseRef.current = remote.board;
    syncVersionRef.current = remote.version;
    boardRef.current = next;
    setBoard(next);
    // Viewers always take the shared snapshot; owner/edit only on first open so a board sync doesn't wipe local typing.
    if (remote.shareChat && remote.chat && loadedOrg && (first || remote.role === 'view')) {
      applySharedChat(loadedOrg, s.projectId, remote.chat, setMessages, messagesRef);
    }
    if (first && next.nodes.length) window.setTimeout(() => canvasRef.current?.fitView(), 60);
    if (remote.role !== 'owner') {
      setSharedWithMe((list) => list.map((x) => (
        x.id === s.projectId ? { ...x, title: remote.title || x.title, version: remote.version, role: remote.role === 'edit' ? 'edit' : 'view' } : x
      )));
    }
    return remote.title;
  };

  /** Saves my edits to the shared copy (`force`: also when only the title changed). */
  const pushShared = async (force = false) => {
    const s = syncRef.current;
    const base = syncBaseRef.current;
    if (!s || s.role === 'view' || !base) return;
    const local = boardRef.current;
    if (!force && sameBoard(local, base)) return;
    const meta = s.role === 'owner' ? projectsRef.current.find((p) => p.id === s.projectId) : undefined;
    const chat = shareableChat(messagesRef.current as unknown as Array<Record<string, unknown>>);
    const res = await saveSharedMerging(s.projectId, local, base, syncVersionRef.current, meta, chat.length ? chat : undefined);
    if (syncRef.current?.projectId !== s.projectId) return;
    if (!res) {
      lostShared(s);
      return;
    }
    syncBaseRef.current = res.saved;
    syncVersionRef.current = res.version;
    if (res.saved === local) return;
    // Someone saved first: show the merged board, keeping edits made while saving.
    const next = boardRef.current === local ? res.saved : mergeBoards(local, boardRef.current, res.saved);
    boardRef.current = next;
    setBoard(next);
  };

  const syncOpsRef = useRef({ pull: pullShared, push: pushShared });
  syncOpsRef.current = { pull: pullShared, push: pushShared };

  const reportSyncError = useCallback((err: unknown) => {
    syncPauseUntilRef.current = Date.now() + 15000;
    setError(`Não foi possível sincronizar o projeto compartilhado: ${err instanceof Error ? err.message : 'erro de rede'}.`);
  }, []);

  /** Saves pending edits of the project being left, before another board replaces the refs. */
  const flushShared = useCallback(() => {
    const s = syncRef.current;
    const base = syncBaseRef.current;
    const local = boardRef.current;
    if (!s || s.role === 'view' || !base || local === base) return;
    const version = syncVersionRef.current;
    const meta = s.role === 'owner' ? projectsRef.current.find((p) => p.id === s.projectId) : undefined;
    void runSync(async () => {
      if (!sameBoard(local, base)) await saveSharedMerging(s.projectId, local, base, version, meta);
    }).catch(reportSyncError);
  }, [runSync, reportSyncError]);

  /** Persist the owner's board+chat to Postgres (cross-device), even when not shared. */
  const pushOwnCloud = useCallback(async (projectId?: string) => {
    const org = loadedOrgRef.current;
    const id = projectId || activeProjectIdRef.current;
    if (!org || !id) return;
    const role = syncRef.current?.projectId === id ? syncRef.current.role : 'owner';
    if (role === 'view' || role === 'edit') return;
    const meta = projectsRef.current.find((p) => p.id === id);
    if (!meta) return;
    const boardSnap = id === activeProjectIdRef.current ? boardRef.current : loadBoard(org, id);
    const chatSnap = id === activeProjectIdRef.current
      ? chatBookRef.current
      : loadChatBook<CanvasChatMessage>(org, id);
    if (boardIsEmpty(boardSnap) && !chatSnap.chats.some((c) => chatHasContent(c.messages))) return;
    try {
      await upsertOwnProject({
        projectId: id,
        orgId: org,
        meta,
        board: boardSnap,
        ownerChat: chatSnap,
      });
    } catch (err) {
      console.warn('upsert_own_canvas_project', err);
    }
  }, []);

  const flushCloud = useCallback(() => {
    void pushOwnCloud();
  }, [pushOwnCloud]);

  // Debounced cloud save for the active owned project.
  useEffect(() => {
    if (!loadedOrg || !activeProjectId) return undefined;
    if (sync && sync.projectId === activeProjectId && sync.role !== 'owner') return undefined;
    const meta = projects.find((p) => p.id === activeProjectId);
    if (!meta) return undefined;
    if (boardIsEmpty(board) && !chatBook.chats.some((c) => chatHasContent(c.messages))) return undefined;
    const t = window.setTimeout(() => {
      void pushOwnCloud(activeProjectId);
    }, CLOUD_PUSH_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [board, chatBook, projects, loadedOrg, activeProjectId, sync, pushOwnCloud]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushCloud();
    };
    window.addEventListener('pagehide', flushCloud);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flushCloud);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [flushCloud]);

  // Opening a project: connect to its shared copy (the server board wins over the local cache).
  useEffect(() => {
    if (!loadedOrg || !activeProjectId) return;
    const own = projectsRef.current.find((p) => p.id === activeProjectId);
    const theirs = sharedWithMeRef.current.find((x) => x.id === activeProjectId);
    if (!own?.shared && !theirs) {
      setSync(null);
      return;
    }
    setSync({ projectId: activeProjectId, role: own ? 'owner' : theirs!.role, ownerName: theirs?.ownerName });
    syncBaseRef.current = null;
    syncVersionRef.current = 0;
    void runSync(() => syncOpsRef.current.pull())
      .then((title) => {
        // The owner renamed it while another project was open.
        const renamed = own && title !== undefined && title !== projectsRef.current.find((p) => p.id === activeProjectId)?.title;
        if (renamed && syncRef.current?.projectId === activeProjectId) return runSync(() => syncOpsRef.current.push(true));
        return undefined;
      })
      .catch(reportSyncError);
  }, [loadedOrg, activeProjectId, setSync, runSync, reportSyncError]);

  // Debounced save of my edits.
  useEffect(() => {
    if (!sync || sync.role === 'view' || sync.projectId !== activeProjectId) return;
    if (!syncBaseRef.current || board === syncBaseRef.current) return;
    const wait = Math.max(SHARE_PUSH_DELAY_MS, syncPauseUntilRef.current - Date.now());
    const t = window.setTimeout(() => {
      void runSync(() => syncOpsRef.current.push()).catch(reportSyncError);
    }, wait);
    return () => window.clearTimeout(t);
  }, [board, sync, activeProjectId, runSync, reportSyncError]);

  // Others' saves: Realtime ping (version only) + polling fallback.
  const syncProjectId = sync?.projectId || '';
  useEffect(() => {
    if (!syncProjectId) return;
    const onVersion = (v: number) => {
      if (v && v <= syncVersionRef.current) return;
      void runSync(() => syncOpsRef.current.pull()).catch(reportSyncError);
    };
    const unsubscribe = subscribeSharedProject(syncProjectId, onVersion);
    const poll = window.setInterval(() => {
      if (document.hidden) return;
      fetchSharedVersion(syncProjectId)
        .then((v) => {
          if (v == null || v !== syncVersionRef.current) onVersion(v ?? 0);
        })
        .catch(() => undefined);
    }, SHARE_POLL_MS);
    return () => {
      unsubscribe();
      window.clearInterval(poll);
    };
  }, [syncProjectId, runSync, reportSyncError]);

  const saveShare = useCallback(async (settings: ShareSettings) => {
    const id = activeProjectId;
    const meta = projectsRef.current.find((p) => p.id === id);
    if (!meta || !orgId || !loadedOrg) throw new Error('Abra um projeto seu para compartilhar.');
    await runSync(async () => {
      if (syncRef.current?.projectId === id) await syncOpsRef.current.pull();
      const previous = await getShareSettings(id).catch(() => null);
      const prevIds = new Set((previous?.people || []).map((p) => p.userId));
      const current = boardRef.current;
      const chat = settings.shareChat
        ? shareableChat(messagesRef.current as unknown as Array<Record<string, unknown>>)
        : undefined;
      const version = await shareProject({ projectId: id, orgId, meta, board: current, settings, chat });
      const shared = settings.shareAll || settings.people.length > 0;
      setProjects((list) => {
        const next = list.map((p) => (p.id === id ? { ...p, shared } : p));
        saveProjects(loadedOrg, next);
        return next;
      });
      if (!shared) {
        setSync(null);
        // Keep a private cloud copy after unshare so other devices still see it.
        await upsertOwnProject({
          projectId: id,
          orgId,
          meta: { ...meta, shared: false },
          board: current,
          ownerChat: chatBookRef.current,
        }).catch((err) => console.warn('upsert after unshare', err));
        return;
      }
      setSync({ projectId: id, role: 'owner' });
      syncBaseRef.current = current;
      syncVersionRef.current = version;
      const freshPeople = settings.people.filter((p) => !prevIds.has(p.userId));
      const recipients = new Map(freshPeople.map((p) => [p.userId, p.canEdit]));
      if (settings.shareAll && !previous?.shareAll) {
        try {
          const team = await listShareMembers(orgId);
          for (const m of team) {
            if (prevIds.has(m.userId) || recipients.has(m.userId)) continue;
            const override = settings.people.find((p) => p.userId === m.userId);
            recipients.set(m.userId, override ? override.canEdit : settings.shareAllCanEdit);
          }
        } catch (err) {
          console.warn('listShareMembers for notify', err);
        }
      }
      if (recipients.size) {
        try {
          await notifyCanvasShare({
            projectId: id,
            title: meta.title,
            recipients: Array.from(recipients.entries()).map(([userId, canEdit]) => ({ userId, canEdit })),
          });
        } catch (err) {
          console.warn('notify-canvas-share', err);
          setError('Projeto compartilhado, mas o e-mail de aviso não foi enviado. Confira a chave RESEND.');
        }
      }
    });
  }, [activeProjectId, orgId, loadedOrg, runSync, setSync]);

  /** View-only people can take a private copy of the board to keep working on. */
  const duplicateShared = useCallback(() => {
    if (!loadedOrg) return;
    const src = sharedWithMeRef.current.find((x) => x.id === activeProjectId);
    const meta: CanvasProjectMeta = { ...createProjectMeta(`${src?.title || 'Projeto'} (cópia)`.slice(0, 60)), customTitle: true };
    saveBoard(loadedOrg, meta.id, boardRef.current);
    setProjects((list) => {
      const next = [meta, ...list];
      saveProjects(loadedOrg, next);
      return next;
    });
    applyProject(loadedOrg, meta.id);
  }, [loadedOrg, activeProjectId, applyProject]);

  const projectBusy = useMemo(
    () => sending || recording || board.nodes.some((n) => (
      isImage(n) ? n.phase.status === 'generating'
        : isVideo(n) ? n.status === 'generating' || n.status === 'joining'
          : isAudio(n) && n.status === 'generating'
    )),
    [sending, recording, board.nodes],
  );

  /** Nothing in it and not shared: leaving it just discards it. */
  const activeIsDisposable = () => !syncRef.current && boardIsEmpty(boardRef.current) && !messagesRef.current.length;

  const openProject = useCallback((id: string) => {
    if (!loadedOrg || projectBusy || id === activeProjectId) return;
    if (activeIsDisposable()) {
      const leaving = activeProjectId;
      deleteProjectData(loadedOrg, leaving);
      void deleteOwnProject(leaving).catch(() => undefined);
      setProjects((list) => {
        const next = list.filter((p) => p.id !== leaving);
        saveProjects(loadedOrg, next);
        return next;
      });
    }
    flushShared();
    flushCloud();
    applyProject(loadedOrg, id);
  }, [loadedOrg, projectBusy, activeProjectId, applyProject, flushShared, flushCloud]);

  const openSharedProject = useCallback((id: string) => {
    if (!loadedOrg || projectBusy || id === activeProjectId) return;
    flushShared();
    flushCloud();
    applyProject(loadedOrg, id);
  }, [loadedOrg, projectBusy, activeProjectId, applyProject, flushShared, flushCloud]);

  const newProject = useCallback(() => {
    if (!loadedOrg || projectBusy) return;
    if (activeIsDisposable()) return;
    flushShared();
    flushCloud();
    const meta = createProjectMeta();
    setProjects((list) => {
      const next = [meta, ...list];
      saveProjects(loadedOrg, next);
      return next;
    });
    applyProject(loadedOrg, meta.id);
  }, [loadedOrg, projectBusy, applyProject, flushShared, flushCloud]);

  const renameProject = useCallback((id: string, title: string) => {
    if (!loadedOrg) return;
    const next = projectsRef.current.map((p) => (p.id === id ? { ...p, title, customTitle: true } : p));
    projectsRef.current = next;
    setProjects(next);
    saveProjects(loadedOrg, next);
    if (syncRef.current?.projectId === id && syncRef.current.role === 'owner') {
      void runSync(() => syncOpsRef.current.push(true)).catch(reportSyncError);
    }
    void pushOwnCloud(id);
    const b = id === activeProjectId ? boardRef.current : loadBoard(loadedOrg, id);
    renameMediaAssetsByUrl(loadedOrg, projectGeneratedVideoUrls(b), title).catch((err) => {
      setError(err instanceof Error ? err.message : 'Falha ao renomear os vídeos na galeria.');
    });
  }, [loadedOrg, activeProjectId, runSync, reportSyncError, pushOwnCloud]);

  const deleteProject = useCallback((id: string) => {
    if (!loadedOrg || (projectBusy && id === activeProjectId)) return;
    if (syncRef.current?.projectId === id) setSync(null);
    void deleteOwnProject(id).catch((err) => {
      setError(`O projeto saiu da sua lista, mas não foi removido da nuvem: ${err instanceof Error ? err.message : 'erro'}.`);
    });
    deleteProjectData(loadedOrg, id);
    const rest = projects.filter((p) => p.id !== id);
    const next = rest.length ? rest : [createProjectMeta()];
    setProjects(next);
    saveProjects(loadedOrg, next);
    if (id === activeProjectId) {
      const fallback = [...next].sort((a, b) => b.updatedAt - a.updatedAt)[0];
      applyProject(loadedOrg, fallback.id);
    }
  }, [loadedOrg, projectBusy, activeProjectId, projects, applyProject, setSync]);

  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(() => setError(null), 7000);
    return () => window.clearTimeout(t);
  }, [error]);

  /** Every edit goes through here. A real gesture becomes one undo step; uploads and renders do not. */
  const updateBoard = useCallback((fn: (b: Board) => Board, opts?: HistoryRecordOptions) => {
    if (readOnlyRef.current) return;
    const org = loadedOrgRef.current;
    const project = activeProjectIdRef.current;
    // Read the shared board so a render that kept going after navigation patches the
    // same project the page will show when the user comes back.
    const prev = (org && project && liveBoard(org, project)) || boardRef.current;
    const next = fn(prev);
    if (next === prev) return;
    if (!commitHistory(prev, next, opts)) return;
    boardRef.current = next;
    setBoard(next);
    if (org && project) publishLiveBoard(org, project, next);
  }, [commitHistory]);

  const undoBoard = useCallback(() => {
    if (readOnlyRef.current) return;
    const next = historyUndo(boardRef.current);
    if (!next) return;
    boardRef.current = next;
    setBoard(next);
    const org = loadedOrgRef.current;
    const project = activeProjectIdRef.current;
    if (org && project) publishLiveBoard(org, project, next);
    setSelectedIds((ids) => ids.filter((id) => next.nodes.some((n) => n.id === id)));
  }, [historyUndo]);

  const redoBoard = useCallback(() => {
    if (readOnlyRef.current) return;
    const next = historyRedo(boardRef.current);
    if (!next) return;
    boardRef.current = next;
    setBoard(next);
    const org = loadedOrgRef.current;
    const project = activeProjectIdRef.current;
    if (org && project) publishLiveBoard(org, project, next);
    setSelectedIds((ids) => ids.filter((id) => next.nodes.some((n) => n.id === id)));
  }, [historyRedo]);

  const patchPhase = useCallback((id: string, patch: Partial<FragmentadorSessionPhase>, opts?: BoardPatchOpts) => {
    updateBoard((b) => {
      const cur = b.nodes.find((n) => n.id === id);
      if (!cur || !isImage(cur)) return b;
      if (opts?.ifInflight && !nodeInflight(cur)) return b;
      return {
        ...b,
        nodes: b.nodes.map((n) => (n.id === id && isImage(n) ? { ...n, phase: { ...n.phase, ...patch } } : n)),
      };
    }, opts?.record === false || opts?.coalesce ? { record: opts.record, coalesce: opts.coalesce } : undefined);
  }, [updateBoard]);

  const patchImageNode = useCallback((id: string, patch: Partial<BoardImageNode>, opts?: BoardPatchOpts) => {
    updateBoard((b) => {
      const cur = b.nodes.find((n) => n.id === id);
      if (!cur || !isImage(cur)) return b;
      if (opts?.ifInflight && !nodeInflight(cur)) return b;
      return { ...b, nodes: b.nodes.map((n) => (n.id === id && isImage(n) ? { ...n, ...patch } : n)) };
    }, opts);
  }, [updateBoard]);

  const patchVideo = useCallback((id: string, patch: Partial<BoardVideoNode>, opts?: BoardPatchOpts) => {
    updateBoard((b) => {
      const cur = b.nodes.find((n) => n.id === id);
      if (!cur || !isVideo(cur)) return b;
      if (opts?.ifInflight && !nodeInflight(cur)) return b;
      return { ...b, nodes: b.nodes.map((n) => (n.id === id && isVideo(n) ? { ...n, ...patch } : n)) };
    }, opts?.record === false || opts?.coalesce ? { record: opts.record, coalesce: opts.coalesce } : undefined);
  }, [updateBoard]);

  const uploadGeneratorFile = useCallback(async (videoId: string, slot: GeneratorSlot, file: File) => {
    if (!orgId) {
      setError('Você não está em nenhuma organização.');
      return;
    }
    try {
      const asset = await uploadToGallery(orgId, file, { title: file.name });
      const ref = { url: toMediaUrl(asset.video_url), label: file.name };
      updateBoard((b) => ({
        ...b,
        nodes: b.nodes.map((n) => {
          if (n.id !== videoId || !isVideo(n)) return n;
          const g = { ...(n.generator || {}) };
          if (slot === 'start') g.startImage = ref;
          else if (slot === 'end') g.endImage = ref;
          else if (slot === 'video') g.referenceVideo = ref;
          else if (slot === 'audio') g.audio = ref;
          else g.attachments = [...(g.attachments || []), ref].slice(0, 12);
          return { ...n, generator: g };
        }),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar o arquivo.');
    }
  }, [orgId, updateBoard]);

  const askGenerateVideo = useCallback((videoId: string) => {
    const video = nodeById(boardRef.current, videoId);
    if (!isVideo(video)) return;
    const model = getVideoModelById(video.videoModelId || '') || getVideoModelById(videoPick.videoModelId);
    if (video.videoModelId && model) {
      const resolutionId = model.resolutions.some((r) => r.id === video.resolutionId)
        ? video.resolutionId || videoPick.resolutionId
        : (model.resolutions.find((r) => r.id === '720p')?.id || model.resolutions[0]?.id || videoPick.resolutionId);
      setVideoPick({
        videoModelId: model.id,
        resolutionId,
        durationSeconds: video.totalDurationSeconds || videoPick.durationSeconds,
        generateAudio: video.generateAudio ?? videoPick.generateAudio,
      });
    }
    const targets = generationTargets(boardRef.current, videoId);
    rememberDebitRef.current({ kind: 'video', videoIds: targets.length ? targets.map((t) => t.id) : [videoId] });
  }, [videoPick]);

  const patchAudio = useCallback((id: string, patch: Partial<BoardAudioNode>, opts?: BoardPatchOpts) => {
    updateBoard((b) => {
      const cur = b.nodes.find((n) => n.id === id);
      if (!cur || !isAudio(cur)) return b;
      if (opts?.ifInflight && !nodeInflight(cur)) return b;
      return { ...b, nodes: b.nodes.map((n) => (n.id === id && isAudio(n) ? { ...n, ...patch } : n)) };
    }, opts?.record === false || opts?.coalesce ? { record: opts.record, coalesce: opts.coalesce } : undefined);
  }, [updateBoard]);

  const setThread = useCallback((fn: (m: CanvasChatMessage[]) => CanvasChatMessage[]) => {
    const next = fn(messagesRef.current);
    messagesRef.current = next;
    setMessages(next);
  }, []);

  const pushMessage = useCallback((msg: Omit<CanvasChatMessage, 'id'>) => {
    const id = newId('msg');
    setThread((m) => [...m, { ...msg, id }]);
    return id;
  }, [setThread]);

  const patchMessage = useCallback((id: string, patch: Partial<CanvasChatMessage>) => {
    setThread((m) => m.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }, [setThread]);

  /** Reply that owns the debit button while a confirmation is open. */
  const debitOwnerRef = useRef<string | undefined>(undefined);

  const rememberDebit = useCallback((modal: NonNullable<CreditModal>, msgId?: string) => {
    if (modal.kind === 'image' && referenceImageDebit(boardRef.current, modal.videoIds)) {
      const note = 'A entrada está em Referências. As fotos entram juntas, sem quadros novos.';
      if (msgId) patchMessage(msgId, { status: note });
      else pushMessage({ role: 'assistant', text: note });
      return;
    }
    let id = msgId || [...messagesRef.current].reverse().find((m) => m.role === 'assistant' && !m.contextBreak)?.id;
    if (!id) {
      id = pushMessage({
        role: 'assistant',
        text: 'Confirme o débito na janela. Se sair sem confirmar, o botão abaixo abre de novo.',
      });
    }
    debitOwnerRef.current = id;
    const debit = debitFromModal(modal, modal.kind === 'gen-image' ? genImagePromptRef.current : '');
    patchMessage(id, { debit });
    setCreditModal(modal);
  }, [patchMessage, pushMessage]);

  rememberDebitRef.current = rememberDebit;

  const markDebitDone = useCallback(() => {
    const id = debitOwnerRef.current;
    if (!id) return;
    setThread((m) => m.map((x) => (x.id === id && x.debit ? { ...x, debit: { ...x.debit, done: true } } : x)));
  }, [setThread]);

  const reopenDebit = useCallback((messageId: string) => {
    const msg = messagesRef.current.find((m) => m.id === messageId);
    if (!msg?.debit || msg.debit.done) return;
    const modal = modalFromDebit(msg.debit);
    if (!modal) return;
    if (modal.kind === 'image' && referenceImageDebit(boardRef.current, modal.videoIds)) {
      patchMessage(messageId, {
        debit: { ...msg.debit, done: true },
        status: 'A entrada está em Referências. As fotos entram juntas, sem quadros novos.',
      });
      return;
    }
    debitOwnerRef.current = messageId;
    if (modal.kind === 'compose-image') setEditText((cur) => (cur.trim() ? cur : (modal.promptEn || modal.instruction)));
    else if (modal.kind === 'edit-image') setEditText((cur) => (cur.trim() ? cur : modal.instruction));
    if (modal.kind === 'gen-image' && msg.debit.kind === 'gen-image') {
      const stored = msg.debit.prompt;
      setGenImagePrompt((cur) => (cur.trim() ? cur : stored));
    }
    if (modal.kind === 'audio') {
      const node = nodeById(boardRef.current, modal.audioId);
      if (isAudio(node)) {
        setAudioText(node.text);
        setAudioLoop(Boolean(node.loop));
        setAudioSourceId(node.sourceAudioId || '');
        audioModalOpenRef.current = true;
      }
    }
    setCreditModal(modal);
  }, [patchMessage]);

  const insufficientMsg = useCallback(() => (
    role === 'admin' || role === 'fin_ops'
      ? 'Saldo insuficiente na organização. Como gestor, você pode adicionar créditos no menu Pagamentos.'
      : 'Sua organização está sem créditos. Por favor, abra uma solicitação para a equipe financeira.'
  ), [role]);

  const friendlyError = useCallback((err: unknown, fallback: string) => {
    const msg = err instanceof Error ? err.message : fallback;
    if (isCreditError(msg)) return insufficientMsg();
    if (/503|UNAVAILABLE|high demand|RESOURCE_EXHAUSTED|sobrecarregado|try again later/i.test(msg)) {
      return 'O Gemini está sobrecarregado agora. Tente de novo em alguns segundos.';
    }
    return msg;
  }, [insufficientMsg]);

  // ── Images in ────────────────────────────────────────────────

  const placeImages = useCallback(async (
    items: Array<{ file?: File; url?: string; title: string }>,
    at: { x: number; y: number },
  ) => {
    if (!orgId) {
      setError('Você não está em nenhuma organização.');
      return;
    }
    const adoptAspect = !boardRef.current.aspectChosen && !boardRef.current.nodes.some(isImage);
    const placed = items.map((item, i) => {
      const preview = item.file ? URL.createObjectURL(item.file) : item.url || '';
      const node = createImageNode(preview, Math.round(at.x + i * (IMAGE_NODE_W + 28)), Math.round(at.y), item.title);
      node.phase = { ...node.phase, status: item.file ? 'generating' : 'ready', error: item.file ? 'Enviando…' : undefined };
      return { node, item, preview };
    });
    updateBoard((b) => ({ ...b, nodes: [...b.nodes, ...placed.map((p) => p.node)] }));
    setSelectedIds(placed.map((p) => p.node.id));

    await Promise.all(placed.map(async ({ node, item, preview }, i) => {
      try {
        let url = item.url || '';
        if (item.file) {
          const asset = await uploadToGallery(orgId, item.file, { title: item.title });
          url = toMediaUrl(asset.video_url);
          URL.revokeObjectURL(preview);
        }
        const size = await readImageSize(url).catch(() => null);
        patchImageNode(node.id, size ? { width: size.width, height: size.height } : {}, { ifInflight: true });
        if (size && adoptAspect && i === 0) {
          updateBoard((b) => (b.aspectChosen ? b : { ...b, aspectRatio: closestBoardAspect(size.width, size.height) }));
        }
        patchPhase(node.id, { imageUrl: url, status: 'ready', error: undefined }, { ifInflight: true });
      } catch (err) {
        patchPhase(node.id, { status: 'failed', error: friendlyError(err, 'Falha ao enviar imagem.') }, { ifInflight: true });
        setError(friendlyError(err, 'Falha ao enviar imagem.'));
      }
    }));
  }, [orgId, updateBoard, patchImageNode, patchPhase, friendlyError]);

  const placeGalleryItems = useCallback((items: GalleryDragItem[], at: { x: number; y: number }) => {
    const images = items.filter((i) => i.kind !== 'video');
    const videos = items.filter((i) => i.kind === 'video');
    if (images.length) void placeImages(images.map((i) => ({ url: i.url, title: i.title })), at);
    if (!videos.length) return;
    const startX = at.x + images.length * (IMAGE_NODE_W + 28);
    const placed = videos.map((v, i) => createGalleryVideoNode(v.url, Math.round(startX + i * (VIDEO_NODE_W + 28)), Math.round(at.y), v.title));
    updateBoard((b) => ({ ...b, nodes: [...b.nodes, ...placed] }));
    setSelectedIds(placed.map((p) => p.id));
  }, [placeImages, updateBoard]);

  const placeAtViewCenter = useCallback((item: GalleryDragItem) => {
    const at = canvasRef.current?.viewCenter() || { x: 120, y: 120 };
    const w = item.kind === 'video' ? VIDEO_NODE_W : IMAGE_NODE_W;
    const h = item.kind === 'video' ? VIDEO_NODE_H : IMAGE_NODE_H;
    const y = at.y - h;
    const offset = boardRef.current.nodes.filter((n) => Math.abs(n.y - y) < 40).length;
    placeGalleryItems([item], { x: at.x - w / 2 + offset * (w + 28), y });
  }, [placeGalleryItems]);

  const placeVideoFiles = useCallback(async (files: File[], at: { x: number; y: number }) => {
    const placed = files.map((f, i) => {
      const node = createGalleryVideoNode('', Math.round(at.x + i * (VIDEO_NODE_W + 28)), Math.round(at.y), fileTitle(f, 'Vídeo'));
      return { file: f, node: { ...node, status: 'generating' as const, progress: 'Enviando…', clipUrl: undefined, videoUrl: undefined } };
    });
    updateBoard((b) => ({ ...b, nodes: [...b.nodes, ...placed.map((p) => p.node)] }));
    setSelectedIds(placed.map((p) => p.node.id));
    await Promise.all(placed.map(async ({ file, node }) => {
      try {
        const asset = await uploadToGallery(orgId, file, { title: node.title });
        const url = toMediaUrl(asset.video_url);
        patchVideo(node.id, { status: 'ready', progress: undefined, clipUrl: url, videoUrl: url }, { ifInflight: true });
      } catch (err) {
        patchVideo(node.id, { status: 'failed', progress: undefined, error: friendlyError(err, 'Falha ao enviar vídeo.') }, { ifInflight: true });
        setError(friendlyError(err, 'Falha ao enviar vídeo.'));
      }
    }));
  }, [orgId, updateBoard, patchVideo, friendlyError]);

  const placeAudioFiles = useCallback(async (files: File[], at: { x: number; y: number }) => {
    const placed = files.map((f, i) => {
      const node = createAudioNode(Math.round(at.x + i * (AUDIO_NODE_W + 28)), Math.round(at.y), 'music', '');
      return { file: f, node: { ...node, title: fileTitle(f, 'Áudio'), status: 'generating' as const, progress: 'Enviando…' } };
    });
    updateBoard((b) => ({ ...b, nodes: [...b.nodes, ...placed.map((p) => p.node)] }));
    setSelectedIds(placed.map((p) => p.node.id));
    await Promise.all(placed.map(async ({ file, node }) => {
      try {
        if (!isCdnUploadEnabled()) throw new Error('Envio de áudio indisponível neste ambiente (CDN de mídia desligado).');
        const [{ publicUrl }, seconds] = await Promise.all([
          uploadViaCdn({ organizationId: orgId, file, filename: file.name, purpose: 'audio' }),
          readMediaSeconds(file),
        ]);
        const audioUrl = toMediaUrl(publicUrl);
        patchAudio(node.id, {
          status: 'ready',
          progress: undefined,
          ...withNewAudioVersion(node, { audioUrl, createdAt: Date.now(), text: node.title || '', seconds: seconds || undefined }),
        }, { ifInflight: true });
      } catch (err) {
        patchAudio(node.id, { status: 'failed', progress: undefined, error: friendlyError(err, 'Falha ao enviar áudio.') }, { ifInflight: true });
        setError(friendlyError(err, 'Falha ao enviar áudio.'));
      }
    }));
  }, [orgId, updateBoard, patchAudio, friendlyError]);

  const dropFiles = useCallback((files: File[], at: { x: number; y: number }) => {
    if (!orgId) {
      setError('Você não está em nenhuma organização.');
      return;
    }
    const images = files.filter((f) => f.type.startsWith('image/'));
    const videos = files.filter((f) => f.type.startsWith('video/'));
    const audios = files.filter((f) => f.type.startsWith('audio/'));
    let x = at.x;
    if (images.length) {
      void placeImages(images.map((f) => ({ file: f, title: fileTitle(f, 'Imagem') })), { x, y: at.y });
      x += images.length * (IMAGE_NODE_W + 28);
    }
    if (videos.length) {
      void placeVideoFiles(videos, { x, y: at.y });
      x += videos.length * (VIDEO_NODE_W + 28);
    }
    if (audios.length) void placeAudioFiles(audios, { x, y: at.y });
  }, [orgId, placeImages, placeVideoFiles, placeAudioFiles]);

  const dropUrls = useCallback(async (urls: string[], at: { x: number; y: number }) => {
    try {
      const files = await Promise.all(urls.map((u, i) => blobOrDataToFile(u, `imagem-${i + 1}`)));
      void placeImages(files.filter((f) => f.type.startsWith('image/')).map((f) => ({ file: f, title: 'Imagem' })), at);
    } catch (err) {
      setError(friendlyError(err, 'Não consegui baixar essa imagem. Salve no computador e arraste o arquivo.'));
    }
  }, [placeImages, friendlyError]);

  const copyBoardSelection = useCallback((ids: string[], edgeId: string | null) => {
    if (readOnlyRef.current) return;
    const snap = snapshotCanvasSelection(boardRef.current, ids, edgeId);
    if (!snap) return;
    writeCanvasClipboard(snap);
  }, []);

  const pasteBoardClipboard = useCallback(async (at: { x: number; y: number }, textHint?: string | null) => {
    if (readOnlyRef.current) return false;
    const payload = readCanvasClipboardFromText(textHint) || await readCanvasClipboard();
    if (!payload?.nodes.length) return false;
    const { board: next, pastedIds } = pasteCanvasClipboard(boardRef.current, payload, at);
    if (!pastedIds.length) return false;
    updateBoard(() => next);
    setSelectedIds(pastedIds);
    return true;
  }, [updateBoard]);

  const lastMediaPasteRef = useRef<{ key: string; at: number }>({ key: '', at: 0 });
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (readOnlyRef.current) return;
      const t = e.target as HTMLElement | null;
      const inField = Boolean(
        t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable),
      );
      const at = canvasRef.current?.viewCenter() || { x: 120, y: 120 };
      const files = filesFromClipboard(e.clipboardData);
      // Image/video paste works in chat and on the board (screenshots use items API).
      if (files.length) {
        e.preventDefault();
        const key = files.map((f) => `${f.size}:${f.type}`).join('|');
        const now = Date.now();
        if (lastMediaPasteRef.current.key === key && now - lastMediaPasteRef.current.at < 600) return;
        lastMediaPasteRef.current = { key, at: now };
        dropFiles(files, at);
        return;
      }
      if (inField) return;
      const text = e.clipboardData?.getData('text/plain') || '';
      const boardClip = readCanvasClipboardFromText(text) || readCanvasClipboardFromStorage();
      if (boardClip?.nodes.length) {
        e.preventDefault();
        void pasteBoardClipboard(at, text);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [dropFiles, pasteBoardClipboard]);

  // ── Joins ────────────────────────────────────────────────────

  /** The FFmpeg instance is shared: joins and mixes run one at a time. */
  const ffmpegQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const withFfmpeg = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const run = ffmpegQueueRef.current.then(fn, fn);
    ffmpegQueueRef.current = run.catch(() => undefined);
    return run;
  }, []);

  const stitch = useCallback((urls: string[], title: string, transition?: BoardJoinTransition) => withFfmpeg(async () => {
    if (!ffmpeg.loaded) await ffmpeg.load();
    const blobs: Blob[] = [];
    for (const url of urls) {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Falha ao baixar vídeo para juntar.');
      blobs.push(await res.blob());
    }
    const resLabel = /1080/.test(videoPick.resolutionId) ? '1080p' : '720p';
    let transitionOptions = {};
    if (transition) {
      const durations = await Promise.all(blobs.map((b) => ffmpeg.getVideoDuration(b)));
      if (durations.some((d) => !d)) throw new Error('Não consegui ler a duração dos vídeos para aplicar a transição.');
      const seconds = Math.min(transition.seconds, ...durations.map((d) => d / 2 - 0.05));
      transitionOptions = {
        fadeSeconds: Math.max(0.1, seconds),
        fadeAudio: true,
        strictTransitions: true,
        clips: durations.map((duration) => (transition.kind === 'crossfade'
          ? { duration, transitionOut: 'dissolve' as const }
          : { duration, fadeIn: true, fadeOut: true })),
      };
    }
    const stitched = await ffmpeg.concatVideos(blobs, resLabel, { aspect: boardRef.current.aspectRatio, ...transitionOptions });
    if (!stitched) throw new Error('Falha ao juntar os vídeos.');
    const file = new File([stitched], `tela_em_branco_${Date.now()}.mp4`, { type: 'video/mp4' });
    const asset = await uploadToGallery(orgId, file, { title, status: 'ready', generation_context: 'tela_em_branco_join' });
    return toMediaUrl(asset.video_url) || asset.video_url;
  }), [ffmpeg, orgId, videoPick.resolutionId, withFfmpeg]);

  const openBridge = useCallback((stillUrl: string, videoUrl: string) => withFfmpeg(async () => {
    if (!ffmpeg.loaded) await ffmpeg.load();
    const [stillRes, videoRes] = await Promise.all([fetch(stillUrl), fetch(videoUrl)]);
    if (!stillRes.ok || !videoRes.ok) throw new Error('Falha ao baixar a imagem inicial ou o vídeo.');
    const bridged = await ffmpeg.bridgeOpening(await stillRes.blob(), await videoRes.blob());
    if (!bridged) throw new Error('Falha ao ligar a imagem inicial.');
    const file = new File([bridged], `abertura_${Date.now()}.mp4`, { type: 'video/mp4' });
    const asset = await uploadToGallery(orgId, file, {
      title: projectTitleRef.current || 'Abertura',
      status: 'ready',
      generation_context: 'tela_em_branco_abertura',
    });
    return toMediaUrl(asset.video_url) || asset.video_url;
  }), [ffmpeg, orgId, withFfmpeg]);

  /** Latest mix request per edit node; queued mixes that were superseded are skipped. */
  const mixTicketRef = useRef(new Map<string, number>());

  /** Renders a "Vídeo editado": its source video + linked sound tracks + captions / fade / grade. */
  const mixEdit = useCallback(async (editId: string): Promise<void> => {
    const ticket = (mixTicketRef.current.get(editId) || 0) + 1;
    mixTicketRef.current.set(editId, ticket);
    const initial = nodeById(boardRef.current, editId);
    if (!isVideo(initial) || !initial.edit) return;
    const source = upstreamVideos(boardRef.current, editId)[0];
    if (!source?.videoUrl) {
      patchVideo(editId, { status: 'idle', videoUrl: undefined, progress: undefined, error: undefined });
      return;
    }
    patchVideo(editId, { status: 'joining', progress: 'Mixando o áudio…', error: undefined });
    await withFfmpeg(async () => {
      if (mixTicketRef.current.get(editId) !== ticket) return;
      const b = boardRef.current;
      const node = nodeById(b, editId);
      const src = upstreamVideos(b, editId)[0];
      if (!isVideo(node) || !node.edit || !src?.videoUrl) return;
      const edit = node.edit;
      const inputs = audioInputs(b, editId);
      const tracks = inputs.filter((a) => a.audioUrl && !a.muted && a.volume > 0);
      const seconds = videoSeconds(b, editId) || 600;
      const captions = edit.captions ? narrationCaptions(inputs, seconds, edit.captionPosition) : [];
      if (!tracks.length && edit.sourceVolume === 1 && !edit.fade && !edit.grade && !edit.grayscale && !captions.length) {
        patchVideo(editId, { videoUrl: src.videoUrl, status: 'ready', progress: undefined, error: undefined });
        return;
      }
      try {
        if (!ffmpeg.loaded) await ffmpeg.load();
        const fetchBlob = async (url: string) => {
          const res = await fetch(url);
          if (!res.ok) throw new Error('Falha ao baixar mídia para mixar.');
          return res.blob();
        };
        const [videoBlob, ...audioBlobs] = await Promise.all([src.videoUrl, ...tracks.map((t) => t.audioUrl as string)].map(fetchBlob));
        const out = await ffmpeg.mixEditedVideo(
          videoBlob,
          tracks.map((t, i) => ({
            blob: audioBlobs[i],
            volume: t.volume,
            offset: t.startOffset,
            loop: t.loop,
            speed: t.speed,
            bed: t.audioKind === 'music',
            seconds: t.seconds,
          })),
          {
            sourceVolume: edit.sourceVolume,
            captions,
            captionSettings: { ...defaultCaptionsSettings(), position: edit.captionPosition || 'bottom' },
            fade: edit.fade,
            colorGrade: edit.grayscale ? 'bw' : edit.grade ? 'cinematic' : 'none',
          },
        );
        if (!out) throw new Error('Falha ao mixar o áudio.');
        if (mixTicketRef.current.get(editId) !== ticket) return;
        const title = `${projectTitleRef.current || 'Tela em branco'} · editado`;
        const file = new File([out], `tela_em_branco_editado_${Date.now()}.mp4`, { type: 'video/mp4' });
        const asset = await uploadToGallery(orgId, file, { title, status: 'ready', generation_context: 'tela_em_branco_edit' });
        patchVideo(editId, { videoUrl: toMediaUrl(asset.video_url) || asset.video_url, status: 'ready', progress: undefined, error: undefined });
      } catch (err) {
        patchVideo(editId, { status: 'failed', progress: undefined, error: friendlyError(err, 'Falha ao mixar o áudio.') });
      }
    });
  }, [ffmpeg, orgId, patchVideo, withFfmpeg, friendlyError]);

  /** Recompute what a video node plays (upstream joins + own clip, or its mix), then its downstream nodes. */
  const settleVideo = useCallback(async (videoId: string, seen = new Set<string>()): Promise<void> => {
    if (seen.has(videoId)) return;
    seen.add(videoId);
    const target = nodeById(boardRef.current, videoId);
    if (isVideo(target) && target.edit) {
      await mixEdit(videoId);
      for (const d of downstreamVideos(boardRef.current, videoId)) await settleVideo(d.id, seen);
      return;
    }
    const transition = isVideo(target) ? target.joinTransition : undefined;
    const seq = transition ? joinUnits(boardRef.current, videoId) : videoSequence(boardRef.current, videoId);
    if (seq && seq.length) {
      if (seq.length === 1) {
        patchVideo(videoId, { videoUrl: seq[0], status: 'ready', error: undefined, progress: undefined });
      } else {
        const how = transition?.kind === 'crossfade' ? ' com transição suave' : transition ? ' com fade' : '';
        patchVideo(videoId, { status: 'joining', progress: `Juntando ${seq.length} vídeos${how}…`, error: undefined });
        try {
          const url = await stitch(seq, projectTitleRef.current || `Tela em branco · ${seq.length} vídeos`, transition);
          patchVideo(videoId, { videoUrl: url, status: 'ready', progress: undefined });
          generationToast?.pushCompletionToast({
            id: `tela-join-${Date.now()}`,
            title: 'Vídeo pronto',
            message: `${seq.length} vídeos juntados. Clique para visualizar.`,
            mediaUrl: url,
            mediaType: 'video',
          });
        } catch (err) {
          patchVideo(videoId, { status: 'failed', progress: undefined, error: friendlyError(err, 'Falha ao juntar.') });
          return;
        }
      }
    }
    for (const d of downstreamVideos(boardRef.current, videoId)) await settleVideo(d.id, seen);
  }, [patchVideo, stitch, generationToast, friendlyError, mixEdit]);

  const connectNodes = useCallback((from: string, to: string, role: 'flow' | 'ref' = 'flow') => {
    const reason = canConnect(boardRef.current, from, to, role);
    if (reason) {
      setError(reason);
      return false;
    }
    updateBoard((b) => connect(b, from, to, role));
    if (role === 'ref') return true;
    const source = nodeById(boardRef.current, from);
    if ((isVideo(source) || isAudio(source)) && isVideo(nodeById(boardRef.current, to))) {
      void settleVideo(to);
    }
    return true;
  }, [updateBoard, settleVideo]);

  const connectImages = useCallback((imageIds: string[], to: string | null, at: { x: number; y: number }, order: 'reading' | 'given' = 'reading') => {
    const fresh = to ? undefined : createVideoNode(Math.round(at.x), Math.round(at.y - VIDEO_NODE_H / 2));
    const base = fresh ? { ...boardRef.current, nodes: [...boardRef.current.nodes, fresh] } : boardRef.current;
    const videoId = fresh?.id || to!;
    const result = connectImagesTo(base, imageIds, videoId, order);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    updateBoard(() => result.board);
  }, [updateBoard]);

  const resizeNode = useCallback((id: string, scale: number) => {
    const cur = boardRef.current.nodes.find((n) => n.id === id);
    const next = scale >= 1 ? undefined : scale;
    if (!cur || (cur.scale ?? undefined) === next) return;
    updateBoard((b) => ({
      ...b,
      nodes: b.nodes.map((n) => (n.id === id ? { ...n, scale: next } : n)),
    }));
  }, [updateBoard]);

  const moveNodes = useCallback((moves: NodeMove[]) => {
    const at = new Map(moves.map((m) => [m.id, m]));
    updateBoard((b) => ({
      ...b,
      nodes: b.nodes.map((n) => {
        const m = at.get(n.id);
        return m ? { ...n, x: m.x, y: m.y } : n;
      }),
    }));
  }, [updateBoard]);

  const layoutBoardNow = useCallback(() => {
    if (readOnlyRef.current) return false;
    const next = layoutBoard(boardRef.current);
    if (next === boardRef.current) return false;
    updateBoard(() => next);
    window.setTimeout(() => canvasRef.current?.fitView(), 60);
    return true;
  }, [updateBoard]);

  const removeBoardNodes = useCallback((ids: string[]) => {
    const before = boardRef.current;
    const edits = Array.from(new Set(ids.map((id) => editNodeForAudio(before, id)?.id).filter((id): id is string => Boolean(id))));
    const { removed } = removeNodesClean(before, ids);
    if (!removed.length) return;
    updateBoard((b) => removeNodesClean(b, ids).board);
    removedRef.current = [...removedRef.current, ...removed].slice(-40);
    setSelectedIds((s) => s.filter((id) => !ids.includes(id)));
    for (const id of edits) if (!ids.includes(id)) void settleVideo(id);
    const last = messagesRef.current[messagesRef.current.length - 1];
    if (before.nodes.some(isVideo) && !boardRef.current.nodes.some(isVideo) && last && !last.contextBreak) {
      pushMessage({ role: 'assistant', text: 'Quadro recomeçado — o agente esqueceu o fluxo anterior', contextBreak: true });
    }
  }, [updateBoard, settleVideo, pushMessage]);

  const setImageMode = useCallback((videoId: string, mode: BoardImageMode) => {
    updateBoard((b) => applyImageMode(b, videoId, mode));
  }, [updateBoard]);

  /** Format closest to the photos on the board (the most common one wins). */
  const photoAspect = useCallback((): BoardAspect | undefined => {
    const counts = new Map<BoardAspect, number>();
    for (const n of boardRef.current.nodes.filter(isImage)) {
      if (!n.width || !n.height) continue;
      const a = closestBoardAspect(n.width, n.height);
      counts.set(a, (counts.get(a) || 0) + 1);
    }
    let best: BoardAspect | undefined;
    for (const a of BOARD_ASPECTS) if ((counts.get(a) || 0) > (best ? counts.get(best) || 0 : 0)) best = a;
    return best;
  }, []);

  const setBoardAspect = useCallback((value: BoardAspect | 'original') => {
    const aspect = value === 'original' ? photoAspect() : value;
    if (!aspect) {
      setError('Não há fotos no quadro para copiar a proporção.');
      return;
    }
    updateBoard((b) => ({ ...b, aspectRatio: aspect, aspectChosen: true }));
  }, [photoAspect, updateBoard]);

  // ── Storyboard ───────────────────────────────────────────────

  const resolveImages = useCallback((ids: string[]): BoardImageNode[] => {
    const b = boardRef.current;
    const ready = (n: BoardImageNode) => Boolean(n.phase.imageUrl) && n.phase.status === 'ready';
    const byIds = ids.map((id) => nodeById(b, id)).filter(isImage);
    if (byIds.length) return byIds.filter(ready);
    const selected = b.nodes.filter(isImage).filter((n) => selectedIds.includes(n.id)).sort((p, q) => p.x - q.x || p.y - q.y);
    if (selected.length >= 2) return selected.filter(ready);
    const loose = looseImages(b).filter(ready);
    if (loose.length) return loose;
    return b.nodes.filter(isImage).filter(ready).sort((p, q) => p.x - q.x || p.y - q.y);
  }, [selectedIds]);

  const buildStoryboard = useCallback(async (
    intent: {
      image_node_ids: string[];
      brief: string;
      keyframes: boolean;
      scene_count: number;
      duration_seconds?: number;
      new_scenes_position?: NewScenesPosition;
      reset_requests?: boolean;
    },
    msgId: string,
    /** The user's own words for this turn, oldest first (appended to the video's requests). */
    userRequests: string[] = [],
    /** Lay out this script instead of planning a new one (split a flow that no longer fits the model). */
    reuse?: PlannedFlow,
  ): Promise<string | undefined> => {
    let images = resolveImages(intent.image_node_ids);
    if (!images.length) throw new Error('Arraste pelo menos uma imagem para o quadro.');
    const { keyframes } = intent;
    const b0 = boardRef.current;
    const touched = Array.from(new Set(images.map((n) => videoForImage(b0, n.id)).filter(isVideo)));
    const lastVideo = videoForImage(b0, images[images.length - 1].id) || touched[0];
    /**
     * Referências uses the photos already on the card. Extra frames are planned only when
     * the card is Contínuo, including after an explicit "modo contínuo" already flipped it.
     */
    const plainVideos = b0.nodes.filter((n): n is BoardVideoNode => isVideo(n) && !n.edit && !n.fromGallery);
    const pickedVideo = plainVideos.find((v) => selectedIds.includes(v.id));
    /** The card the user already set (even before the photos are linked) wins over a new keyframe video. */
    const anchor = lastVideo || pickedVideo || (plainVideos.length === 1 ? plainVideos[0] : undefined);
    const cardMode: BoardImageMode = anchor?.imageMode || 'keyframes';
    const makeKeyframes = Boolean(keyframes) && cardMode !== 'reference';
    const imageMode: BoardImageMode = cardMode === 'reference' ? 'reference' : (makeKeyframes ? 'keyframes' : cardMode);
    const sceneCount = makeKeyframes ? Math.min(10, Math.max(2, intent.scene_count)) : images.length;
    if (images.length < 2 && !makeKeyframes) {
      throw new Error(imageMode === 'reference'
        ? 'Com uma imagem só, o modo Referências não gera quadros novos. Adicione outra imagem.'
        : 'Com uma imagem só o roteiro precisa de pelo menos 2 cenas: peça keyframes (ex.: "crie 3 cenas a partir dessa imagem") ou adicione outra imagem.');
    }
    images = images.slice(0, 10);
    patchMessage(msgId, { status: 'Montando roteiro…', busy: true });
    const groupId = touched.find((v) => v.flowGroup)?.flowGroup;
    const group = groupId ? flowGroupNodes(b0, groupId) : undefined;
    /** Re-planning a single trecho of a split storyboard keeps it one flow inside its group. */
    const inGroupFlow = group && touched.length === 1 ? touched[0] : undefined;
    const oldFlows = inGroupFlow ? [inGroupFlow] : group ? group.flows : anchor ? [anchor] : [];
    const oldJoin = inGroupFlow ? undefined : group?.join;
    const oldTotal = oldFlows.every((f) => f.planned) && oldFlows.length
      ? oldFlows.reduce((sum, f) => sum + (f.totalDurationSeconds || 0), 0)
      : undefined;
    const prevRequests = intent.reset_requests ? [] : oldFlows.find((f) => f.requests?.length)?.requests || [];
    const newRequests = userRequests.map((t) => t.trim()).filter(Boolean);
    const requests = newRequests.length
      ? newRequests.reduce(appendRequest, prevRequests)
      : prevRequests.length
      ? prevRequests
      : appendRequest([], intent.brief);
    const planned = reuse || await planFlow({
      orgId,
      brief: intent.brief,
      requests,
      images: images.map((n) => n.phase),
      keyframes: makeKeyframes,
      sceneCount,
      aspect: b0.aspectRatio,
      durationSeconds: intent.duration_seconds || oldTotal || undefined,
      newScenesPosition: intent.new_scenes_position,
    });
    const storyPhases = bindStoryboardPhases(planned.phases, images, makeKeyframes);
    const segments = inGroupFlow
      ? [{ start: 0, end: storyPhases.length }]
      : flowSegments(storyPhases, videoPick.videoModelId);
    const multi = segments.length > 1;
    const phases = multi
      ? segments.flatMap((s) => rebasePhases(storyPhases.slice(s.start, s.end)))
      : storyPhases;
    /** A single-flow video already generated keeps its full-length versions instead of becoming trecho 1. */
    const previousWhole = multi && !group && lastVideo && videoVersions(lastVideo).length ? lastVideo : undefined;
    const reusable = previousWhole ? [] : oldFlows;

    updateBoard((b) => {
      const lead = images.reduce((a, n) => (n.x < a.x ? n : a), images[0]);
      const origin = { x: lead.x, y: lead.y };
      const oldChain = oldFlows.flatMap((f) => cardMode === 'reference' ? ancestorImages(b, f.id) : chainForVideo(b, f.id));
      const keepIds = new Set(phases.map((p) => p.id));
      const dropIds = oldChain.filter((n) => !keepIds.has(n.id) && !n.phase.imageUrl).map((n) => n.id);
      const flowCount = multi ? segments.length : 1;
      const extraIds = [
        ...oldFlows.slice(flowCount).map((f) => f.id),
        ...(!multi && oldJoin ? [oldJoin.id] : []),
      ];
      let next = removeNodes(b, [...dropIds, ...extraIds]);
      const internal = new Set([
        ...phases.map((p) => p.id),
        ...oldChain.map((n) => n.id),
        ...oldFlows.map((f) => f.id),
        ...(oldJoin ? [oldJoin.id] : []),
      ]);
      next = { ...next, edges: next.edges.filter((e) => !(internal.has(e.from) && internal.has(e.to))) };

      const step = IMAGE_NODE_W + NODE_GAP_X;
      const nodes = [...next.nodes];
      phases.forEach((phase, i) => {
        const x = origin.x + i * step;
        const idx = nodes.findIndex((n) => n.id === phase.id);
        if (idx >= 0 && isImage(nodes[idx])) {
          const cur = nodes[idx] as BoardImageNode;
          nodes[idx] = {
            ...cur,
            x,
            y: origin.y,
            phase: {
              ...phase,
              id: cur.phase.id,
              label: cur.phase.label || phase.label,
              imageUrl: phase.imageUrl || cur.phase.imageUrl,
            },
          };
        } else if (!phase.imageUrl || !images.some((n) => n.phase.imageUrl === phase.imageUrl)) {
          nodes.push({ id: phase.id, kind: 'image', x, y: origin.y, phase });
        }
      });

      const reuse = (i: number): BoardVideoNode => {
        const old = reusable[i] && nodes.find((n) => n.id === reusable[i].id);
        return isVideo(old) ? { ...old } : createVideoNode(0, 0);
      };
      const upsert = (v: BoardVideoNode) => {
        const idx = nodes.findIndex((n) => n.id === v.id);
        if (idx >= 0) nodes[idx] = v;
        else nodes.push(v);
      };
      const common = {
        planned: true,
        brief: intent.brief,
        requests,
        requestPlan: planned.requestPlan,
        uncovered: planned.uncovered.length ? planned.uncovered : undefined,
        look: planned.look,
        intentHint: planned.intentHint,
        error: undefined,
      };
      const rowTopY = origin.y - Math.round((VIDEO_NODE_H - IMAGE_NODE_H) / 2);
      const links: Array<[string, string]> = [];
      const stampIds = new Set<string>();

      if (!multi) {
        const video = reuse(0);
        stampIds.add(video.id);
        upsert({
          ...video,
          ...(inGroupFlow ? {} : { x: origin.x + phases.length * step, y: rowTopY, flowGroup: undefined }),
          ...common,
          imageMode,
          totalDurationSeconds: phases[phases.length - 1]?.t_end || planned.totalDurationSeconds,
          status: video.videoUrl ? 'ready' : 'idle',
        });
        links.push(...storyboardEdges(imageMode, phases.map((p) => p.id), video.id));
      } else {
        const gid = groupId || newId('flows');
        const flowRowY = origin.y + IMAGE_NODE_H + 90;
        let minX = -Infinity;
        const flowIds = segments.map((s, i) => {
          const video = reuse(i);
          stampIds.add(video.id);
          const seg = phases.slice(s.start, s.end);
          const x = Math.max(origin.x + s.start * step, minX);
          minX = x + VIDEO_NODE_W + 32;
          upsert({
            ...video,
            ...common,
            imageMode,
            x,
            y: flowRowY,
            flowGroup: gid,
            joinOrder: undefined,
            title: seg.map((p) => p.label).filter(Boolean).join(' + ') || `Trecho ${i + 1}`,
            totalDurationSeconds: seg[seg.length - 1]?.t_end || 0,
            status: video.videoUrl ? 'ready' : 'idle',
          });
          links.push(...storyboardEdges(imageMode, seg.map((p) => p.id), video.id));
          return video.id;
        });
        const joinOld = oldJoin && nodes.find((n) => n.id === oldJoin.id);
        const join: BoardVideoNode = isVideo(joinOld) ? { ...joinOld } : createVideoNode(0, 0);
        upsert({
          ...join,
          x: Math.max(origin.x + phases.length * step, minX),
          y: rowTopY,
          flowGroup: gid,
          joinOrder: flowIds,
          title: 'Vídeo final',
          planned: undefined,
          status: join.videoUrl ? 'ready' : 'idle',
          error: undefined,
        });
        flowIds.forEach((id) => links.push([id, join.id]));
        const whole = previousWhole && nodes.find((n) => n.id === previousWhole.id);
        if (isVideo(whole)) {
          upsert({
            ...whole,
            x: origin.x,
            y: flowRowY + VIDEO_NODE_H + 70,
            title: 'Vídeo anterior (fluxo único)',
            planned: undefined,
            status: whole.videoUrl ? 'ready' : 'idle',
          });
        }
      }

      next = { ...next, nodes };
      for (const [from, to] of links) next = connect(next, from, to);
      return {
        ...next,
        nodes: next.nodes.map((n) => {
          if (!isVideo(n) || !stampIds.has(n.id)) return n;
          const chain = chainForVideo(next, n.id);
          // The card's prompt box now shows this script instead of what the user typed.
          const generator = n.generator?.promptSource === 'user' ? { ...n.generator, prompt: undefined, promptSource: 'script' as const } : n.generator;
          return chain.length ? { ...n, generator, scriptBasis: storyScriptKey(chain) } : { ...n, generator };
        }),
      };
    });

    const pending = phases.filter(phaseNeedsImage).length;
    window.setTimeout(() => canvasRef.current?.fitView(), 60);
    const total = multi ? planned.totalDurationSeconds : phases[phases.length - 1]?.t_end || planned.totalDurationSeconds;
    const left = planned.uncovered.length ? `\nFicou fora do roteiro: ${planned.uncovered.join('; ')}` : '';
    const why = multi ? flowSplitReason(planned.phases, videoPick.videoModelId) : null;
    const split = multi ? ` · ${segments.length} trechos${why ? ` (${why})` : ''}, juntados no vídeo final` : '';
    const readyNote = imageMode === 'reference'
      ? ' · as fotos entram como referência, sem quadros novos'
      : pending ? ` · ${pending} keyframe(s) a gerar` : ' · pronto para gerar o vídeo';
    return `Roteiro: ${phases.length} cenas · ${total}s${split}${readyNote}${left}`;
  }, [orgId, resolveImages, patchMessage, updateBoard, videoPick.videoModelId, selectedIds]);

  // ── Keyframes + video ────────────────────────────────────────

  const imageCredits = pickImageModel(imageModelId).creditsPerImage;

  const pendingKeyframes = useCallback((videoIds: string[]) => {
    const b = boardRef.current;
    return videoIds.flatMap((vid) => chainForVideo(b, vid).filter((n) => phaseNeedsImage(n.phase)).map((n) => ({ vid, id: n.id })));
  }, []);

  const runKeyframes = useCallback(async (videoIds: string[]) => {
    setCreditModal(null);
    const ids = videoIds.filter((id) => {
      const video = nodeById(boardRef.current, id);
      return !(isVideo(video) && video.imageMode === 'reference');
    });
    const targets = pendingKeyframes(ids);
    if (!targets.length) return;
    updateBoard((b) => ({ ...b, imageModelId }), { record: false });
    let done = 0;
    let lastError = '';
    for (const { vid, id } of targets) {
      patchPhase(id, { status: 'generating', error: undefined });
      try {
        const chain = chainForVideo(boardRef.current, vid);
        const index = chain.findIndex((n) => n.id === id);
        if (index < 0) throw new Error('Cena saiu do fluxo.');
        const video = nodeById(boardRef.current, vid) as BoardVideoNode | undefined;
        const { url, jobId } = await generateStill({
          orgId,
          phases: chain.map((n) => n.phase),
          index,
          imageModelId,
          aspect: boardRef.current.aspectRatio,
          intentHint: video?.intentHint,
          videoModelId: videoPick.videoModelId,
          onJob: (j) => patchPhase(id, { jobId: j }),
          onStatus: (label) => patchPhase(id, { error: label }),
        });
        patchPhase(id, { status: 'ready', imageUrl: url, jobId, error: undefined }, { ifInflight: true });
        done += 1;
      } catch (err) {
        lastError = friendlyError(err, 'Falha na geração da cena.');
        patchPhase(id, { status: 'failed', error: lastError }, { ifInflight: true });
        if (isCreditError(lastError) || lastError === insufficientMsg()) break;
      }
    }
    await refreshOrg(false);
    pushMessage({
      role: 'assistant',
      text: lastError
        ? `${done}/${targets.length} keyframe(s) geradas. ${lastError}`
        : `${done} keyframe(s) prontas. Peça “gerar vídeo” quando quiser.`,
      generationCredits: done * imageCredits,
      generationKind: 'image',
      error: Boolean(lastError),
    });
    if (!lastError && done > 0 && !pendingKeyframes(ids).length) setKeyframesDone(ids);
  }, [pendingKeyframes, updateBoard, imageModelId, patchPhase, orgId, videoPick.videoModelId, friendlyError, insufficientMsg, refreshOrg, pushMessage, imageCredits]);

  const flowPhases = useCallback((videoId: string) => {
    const video = nodeById(boardRef.current, videoId);
    // Background replace / V2V-only edits ignore the storyboard chain.
    if (isVideo(video) && video.generator?.v2vOnly) return [];
    if (isVideo(video) && generatorHasImages(video)) {
      return phasesFromGenerator(video, video.totalDurationSeconds || videoPick.durationSeconds);
    }
    return chainForVideo(boardRef.current, videoId).map((n) => n.phase);
  }, [videoPick.durationSeconds]);

  /** create-video session of one flow: the node's own model when it has one, else the page pick. */
  const sessionFor = useCallback((video: BoardVideoNode, phases: FragmentadorSessionPhase[], pick: VideoModelPickerValue): FragmentadorSession => {
    const total = phases[phases.length - 1]?.t_end || video.totalDurationSeconds || pick.durationSeconds;
    return {
      brief: video.brief || '',
      startUrl: phases[0]?.imageUrl || '',
      endUrl: phases[phases.length - 1]?.imageUrl || '',
      imageModelId,
      videoModelId: pick.videoModelId,
      resolutionId: pick.resolutionId,
      durationSeconds: total,
      aspectRatio: boardRef.current.aspectRatio,
      generateAudio: Boolean(pick.generateAudio),
      look: video.look || 'cinematic',
      intentHint: video.intentHint || 'camera_showcase',
      phases,
      totalDurationSeconds: total,
      ...(video.requestPlan || {}),
    };
  }, [imageModelId]);

  /** Full prompt of the flow's current script, as the generator card shows it. */
  const scriptPrompt = useCallback((video: BoardVideoNode, phases: FragmentadorSessionPhase[]): string => {
    if (!phases.length) return '';
    const modelId = video.videoModelId && getVideoModelById(video.videoModelId) ? video.videoModelId : videoPick.videoModelId;
    const pick: VideoModelPickerValue = { ...videoPick, videoModelId: modelId, resolutionId: video.resolutionId || videoPick.resolutionId };
    try {
      return flowPromptText(sessionFor(video, phases, pick), video.imageMode || 'keyframes');
    } catch {
      return '';
    }
  }, [sessionFor, videoPick]);

  /**
   * The arrows and the photos are the source of truth. If either changed since the script was
   * written, rewrite the scenes in the current order without moving nodes.
   */
  const syncScript = useCallback(async (videoId: string): Promise<boolean> => {
    const video = nodeById(boardRef.current, videoId);
    if (!isVideo(video) || !video.planned || video.edit || video.fromGallery) return false;
    const chain = chainForVideo(boardRef.current, videoId);
    if (chain.length < 2 || chain.some((n) => phaseNeedsImage(n.phase))) return false;
    const key = storyScriptKey(chain);
    const edited = chain.some((n) => {
      const versions = imageVersions(n);
      return versions.length > 1 && Boolean(n.phase.imageUrl) && n.phase.imageUrl !== versions[0].imageUrl;
    });
    if (video.scriptBasis === key || (!video.scriptBasis && !edited)) {
      if (!video.scriptBasis) {
        updateBoard((b) => ({
          ...b,
          nodes: b.nodes.map((n) => (n.id === videoId && isVideo(n) ? { ...n, scriptBasis: key } : n)),
        }));
      }
      return false;
    }
    const planned = await planFlow({
      orgId,
      brief: video.brief || '',
      requests: video.requests,
      images: chain.map((n) => n.phase),
      keyframes: false,
      sceneCount: chain.length,
      aspect: boardRef.current.aspectRatio,
      durationSeconds: video.totalDurationSeconds,
      rewrite: true,
    });
    let applied = false;
    updateBoard((b) => {
      const liveVideo = nodeById(b, videoId);
      if (!isVideo(liveVideo)) return b;
      const live = chainForVideo(b, videoId);
      if (storyScriptKey(live) !== key) return b;
      applied = true;
      return {
        ...b,
        nodes: b.nodes.map((n) => {
          if (n.id === videoId && isVideo(n)) {
            return {
              ...n,
              scriptBasis: key,
              requestPlan: planned.requestPlan,
              look: planned.look,
              intentHint: planned.intentHint,
              totalDurationSeconds: planned.totalDurationSeconds,
              uncovered: planned.uncovered.length ? planned.uncovered : undefined,
            };
          }
          if (!isImage(n)) return n;
          const i = live.findIndex((c) => c.id === n.id);
          const phase = planned.phases[i];
          if (i < 0 || !phase) return n;
          return {
            ...n,
            phase: {
              ...phase,
              id: n.phase.id,
              imageUrl: n.phase.imageUrl,
              status: n.phase.imageUrl ? 'ready' as const : n.phase.status,
              jobId: n.phase.jobId,
            },
          };
        }),
      };
    });
    return applied;
  }, [orgId, updateBoard]);

  const syncScripts = useCallback(async (videoIds: string[]): Promise<boolean> => {
    let changed = false;
    for (const id of videoIds) {
      try {
        if (await syncScript(id)) changed = true;
      } catch (err) {
        console.warn('script sync failed', err);
      }
    }
    return changed;
  }, [syncScript]);

  /**
   * Renders one flow. `batch`: the caller joins downstream once and reports all flows together.
   * `resume`: the page was reopened mid-generation — wait on the jobs saved in the node (and stitch) with
   * the node's own model, no script rewrite and no new charge.
   */
  const runVideo = useCallback(async (videoId: string, batch = false, resume = false): Promise<{ ok: boolean; credits: number; error?: string }> => {
    setCreditModal(null);
    const markAssembling = (on: boolean) => {
      setAssemblingIds((cur) => (on
        ? (cur.includes(videoId) ? cur : [...cur, videoId])
        : cur.filter((id) => id !== videoId)));
    };
    const beforeRun = nodeById(boardRef.current, videoId);
    if (!resume) {
      markAssembling(true);
      // V2V background replace must not rewrite the storyboard (and must not wait on fragmentador-plan).
      if (!(isVideo(beforeRun) && beforeRun.generator?.v2vOnly)) {
        try { await syncScript(videoId); } catch { /* keep the script already on the board */ }
      }
    }
    const video = nodeById(boardRef.current, videoId);
    if (!isVideo(video)) {
      markAssembling(false);
      return { ok: false, credits: 0 };
    }
    const phases = flowPhases(videoId);
    const total = phases[phases.length - 1]?.t_end || video.totalDurationSeconds || videoPick.durationSeconds;
    const ownModel = resume && video.videoModelId && getVideoModelById(video.videoModelId);
    // Debit modal choice (videoPick) wins over a stale node model (e.g. Kling left from a prior try).
    const v2vFallback = video.generator?.v2vOnly
      ? (listVideoReferenceModels().find((m) => m.id === videoPick.videoModelId)
        || listVideoReferenceModels().find((m) => m.id === videoReferenceSiblingId(videoPick.videoModelId || ''))
        || listVideoReferenceModels().find((m) => m.id === video.videoModelId)
        || preferredVideoReferenceModel()
        || listVideoReferenceModels()[0])
      : undefined;
    const pick: VideoModelPickerValue = ownModel
      ? { videoModelId: video.videoModelId!, resolutionId: video.resolutionId || videoPick.resolutionId, durationSeconds: total, generateAudio: video.generateAudio ?? videoPick.generateAudio }
      : v2vFallback
        ? {
            videoModelId: v2vFallback.id,
            resolutionId: video.resolutionId
              || v2vFallback.resolutions.find((r) => r.id === videoPick.resolutionId)?.id
              || v2vFallback.resolutions[0]?.id
              || videoPick.resolutionId,
            durationSeconds: total,
            generateAudio: video.generateAudio ?? videoPick.generateAudio,
          }
      : { ...videoPick, durationSeconds: total };
    const imageMode = video.imageMode || 'keyframes';
    // With a script, the user's words are already inside it; only text the agent has not planned yet goes in front.
    const userText = video.generator?.promptSource === 'user' || !phases.length
      ? (video.generator?.prompt || video.brief || '').trim()
      : '';
    const spoken = [
      userText,
      video.generator?.music ? 'Include background music.' : '',
      video.generator?.draft ? 'Draft preview, simpler motion.' : '',
    ].filter(Boolean).join('\n');
    const refVideo = video.generator?.referenceVideo?.url;
    const refuse = (error: string) => {
      markAssembling(false);
      if (!batch) pushMessage({ role: 'assistant', text: error, error: true });
      return { ok: false as const, credits: 0, error };
    };
    if (!phases.length && !refVideo) return refuse('Adicione imagem inicial, final, anexos ou um vídeo de referência.');
    if (!phases.length && !getVideoModelById(pick.videoModelId)?.modalities.includes('v2v')) {
      return refuse('Este modelo não aceita vídeo de referência. Escolha um modelo V2V.');
    }
    if (!phases.length && !spoken.trim()) return refuse('Escreva o prompt para o vídeo de referência.');
    const quoteModel = getVideoModelById(pick.videoModelId);
    const quoteRes = quoteModel?.resolutions.find((r) => r.id === pick.resolutionId) || quoteModel?.resolutions[0];
    const credits = phases.length
      ? estimateFlowVideoCredits(phases, pick, imageMode)
      : (quoteModel && quoteRes
        ? estimateCredits({
          creditsPerSecond: rateForGeneratedAudio(quoteRes, Boolean(pick.generateAudio) && supportsGeneratedAudio(quoteModel)).creditsPerSecond,
          durationSeconds: total,
          referenceDurationSeconds: quoteModel.billedOutputPlusRef ? total : 0,
          billedOutputPlusRef: quoteModel.billedOutputPlusRef,
        })
        : 0);
    const session = sessionFor(video, phases, pick);
    const sameRender = video.videoModelId === pick.videoModelId && video.resolutionId === pick.resolutionId && !video.clipUrl;
    const retryParts = sameRender ? video.partUrls : undefined;
    const retryJobs = resume && sameRender ? video.partJobIds : undefined;
    markAssembling(false);
    patchVideo(videoId, {
      status: 'generating',
      progress: resume ? 'Retomando…' : 'Enviando…',
      error: undefined,
      videoModelId: pick.videoModelId,
      resolutionId: pick.resolutionId,
      generateAudio: pick.generateAudio,
      versions: videoVersions(video),
      clipUrl: undefined,
    });
    const runOrg = loadedOrgRef.current;
    const runProject = activeProjectIdRef.current;
    if (runOrg && runProject) markVideoRunning(runOrg, runProject, videoId, true);
    try {
      const res = phases.length
        ? await generateFlowVideo({
          orgId,
          session,
          pick,
          stitch,
          galleryTitle: projectTitleRef.current,
          imageMode,
          openBridge: imageMode !== 'reference' ? openBridge : undefined,
          existingPartUrls: retryParts,
          existingPartJobIds: retryJobs,
          userPrompt: spoken || undefined,
          referenceVideoUrl: refVideo,
          audioUrl: video.generator?.audio?.url,
          onProgress: ({ label, partUrls, partJobIds }) => patchVideo(videoId, { progress: label, partUrls, partJobIds }),
        })
        : await generateReferenceClip({
          orgId,
          prompt: spoken,
          referenceVideoUrl: refVideo || '',
          referenceImageUrls: (video.generator?.attachments || []).map((a) => a.url).filter(Boolean),
          audioUrl: video.generator?.audio?.url,
          pick,
          aspectRatio: boardRef.current.aspectRatio,
          galleryTitle: projectTitleRef.current,
          onProgress: (label) => patchVideo(videoId, { progress: label }),
        }).then((r) => ({ clipUrl: r.clipUrl, partUrls: [r.clipUrl], partJobIds: [] as string[], parts: 1 }));
      const current = nodeById(boardRef.current, videoId);
      patchVideo(videoId, {
        ...(isVideo(current)
          ? withNewVideoVersion(current, {
              clipUrl: res.clipUrl,
              createdAt: Date.now(),
              videoModelId: pick.videoModelId,
              resolutionId: pick.resolutionId,
              seconds: total,
              parts: res.parts,
              credits,
              ...(current.brief ? { brief: current.brief } : {}),
              scenes: chainForVideo(boardRef.current, videoId).map((n) => ({ label: n.phase.label, description: n.phase.description_pt })),
            })
          : { clipUrl: res.clipUrl }),
        partUrls: res.partUrls,
        partJobIds: res.partJobIds,
        progress: undefined,
      }, { ifInflight: true });
      if (!batch && res.parts > 1 && !upstreamVideos(boardRef.current, videoId).length) {
        generationToast?.pushCompletionToast({
          id: `tela-video-${Date.now()}`,
          title: 'Vídeo pronto',
          message: `${res.parts} partes juntadas. Clique para visualizar.`,
          mediaUrl: res.clipUrl,
          mediaType: 'video',
        });
      }
      const skip = batch ? new Set(downstreamVideos(boardRef.current, videoId).map((d) => d.id)) : undefined;
      await settleVideo(videoId, skip);
      const done = nodeById(boardRef.current, videoId);
      if (isVideo(done) && projectTitleRef.current) {
        renameMediaAssetsByUrl(orgId, [done.clipUrl, done.videoUrl].filter((u): u is string => Boolean(u)), projectTitleRef.current)
          .catch(() => undefined);
      }
      if (!batch) {
        const joined = downstreamVideos(boardRef.current, videoId).length > 0;
        pushMessage({
          role: 'assistant',
          text: joined ? 'Trecho pronto — o vídeo final foi atualizado.' : 'Vídeo pronto no fim do fluxo.',
          generationCredits: credits,
          generationKind: 'video',
        });
      }
      return { ok: true, credits };
    } catch (err) {
      const msg = friendlyError(err, 'Falha na geração do vídeo.');
      patchVideo(videoId, { status: 'failed', progress: undefined, error: msg }, { ifInflight: true });
      if (!batch) pushMessage({ role: 'assistant', text: msg, error: true });
      return { ok: false, credits: 0, error: msg };
    } finally {
      if (runOrg && runProject) markVideoRunning(runOrg, runProject, videoId, false);
      if (!batch) await refreshOrg(false);
    }
  }, [syncScript, flowPhases, sessionFor, videoPick, patchVideo, orgId, stitch, openBridge, generationToast, settleVideo, pushMessage, friendlyError, refreshOrg]);

  /**
   * Work left running when the page was closed: jobs already queued keep rendering on the server, so on
   * return the page waits on them, stitches the parts and rebuilds pending joins instead of asking again.
   */
  const finishRemixJob = useCallback(async (videoId: string, jobId: string, announce = true) => {
    try {
      const url = await waitForJobResultUrl(jobId, {
        timeoutMs: 60 * 60 * 1000,
        onStatus: (job) => patchVideo(videoId, { progress: jobStatusLabel(job) }, { ifInflight: true }),
      });
      const current = nodeById(boardRef.current, videoId);
      if (!isVideo(current)) return false;
      patchVideo(videoId, {
        ...withNewVideoVersion(current, {
          clipUrl: url,
          createdAt: Date.now(),
          videoModelId: current.videoModelId,
          resolutionId: current.resolutionId,
          seconds: current.totalDurationSeconds,
        }),
        videoUrl: url,
        status: 'ready',
        progress: undefined,
        error: undefined,
        remixJobId: undefined,
        templateJobId: jobId,
      }, { ifInflight: true });
      await settleVideo(videoId);
      if (announce) {
        pushMessage({ role: 'assistant', text: 'Vídeo do modelo pronto no fluxo.', generationKind: 'video' });
        await refreshOrg(false);
      }
      return true;
    } catch (err) {
      const msg = friendlyError(err, 'Falha na geração do vídeo.');
      patchVideo(videoId, { status: 'failed', progress: undefined, error: msg }, { ifInflight: true });
      if (announce) pushMessage({ role: 'assistant', text: msg, error: true });
      return false;
    }
  }, [patchVideo, settleVideo, pushMessage, friendlyError, refreshOrg]);

  /** Re-renders an Explore clip from its stored prompt, with the person's change applied on the server. */
  const runReviseTemplate = useCallback(async (videoId: string, change: string) => {
    if (!orgId) {
      setError('Você não está em nenhuma organização.');
      return;
    }
    const video = nodeById(boardRef.current, videoId);
    if (!isVideo(video)) return;
    patchVideo(videoId, { status: 'generating', progress: 'Ajustando o prompt…', error: undefined, videoUrl: video.videoUrl });
    try {
      const result = await remixTemplate({
        organization_id: orgId,
        intent: 'revise',
        source_job_id: video.templateJobId,
        title: video.title,
        instruction: change,
      });
      if (!result.job_id) throw new Error('Não foi possível enfileirar o vídeo.');
      patchVideo(videoId, { remixJobId: result.job_id, templateJobId: result.job_id, progress: 'Enviando…' });
      pushMessage({ role: 'assistant', text: 'O vídeo do modelo está sendo gerado de novo com o prompt ajustado.' });
      void finishRemixJob(videoId, result.job_id);
    } catch (err) {
      const msg = friendlyError(err, 'Falha ao ajustar o vídeo.');
      patchVideo(videoId, { status: video.videoUrl ? 'ready' : 'failed', progress: undefined, error: msg });
      pushMessage({ role: 'assistant', text: msg, error: true });
    }
  }, [orgId, patchVideo, pushMessage, friendlyError, finishRemixJob]);

  const resumeProjectRef = useRef('');
  useEffect(() => {
    if (!orgId || !activeProjectId || resumeProjectRef.current === activeProjectId) return;
    resumeProjectRef.current = activeProjectId;
    if (readOnlyRef.current) return;
    const b = boardRef.current;
    // Recompose/edit keeps the prior imageUrl (v1) while status=generating — still resume by jobId.
    const stills = b.nodes.filter(isImage).filter((n) => n.phase.status === 'generating' && n.phase.jobId);
    const videos = b.nodes.filter(isVideo).filter((n) => n.status === 'generating' && videoResumable(n) && !isVideoRunning(orgId, activeProjectId, n.id));
    const joins = b.nodes.filter(isVideo).filter((n) => n.status === 'joining');
    if (!stills.length && !videos.length && !joins.length) return;
    void (async () => {
      await Promise.all(stills.map(async (n) => {
        try {
          const url = await resumeStillJob({
            orgId,
            jobId: n.phase.jobId as string,
            aspect: b.aspectRatio,
            title: `Tela em branco · ${n.phase.label || 'cena'}`,
            videoModelId: videoPick.videoModelId,
            onStatus: (label) => patchPhase(n.id, { error: label }),
          });
          const cur = nodeById(boardRef.current, n.id);
          if (isImage(cur) && cur.phase.imageUrl && cur.phase.imageUrl !== url) {
            const size = await readImageSize(url).catch(() => null);
            patchImageNode(n.id, withNewImageVersion(cur, {
              imageUrl: url,
              createdAt: Date.now(),
              ...(size ? { width: size.width, height: size.height } : {}),
              instruction: 'Geração retomada',
            }), { ifInflight: true });
          }
          patchPhase(n.id, { status: 'ready', imageUrl: url, error: undefined, jobId: n.phase.jobId }, { ifInflight: true });
        } catch (err) {
          // Prefer prior still over a permanent spinner if the job is gone / timed out.
          const cur = nodeById(boardRef.current, n.id);
          if (isImage(cur) && cur.phase.imageUrl) {
            patchPhase(n.id, { status: 'ready', error: undefined }, { ifInflight: true });
          } else {
            patchPhase(n.id, { status: 'failed', error: friendlyError(err, 'Falha na geração da cena.') }, { ifInflight: true });
          }
        }
      }));
      const remixes = videos.filter((n) => n.remixJobId);
      const flows = videos.filter((n) => !n.remixJobId);
      const remixResults = await Promise.all(remixes.map((n) => finishRemixJob(n.id, n.remixJobId as string, false)));
      const results = [
        ...remixResults.map((ok) => ({ ok, credits: 0 })),
        ...await Promise.all(flows.map((n) => runVideo(n.id, true, true))),
      ];
      for (const n of joins) {
        const cur = nodeById(boardRef.current, n.id);
        if (!isVideo(cur) || cur.status !== 'joining') continue;
        const ready = cur.edit || Boolean(videoSequence(boardRef.current, n.id)?.length);
        if (ready) await settleVideo(n.id);
        else patchVideo(n.id, { status: 'failed', progress: undefined, error: 'Interrompido — peça para gerar de novo.' }, { ifInflight: true });
      }
      const okCount = results.filter((r) => r.ok).length;
      if (videos.length) {
        pushMessage({
          role: 'assistant',
          text: okCount === videos.length
            ? `Retomei ${videos.length === 1 ? 'a geração que estava em andamento' : `${videos.length} gerações em andamento`}: pronto.`
            : `Retomei ${videos.length} geração(ões): ${okCount} pronta(s), ${videos.length - okCount} com falha.`,
          error: okCount !== videos.length,
        });
        await refreshOrg(false);
      }
    })();
  }, [orgId, activeProjectId, runVideo, finishRemixJob, settleVideo, patchPhase, patchImageNode, patchVideo, pushMessage, friendlyError, refreshOrg, videoPick.videoModelId]);

  // Leaving mid-generation drops the browser side of the work (polling, stitching, joins); warn first.
  const anyBusy = busyVideoIds.size > 0 || sending
    || board.nodes.some((n) => (isImage(n) && n.phase.status === 'generating') || (isAudio(n) && n.status === 'generating'));
  useEffect(() => {
    if (!anyBusy) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [anyBusy]);

  /**
   * A flow longer (or with more images) than the picked model takes in one video is split on the
   * board into trechos plus a final join, keeping its script. Returns the flows to render.
   */
  const splitToFit = useCallback(async (videoIds: string[]): Promise<string[]> => {
    const out: string[] = [];
    for (const id of videoIds) {
      const video = nodeById(boardRef.current, id);
      const chain = chainForVideo(boardRef.current, id);
      const phases = chain.map((n) => n.phase);
      const fits = flowParts(phases, videoPick.videoModelId, (isVideo(video) && video.imageMode) || 'keyframes').length <= 1;
      if (!isVideo(video) || video.flowGroup || video.edit || video.fromGallery || !video.planned || fits) {
        out.push(id);
        continue;
      }
      const note = await buildStoryboard(
        { image_node_ids: chain.map((n) => n.id), brief: video.brief || '', keyframes: false, scene_count: chain.length },
        '',
        [],
        {
          phases,
          look: video.look || 'cinematic',
          intentHint: video.intentHint || 'camera_showcase',
          totalDurationSeconds: phases[phases.length - 1]?.t_end || video.totalDurationSeconds || 0,
          requestPlan: video.requestPlan || {},
          uncovered: video.uncovered || [],
        },
      );
      const gid = (nodeById(boardRef.current, id) as BoardVideoNode | undefined)?.flowGroup;
      const flows = gid ? flowGroupNodes(boardRef.current, gid).flows.map((f) => f.id) : [id];
      out.push(...flows);
      if (note) pushMessage({ role: 'assistant', text: `Dividi o fluxo para caber no modelo. ${note}` });
    }
    return out;
  }, [videoPick.videoModelId, buildStoryboard, pushMessage]);

  /** Renders several flows in parallel, then re-joins what they feed once. */
  const runVideos = useCallback(async (requested: string[]) => {
    setCreditModal(null);
    const videoIds = await splitToFit(requested);
    if (videoIds.length === 1) {
      await runVideo(videoIds[0]);
      return;
    }
    const results = await Promise.all(videoIds.map((id) => runVideo(id, true)));
    const b = boardRef.current;
    const ids = new Set(videoIds);
    const joins = Array.from(new Set(videoIds.flatMap((id) => downstreamVideos(b, id).map((d) => d.id)))).filter((id) => !ids.has(id));
    for (const id of joins) await settleVideo(id);
    await refreshOrg(false);
    const ok = results.filter((r) => r.ok).length;
    const credits = results.reduce((sum, r) => sum + r.credits, 0);
    const firstError = results.find((r) => !r.ok)?.error;
    pushMessage({
      role: 'assistant',
      text: firstError
        ? `${ok}/${videoIds.length} trechos prontos. ${firstError} Peça “gerar vídeo” de novo para refazer só o que falhou.`
        : `${ok} trechos prontos e juntados no vídeo final.`,
      ...(credits ? { generationCredits: credits, generationKind: 'video' as const } : {}),
      error: Boolean(firstError),
    });
  }, [splitToFit, runVideo, settleVideo, refreshOrg, pushMessage]);

  const selectVideoVersion = useCallback((videoId: string, versionId: string) => {
    const video = nodeById(boardRef.current, videoId);
    if (!isVideo(video) || video.status === 'generating' || video.status === 'joining') return;
    const version = videoVersions(video).find((v) => v.id === versionId);
    if (!version || version.clipUrl === video.clipUrl) return;
    patchVideo(videoId, { versions: videoVersions(video), clipUrl: version.clipUrl, error: undefined });
    void settleVideo(videoId);
  }, [patchVideo, settleVideo]);

  // ── Sound ────────────────────────────────────────────────────

  /** Voice nodes with a finished track that feed the same edit node (Add Instrumental sources). */
  const voiceSources = useCallback((audioId: string): BoardAudioNode[] => {
    const b = boardRef.current;
    const edit = editNodeForAudio(b, audioId);
    return edit ? audioInputs(b, edit.id).filter((a) => a.audioKind === 'voice' && a.audioUrl && a.id !== audioId) : [];
  }, []);

  const openAudioModalRef = useRef<(audioId: string) => void>(() => undefined);
  const nextAudioModal = useCallback(() => {
    audioModalOpenRef.current = false;
    const next = audioModalQueueRef.current.shift();
    if (next) window.setTimeout(() => openAudioModalRef.current(next), 0);
  }, []);

  const openAudioModal = useCallback((audioId: string) => {
    const node = nodeById(boardRef.current, audioId);
    if (!isAudio(node)) return;
    if (audioModalOpenRef.current) {
      if (!audioModalQueueRef.current.includes(audioId)) audioModalQueueRef.current.push(audioId);
      return;
    }
    audioModalOpenRef.current = true;
    setAudioText(node.text);
    setAudioLoop(Boolean(node.loop));
    setAudioSourceId(node.sourceAudioId || voiceSources(audioId)[0]?.id || '');
    if (node.voiceModelId) {
      setAudioPicks((p) => ({ ...p, [node.audioKind]: { voiceModelId: node.voiceModelId as string, voiceId: node.voiceId || '' } }));
    }
    rememberDebitRef.current({ kind: 'audio', audioId });
  }, [voiceSources]);
  openAudioModalRef.current = openAudioModal;

  const runAudio = useCallback(async (audioId: string) => {
    setCreditModal(null);
    nextAudioModal();
    const node = nodeById(boardRef.current, audioId);
    if (!isAudio(node)) return;
    const pick = audioPicks[node.audioKind];
    const model = pickAudioModel(pick.voiceModelId, node.audioKind);
    const text = audioText.trim() || node.text;
    const source = model.requiresSourceAudio ? nodeById(boardRef.current, audioSourceId) : undefined;
    const edit = editNodeForAudio(boardRef.current, audioId);
    const seconds = edit ? videoSeconds(boardRef.current, edit.id) : 0;
    const loop = model.poyoModel === 'generate-sounds' ? audioLoop : node.loop;
    patchAudio(audioId, {
      text,
      voiceModelId: model.id,
      voiceId: pick.voiceId,
      loop,
      sourceAudioId: isAudio(source) ? source.id : undefined,
      status: 'generating',
      progress: 'Enviando…',
      error: undefined,
    });
    const label = node.title || AUDIO_LABELS[node.audioKind].title;
    try {
      const res = await generateBoardAudio({
        orgId,
        kind: node.audioKind,
        text,
        voiceModelId: model.id,
        voiceId: pick.voiceId,
        loop,
        sourceAudioUrl: isAudio(source) ? source.audioUrl : undefined,
        durationSeconds: node.audioKind === 'music' && seconds ? seconds : undefined,
        title: projectTitleRef.current || label,
        onStatus: (status) => patchAudio(audioId, { progress: status }),
      });
      const current = nodeById(boardRef.current, audioId);
      if (!isAudio(current)) return;
      patchAudio(audioId, {
        ...withNewAudioVersion(current, {
          audioUrl: res.url,
          createdAt: Date.now(),
          text,
          voiceModelId: model.id,
          voiceId: pick.voiceId,
          seconds: res.seconds,
          credits: res.credits,
        }),
        status: 'ready',
        progress: undefined,
      }, { ifInflight: true });
      const target = editNodeForAudio(boardRef.current, audioId);
      if (target) await settleVideo(target.id);
      const span = seconds - (current.startOffset || 0);
      const long = node.audioKind === 'voice' && seconds > 0 && res.seconds / (current.speed || 1) > span * 1.1;
      pushMessage({
        role: 'assistant',
        text: long
          ? `${label} pronta (${res.seconds.toFixed(1)}s), mas o vídeo tem ${seconds}s — o fim foi cortado. Peça “acelere a locução” ou um texto mais curto.`
          : target
            ? `${label} pronta e mixada no vídeo editado.`
            : `${label} pronta.`,
        generationCredits: res.credits,
        generationKind: 'audio',
      });
    } catch (err) {
      const msg = friendlyError(err, 'Falha ao gerar o som.');
      patchAudio(audioId, { status: 'failed', progress: undefined, error: msg }, { ifInflight: true });
      pushMessage({ role: 'assistant', text: msg, error: true });
    } finally {
      await refreshOrg(false);
    }
  }, [audioPicks, audioText, audioLoop, audioSourceId, orgId, patchAudio, settleVideo, pushMessage, friendlyError, refreshOrg, nextAudioModal]);

  const selectAudioVersion = useCallback((audioId: string, versionId: string) => {
    const node = nodeById(boardRef.current, audioId);
    if (!isAudio(node) || node.status === 'generating') return;
    const version = audioVersions(node).find((v) => v.id === versionId);
    if (!version || version.audioUrl === node.audioUrl) return;
    patchAudio(audioId, {
      audioUrl: version.audioUrl,
      seconds: version.seconds,
      text: version.text,
      voiceModelId: version.voiceModelId,
      voiceId: version.voiceId,
      error: undefined,
    });
    const edit = editNodeForAudio(boardRef.current, audioId);
    if (edit) void settleVideo(edit.id);
  }, [patchAudio, settleVideo]);

  /** Track settings changed on the board (volume, mute, start…): remix without new credits. */
  const updateAudioTrack = useCallback((audioId: string, patch: Partial<Pick<BoardAudioNode, 'volume' | 'startOffset' | 'loop' | 'muted' | 'speed'>>) => {
    const clean: typeof patch = {};
    if (patch.volume != null && Number.isFinite(patch.volume)) clean.volume = Math.max(0, Math.min(1.5, patch.volume));
    if (patch.startOffset != null && Number.isFinite(patch.startOffset)) clean.startOffset = Math.max(0, patch.startOffset);
    if (patch.speed != null && Number.isFinite(patch.speed)) clean.speed = Math.max(0.5, Math.min(2, patch.speed));
    if (patch.loop != null) clean.loop = patch.loop;
    if (patch.muted != null) clean.muted = patch.muted;
    patchAudio(audioId, clean);
    const edit = editNodeForAudio(boardRef.current, audioId);
    if (edit) void settleVideo(edit.id);
  }, [patchAudio, settleVideo]);

  /** The "Vídeo editado" fed by `videoId`, created to its right when missing. */
  const ensureEditNode = useCallback((videoId: string): BoardVideoNode | undefined => {
    const b = boardRef.current;
    const existing = editNodeForVideo(b, videoId);
    if (existing) return existing;
    const source = nodeById(b, videoId);
    if (!isVideo(source)) return undefined;
    const edit = createEditNode(source.x + VIDEO_NODE_W + 140, source.y);
    updateBoard((x) => connect({ ...x, nodes: [...x.nodes, edit] }, source.id, edit.id));
    return edit;
  }, [updateBoard]);

  const resolveVideoId = useCallback((explicit?: string): string | undefined => {
    const b = boardRef.current;
    if (explicit && isVideo(nodeById(b, explicit))) return explicit;
    const videos = b.nodes.filter(isVideo);
    const selectedVideo = videos.find((v) => selectedIds.includes(v.id));
    if (selectedVideo) return selectedVideo.id;
    const selectedAudio = selectedIds.map((id) => nodeById(b, id)).find(isAudio);
    const viaAudio = selectedAudio ? editNodeForAudio(b, selectedAudio.id) : undefined;
    if (viaAudio) return viaAudio.id;
    const viaImage = selectedIds.map((id) => videoForImage(b, id)).find(Boolean);
    if (viaImage) return viaImage.id;
    const sinks = videos.filter((v) =>
      !v.fromGallery &&
      (chainForVideo(b, v.id).length > 0 || upstreamVideos(b, v.id).length > 0) &&
      !downstreamVideos(b, v.id).length);
    if (sinks.length === 1) return sinks[0].id;
    const withFlow = videos.filter((v) => chainForVideo(b, v.id).length > 0);
    if (withFlow.length === 1) return withFlow[0].id;
    return withFlow.find((v) => !v.videoUrl)?.id || withFlow[0]?.id;
  }, [selectedIds]);

  // ── Normalize ────────────────────────────────────────────────

  const showImageStep = useCallback((m: ImageStepModal) => {
    if (m.kind === 'edit-image') setEditText(m.instruction);
    if (m.kind === 'outpaint') {
      setOutpaintMode(m.preferRecompose ? 'recompose' : 'expand');
      if (m.preferRecompose) {
        setRecomposeModelId((id) => pickImageModelForUseCase('recompose', id).id);
      } else {
        setOutpaintModelId((id) => pickImageModelForUseCase('outpaint', id).id);
      }
    }
    rememberDebit(m);
  }, [rememberDebit]);

  const offerImageStep = useCallback((m: ImageStepModal) => {
    if (imageStepBusyRef.current) {
      imageStepQueueRef.current.push(m);
      return;
    }
    imageStepBusyRef.current = true;
    showImageStep(m);
  }, [showImageStep]);

  /** Called when an expand/edit step is cancelled or done. */
  const nextImageStep = useCallback(() => {
    const next = imageStepQueueRef.current.shift();
    imageStepBusyRef.current = Boolean(next);
    if (next) showImageStep(next);
  }, [showImageStep]);

  const runImageStep = useCallback((run: () => Promise<unknown>) => {
    imageStepRunningRef.current = true;
    void run().finally(() => {
      imageStepRunningRef.current = false;
      nextImageStep();
    });
  }, [nextImageStep]);

  // A step was cancelled (or replaced by another confirmation): open the next one once the modal closes.
  useEffect(() => {
    if (!creditModal && imageStepBusyRef.current && !imageStepRunningRef.current) nextImageStep();
  }, [creditModal, nextImageStep]);

  /** Ready images among `ids`, in board order. */
  const editableImages = useCallback((ids: string[]) => (
    boardRef.current.nodes
      .filter(isImage)
      .filter((n) => ids.includes(n.id) && Boolean(n.phase.imageUrl) && n.phase.status === 'ready')
  ), []);

  const normalizeImages = useCallback(async (
    intent: { node_ids: string[]; aspect_ratio?: string; match_resolution?: boolean },
    msgId: string,
  ): Promise<string> => {
    const b = boardRef.current;
    const pool = b.nodes.filter(isImage).filter((n) => n.phase.imageUrl && n.phase.status === 'ready');
    const targets = intent.node_ids.length ? pool.filter((n) => intent.node_ids.includes(n.id)) : pool;
    if (!targets.length) throw new Error('Não há imagens prontas para ajustar.');
    patchMessage(msgId, { status: `Analisando ${targets.length} imagem(ns)…`, busy: true });
    const sizes = await Promise.all(targets.map((n) => readImageSize(n.phase.imageUrl as string)));
    const plan = planNormalizedSizes(sizes, { aspect: intent.aspect_ratio, matchResolution: intent.match_resolution });
    const jobs: NormalizeJob[] = targets
      .map((n, i) => ({ nodeId: n.id, frame: plan[i], outpaint: needsOutpaint(sizes[i], plan[i]), same: sameSize(sizes[i], plan[i]) }))
      .filter((j) => !j.same)
      .map(({ nodeId, frame, outpaint }) => ({ nodeId, frame, outpaint }));
    const aspect = isBoardAspect(intent.aspect_ratio) ? intent.aspect_ratio : undefined;
    if (!jobs.length) {
      if (aspect) updateBoard((x) => ({ ...x, aspectRatio: aspect }));
      return 'As imagens já estavam nesse formato.';
    }
    const res = plan[0] ? `${plan[0].width}×${plan[0].height}` : '';
    const summary = intent.match_resolution && res ? res : aspect || res;
    const expand = jobs.filter((j) => j.outpaint).length;
    if (expand) {
      const preferRecompose = targets.some((n, i) => {
        const job = jobs.find((j) => j.nodeId === n.id);
        if (!job?.outpaint) return false;
        return isOrientationFlip(sizes[i].width / sizes[i].height, job.frame.width / job.frame.height);
      });
      offerImageStep({ kind: 'outpaint', jobs, aspect, summary, preferRecompose });
      return preferRecompose
        ? `${expand} imagem(ns) precisam virar ${summary} (horizontal↔vertical) — na janela: recompor com modelo de edição ou recortar. Expandir bordas não serve nessa troca.`
        : `${expand} imagem(ns) precisam mudar de proporção para ${summary} — escolha na janela: recortar, expandir ou recompor com IA.`;
    }
    return runNormalizeRef.current(jobs, aspect, summary);
  }, [patchMessage, updateBoard, offerImageStep]);

  const runNormalize = useCallback(async (
    jobs: NormalizeJob[],
    aspect: BoardAspect | undefined,
    summary: string,
    mode: OutpaintMode = 'expand',
    picks: Record<string, CropPick> = {},
  ): Promise<string> => {
    setCreditModal(null);
    const recompose = mode === 'recompose';
    const crop = mode === 'crop';
    const aiModelId = recompose ? recomposeModelId : outpaintModelId;
    const credits = pickImageModel(aiModelId).creditsPerImage;
    let changed = 0;
    let expanded = 0;
    let cropped = 0;
    let lastError = '';
    let outOfCredits = false;
    const queue = [...jobs];
    const runOne = async (job: NormalizeJob) => {
      const node = nodeById(boardRef.current, job.nodeId);
      if (!isImage(node) || !node.phase.imageUrl) return;
      const source = node.phase.imageUrl;
      const title = `${node.phase.label || 'Imagem'} · ${job.frame.width}×${job.frame.height}`;
      patchPhase(node.id, {
        status: 'generating',
        error: !job.outpaint ? 'Ajustando…' : crop ? 'Recortando…' : recompose ? 'Recompondo imagem…' : 'Expandindo cenário…',
      });
      try {
        let url: string;
        let width: number;
        let height: number;
        if (job.outpaint && crop) {
          const out = await cropStillRect(source, job.frame.width / job.frame.height, picks[node.id] ?? CENTER_CROP);
          const asset = await uploadToGallery(orgId, out.file, { title: `${node.phase.label || 'Imagem'} · ${summary}` });
          ({ width, height } = out);
          url = toMediaUrl(asset.video_url);
          cropped += 1;
        } else if (job.outpaint && recompose) {
          ({ url } = await recomposeStill({
            orgId,
            sourceUrl: source,
            aspect: aspect ?? closestBoardAspect(job.frame.width, job.frame.height),
            imageModelId: recomposeModelId,
            title,
            onJob: (j) => patchPhase(node.id, { jobId: j }),
            onStatus: (label) => patchPhase(node.id, { error: label }),
          }));
          ({ width, height } = await readImageSize(url));
          expanded += 1;
        } else if (job.outpaint) {
          ({ url, width, height } = await outpaintStill({
            orgId,
            sourceUrl: source,
            frame: job.frame,
            imageModelId: outpaintModelId,
            title,
            onJob: (j) => patchPhase(node.id, { jobId: j }),
            onStatus: (label) => patchPhase(node.id, { error: label }),
          }));
          expanded += 1;
        } else {
          const out = await resizeStill(source, job.frame);
          const asset = await uploadToGallery(orgId, out.file, { title });
          ({ width, height } = out);
          url = toMediaUrl(asset.video_url);
        }
        const current = nodeById(boardRef.current, node.id);
        const version = isImage(current)
          ? withNewImageVersion(current, {
              imageUrl: url,
              createdAt: Date.now(),
              width,
              height,
              instruction: !job.outpaint
                ? `Redimensionada para ${width}×${height}`
                : crop ? `Recortada para ${summary}` : recompose ? `Recomposta em ${summary}` : `Cenário expandido para ${summary}`,
              ...(job.outpaint && !crop ? { imageModelId: aiModelId, credits } : {}),
            })
          : { width, height };
        patchImageNode(node.id, version, { ifInflight: true });
        patchPhase(node.id, { status: 'ready', imageUrl: url, error: undefined }, { ifInflight: true });
        changed += 1;
      } catch (err) {
        lastError = friendlyError(err, 'Falha ao ajustar a imagem.');
        patchPhase(node.id, { status: 'ready', imageUrl: source, error: undefined }, { ifInflight: true });
        if (isCreditError(lastError) || lastError === insufficientMsg()) outOfCredits = true;
      }
    };
    await Promise.all(Array.from({ length: Math.min(NORMALIZE_CONCURRENCY, queue.length) }, async () => {
      while (!outOfCredits && queue.length) await runOne(queue.shift()!);
    }));
    if (aspect && !lastError) updateBoard((x) => ({ ...x, aspectRatio: aspect }));
    if (expanded) await refreshOrg(false);
    const text = lastError
      ? `${changed}/${jobs.length} imagem(ns) ajustada(s). ${lastError}`
      : cropped
        ? `${changed} imagem(ns) ajustada(s) para ${summary}, ${cropped} recortada(s) sem IA.`
        : recompose && expanded
        ? `${changed} imagem(ns) ajustada(s) para ${summary}, ${expanded} recomposta(s) pela IA.`
        : `${changed} imagem(ns) ajustada(s) para ${summary}${expanded ? `, ${expanded} com cenário expandido por IA` : ''}, sem cortar a foto original.`;
    if (jobs.some((j) => j.outpaint)) {
      pushMessage({
        role: 'assistant',
        text,
        ...(expanded ? { generationCredits: expanded * credits, generationKind: 'image' as const } : {}),
        error: Boolean(lastError),
      });
    }
    return text;
  }, [outpaintModelId, recomposeModelId, orgId, patchPhase, patchImageNode, updateBoard, refreshOrg, pushMessage, friendlyError, insufficientMsg]);
  const runNormalizeRef = useRef(runNormalize);
  runNormalizeRef.current = runNormalize;

  const outpaintKey = creditModal?.kind === 'outpaint' ? creditModal.jobs.map((j) => j.nodeId).join(',') : '';
  useEffect(() => {
    if (outpaintKey) setCropPicks({});
  }, [outpaintKey]);

  const modalVideoKey = creditModal?.kind === 'video' ? creditModal.videoIds.join(',') : '';
  useEffect(() => {
    setAspectFix(null);
    if (!modalVideoKey) {
      setAspectCheck(null);
      return;
    }
    setAspectCheck({ key: modalVideoKey, items: null });
    const ratio = ratioFromLabel(boardRef.current.aspectRatio) || 16 / 9;
    const nodes = modalVideoKey
      .split(',')
      .flatMap((id) => chainForVideo(boardRef.current, id))
      .filter((n) => n.phase.imageUrl);
    let cancelled = false;
    void Promise.all(nodes.map(async (n) => {
      try {
        const src = n.phase.imageUrl as string;
        const [full, content] = await Promise.all([readImageSize(src), readContentSize(src)]);
        return { nodeId: n.id, ...content, baked: !sameSize(full, content) };
      } catch {
        return null;
      }
    })).then((sizes) => {
      if (cancelled) return;
      const items = sizes.filter((s): s is NonNullable<typeof s> =>
        Boolean(s) && (s!.baked || Math.abs(s!.width / s!.height - ratio) / ratio > ASPECT_TOLERANCE));
      setAspectCheck({ key: modalVideoKey, items });
    });
    return () => {
      cancelled = true;
    };
  }, [modalVideoKey]);

  /** Applies the aspect choice from the video modal, then renders. */
  const runVideoPrepared = useCallback(async (videoIds: string[]) => {
    const items = aspectCheck?.key === videoIds.join(',') ? aspectCheck.items || [] : [];
    const label = boardRef.current.aspectRatio;
    const ratio = ratioFromLabel(label) || 16 / 9;
    if (items.length && aspectFix === 'crop') {
      setCreditModal(null);
      try {
        await Promise.all(items.map(async (it) => {
          const node = nodeById(boardRef.current, it.nodeId);
          if (!isImage(node) || !node.phase.imageUrl) return;
          patchPhase(node.id, { status: 'generating', error: 'Recortando…' });
          try {
            const out = await cropStillToRatio(node.phase.imageUrl, ratio);
            const asset = await uploadToGallery(orgId, out.file, { title: `${node.phase.label || 'Imagem'} · ${label}` });
            patchImageNode(node.id, { width: out.width, height: out.height }, { ifInflight: true });
            patchPhase(node.id, { status: 'ready', imageUrl: toMediaUrl(asset.video_url), error: undefined }, { ifInflight: true });
          } catch (err) {
            patchPhase(node.id, { status: 'ready', error: undefined }, { ifInflight: true });
            throw err;
          }
        }));
      } catch (err) {
        pushMessage({ role: 'assistant', text: friendlyError(err, 'Falha ao recortar as fotos.'), error: true });
        return;
      }
    } else if (items.length && aspectFix === 'expand') {
      setCreditModal(null);
      try {
        await Promise.all(items.filter((it) => it.baked).map(async (it) => {
          const node = nodeById(boardRef.current, it.nodeId);
          if (!isImage(node) || !node.phase.imageUrl) return;
          const out = await cropStillToRatio(node.phase.imageUrl, null);
          const asset = await uploadToGallery(orgId, out.file, { title: `${node.phase.label || 'Imagem'} · sem faixas` });
          patchImageNode(node.id, { width: out.width, height: out.height });
          patchPhase(node.id, { imageUrl: toMediaUrl(asset.video_url) });
        }));
      } catch (err) {
        pushMessage({ role: 'assistant', text: friendlyError(err, 'Falha ao remover as faixas das fotos.'), error: true });
        return;
      }
      const before = new Map(items.map((it) => {
        const node = nodeById(boardRef.current, it.nodeId);
        return [it.nodeId, isImage(node) ? node.phase.imageUrl : undefined] as const;
      }));
      await runNormalize(
        items.map((it) => ({ nodeId: it.nodeId, frame: containCanvas(it.width, it.height, ratio), outpaint: true })),
        undefined,
        label,
      );
      const failed = items.filter((it) => {
        const node = nodeById(boardRef.current, it.nodeId);
        return isImage(node) && node.phase.imageUrl === before.get(it.nodeId);
      });
      if (failed.length) {
        pushMessage({ role: 'assistant', text: 'Nem todas as fotos foram expandidas — o vídeo não foi gerado.', error: true });
        return;
      }
    }
    await runVideos(videoIds);
  }, [aspectCheck, aspectFix, orgId, patchPhase, patchImageNode, pushMessage, friendlyError, runNormalize, runVideos]);

  // ── Intents ──────────────────────────────────────────────────

  const thumbCacheRef = useRef(new Map<string, string>());

  const thumbsFor = useCallback(async (nodes: BoardImageNode[]) => {
    const cache = thumbCacheRef.current;
    const thumbs = await Promise.all(nodes.map(async (n) => {
      const url = n.phase.imageUrl || '';
      if (!url) return null;
      if (!cache.has(url)) {
        try {
          cache.set(url, await thumbnailBase64(url, 512, 0.8));
        } catch {
          return null;
        }
      }
      const data = cache.get(url);
      return data ? { node_id: n.id, data } : null;
    }));
    return thumbs.filter((t): t is { node_id: string; data: string } => Boolean(t));
  }, []);

  /** Conversation the agent remembers: since the last board reset, errors left out. */
  const agentHistory = useCallback(() => {
    const thread = messagesRef.current;
    let lastBreak = -1;
    thread.forEach((m, i) => { if (m.contextBreak) lastBreak = i; });
    return thread
      .slice(lastBreak + 1)
      .filter((m) => m.text && !m.error && !m.contextBreak)
      .slice(-14)
      .map((m) => ({ role: m.role, text: m.text }));
  }, []);

  const reorderImages = useCallback(async (intent: { video_node_id?: string; brief: string }, msgId: string): Promise<string | undefined> => {
    if (!orgId) return undefined;
    const vid = resolveVideoId(intent.video_node_id);
    const images = vid ? storyboardImages(boardRef.current, vid) : [];
    if (images.length < 2) return 'Ligue pelo menos 2 imagens a um vídeo para reordenar.';
    patchMessage(msgId, { status: 'Olhando as imagens para reordenar…', busy: true });
    const ready = images.filter((n) => Boolean(n.phase.imageUrl) && n.phase.status === 'ready');
    const res = await reorderCanvasImages({
      organization_id: orgId,
      reorder: { brief: intent.brief, image_node_ids: images.map((n) => n.id) },
      history: agentHistory(),
      board: boardSummaryForAgent(boardRef.current, selectedIds),
      image_thumbs: await thumbsFor(ready),
    });
    patchMessage(msgId, { status: undefined, busy: false });
    const replyId = pushMessage({
      role: 'assistant',
      text: res.text,
      credits: res.credits_charged,
      inputTokens: res.input_tokens,
      outputTokens: res.output_tokens,
    });
    void refreshOrg(false);
    const notes: string[] = [];
    for (const next of res.intents) {
      if (next.type !== 'build_storyboard') continue;
      try {
        const note = await buildStoryboard(next, replyId);
        if (note) notes.push(note);
      } catch (err) {
        notes.push(friendlyError(err, 'Falha ao refazer o roteiro.'));
      }
    }
    patchMessage(replyId, { status: notes.length ? notes.join(' · ') : undefined, busy: false });
    return undefined;
  }, [orgId, resolveVideoId, patchMessage, agentHistory, selectedIds, thumbsFor, pushMessage, refreshOrg, buildStoryboard, friendlyError]);

  const openCompose = useCallback((rawIds: string[], instruction: string, msgId?: string, promptEn?: string): string => {
    const ordered = rawIds.length >= 2 ? orderComposeSources(boardRef.current, rawIds) : [];
    const ids = ordered.length >= 2 ? ordered : composeSourceIds(boardRef.current, rawIds, instruction);
    if (ids.length < 2) {
      return 'Selecione pelo menos duas imagens prontas. A nova foto usa as duas como referência e não altera as originais.';
    }
    const text = instruction.trim();
    const prompt = (promptEn || (wantsPromptUntouched(text) ? verbatimImagePrompt(text) : text)).trim();
    const placed = placeComposedImage(boardRef.current, ids, text, prompt);
    if ('error' in placed) return placed.error;
    updateBoard(() => placed.board);
    setSelectedIds([placed.nodeId]);
    window.setTimeout(() => canvasRef.current?.fitView(), 60);
    setEditText(prompt);
    rememberDebit({
      kind: 'compose-image',
      nodeId: placed.nodeId,
      sourceNodeIds: placed.sourceIds,
      instruction: text,
      promptEn: prompt,
    }, msgId);
    return 'A imagem nova está no quadro, ligada por setas tracejadas de referência. Elas não entram no vídeo. A seta contínua lilás é a padrão, a que gera o vídeo — você pode ligar qualquer uma dessas fotos nela também. Escolha o modelo e confirme o débito na janela.';
  }, [rememberDebit, updateBoard]);

  const runIntents = useCallback(async (intents: CanvasIntent[], msgId: string, userTexts: string[]) => {
    const lastUserText = userTexts[userTexts.length - 1] || '';
    const notes: string[] = [];
    const roundTexts = [...userTexts];
    for (let i = messagesRef.current.length - 1; i >= 0; i--) {
      const m = messagesRef.current[i];
      if (m.id === msgId) continue;
      if (m.role === 'assistant') break;
      if (m.role === 'user' && m.text.trim() && !roundTexts.includes(m.text.trim())) roundTexts.push(m.text.trim());
    }
    const choice = keyframeChoice(roundTexts);
    const askedMode = requestedImageMode(userTexts);
    const runnable = intents.filter((i) => {
      if (i.type !== 'set_image_mode') return true;
      if (choice === 'yes') return i.image_mode === 'keyframes';
      if (choice === 'no') return i.image_mode !== 'keyframes';
      return i.image_mode === askedMode;
    });
    const ordered = [
      ...runnable.filter((i) => i.type === 'set_image_mode'),
      ...runnable.filter((i) => i.type !== 'set_image_mode'),
    ];
    debitOwnerRef.current = msgId;
    /** A mode change right after a storyboard applies to the video that storyboard built. */
    let builtImageId: string | undefined;
    /** Video that owns the storyboard built earlier in this same turn. */
    let builtVideoId: string | undefined;
    const turnBuilds = ordered.some((i) => i.type === 'build_storyboard' || i.type === 'generate_keyframes');
    /** Video regen is queued after scene edits — skip a same-turn generate_video for that node. */
    const deferVideoRegen = new Set(
      ordered
        .filter((i) => (i.type === 'edit_images' || i.type === 'apply_background') && Boolean(
          (i as { regenerate_video_id?: string }).regenerate_video_id,
        ))
        .map((i) => (i as { regenerate_video_id: string }).regenerate_video_id),
    );
    for (const intent of ordered) {
      try {
        if (intent.type === 'revise_template') {
          const video = nodeById(boardRef.current, intent.video_node_id);
          if (!isVideo(video) || (!video.generator?.hidePrompt && !video.templateJobId)) {
            notes.push('Esse vídeo não veio de um modelo do Explorar.');
            continue;
          }
          rememberDebit({ kind: 'revise-template', videoId: video.id, change: intent.change }, msgId);
          continue;
        }
        if (intent.type === 'build_storyboard') {
          const linkedTemplate = intent.image_node_ids.map((id) => videoForImage(boardRef.current, id)).find((v) => isVideo(v) && (v.generator?.hidePrompt || v.templateJobId));
          if (linkedTemplate) {
            rememberDebit({ kind: 'revise-template', videoId: linkedTemplate.id, change: intent.brief || lastUserText }, msgId);
            continue;
          }
          const linked = intent.image_node_ids.map((id) => videoForImage(boardRef.current, id)).filter(isVideo);
          const plain = boardRef.current.nodes.filter((n): n is BoardVideoNode => isVideo(n) && !n.edit && !n.fromGallery);
          const anchor = linked[linked.length - 1] || plain.find((v) => selectedIds.includes(v.id)) || (plain.length === 1 ? plain[0] : undefined);
          const onReference = (anchor?.imageMode || 'keyframes') === 'reference';
          const build = onReference && askedMode !== 'keyframes'
            ? { ...intent, keyframes: false }
            : choice === 'yes'
              ? {
                ...intent,
                keyframes: true,
                scene_count: keyframeSceneCount(intent.scene_count, intent.image_node_ids.length, roundTexts),
              }
              : choice === 'no'
                ? { ...intent, keyframes: false }
                : intent;
          const note = await buildStoryboard(build, msgId, userTexts);
          if (note) notes.push(note);
          builtImageId = intent.image_node_ids[0];
          builtVideoId = videoForImage(boardRef.current, builtImageId)?.id || builtVideoId;
          if (build.keyframes && builtImageId) {
            const vid = videoForImage(boardRef.current, builtImageId)?.id;
            const flowIds = vid ? generationTargets(boardRef.current, vid).map((t) => t.id) : [];
            if (flowIds.length && pendingKeyframes(flowIds).length) rememberDebit({ kind: 'image', videoIds: flowIds }, msgId);
          }
        } else if (intent.type === 'generate_keyframes') {
          const b = boardRef.current;
          const ids = intent.video_node_id ? [intent.video_node_id] : b.nodes.filter(isVideo).map((v) => v.id);
          const reference = ids.length > 0 && ids.every((id) => {
            const v = nodeById(b, id);
            return isVideo(v) && v.imageMode === 'reference';
          });
          if (reference && askedMode !== 'keyframes') {
            notes.push('A entrada está em Referências. As fotos entram juntas, sem quadros novos.');
            continue;
          }
          if (!pendingKeyframes(ids).length && choice === 'yes') {
            const photos = ids.flatMap((id) => {
              const video = nodeById(boardRef.current, id);
              if (!isVideo(video)) return [];
              return chainForVideo(boardRef.current, id).filter((n) => n.phase.imageUrl);
            });
            if (photos.length) {
              const note = await buildStoryboard({
                image_node_ids: photos.map((n) => n.id),
                brief: lastUserText || 'keyframes intermediárias',
                keyframes: true,
                scene_count: keyframeSceneCount(photos.length, photos.length, roundTexts),
              }, msgId, userTexts);
              if (note) notes.push(note);
              const vid = videoForImage(boardRef.current, photos[0].id)?.id;
              if (vid) ids.splice(0, ids.length, ...generationTargets(boardRef.current, vid).map((t) => t.id));
            }
          }
          if (!pendingKeyframes(ids).length) {
            notes.push('Nenhuma keyframe pendente.');
          } else rememberDebit({ kind: 'image', videoIds: ids }, msgId);
        } else if (intent.type === 'generate_video') {
          const vid = intent.video_node_id
            ? resolveVideoId(intent.video_node_id)
            : (builtVideoId || resolveVideoId());
          if (!vid) {
            notes.push('Ainda não há fluxo com vídeo. Peça “monte um roteiro com base nessas imagens”.');
            continue;
          }
          if (deferVideoRegen.has(vid) || (intent.video_node_id && deferVideoRegen.has(intent.video_node_id))) {
            continue;
          }
          const video = nodeById(boardRef.current, vid) as BoardVideoNode;
          if (video.fromGallery) {
            notes.push('Esse vídeo veio pronto da galeria — ligue-o a outro vídeo para juntar.');
            continue;
          }
          if (!generationTargets(boardRef.current, vid).length) {
            if (upstreamVideos(boardRef.current, vid).length) {
              void settleVideo(vid);
              notes.push('Este nó junta vídeos — atualizando a junção.');
            } else {
              notes.push('Ligue imagens a este vídeo antes de gerar.');
            }
            continue;
          }
          let rootId = vid;
          for (const t of generationTargets(boardRef.current, vid).filter((f) => !f.planned)) {
            const chain = chainForVideo(boardRef.current, t.id);
            const referenceTake = (t.imageMode || 'keyframes') === 'reference'
              && chain.some((n) => n.phase.status === 'ready' && Boolean(n.phase.imageUrl));
            // One photo in Referências is already a take. Rebuilding a storyboard needs two scenes and never opens the debit window.
            if (referenceTake) continue;
            const note = await buildStoryboard(
              { image_node_ids: chain.map((n) => n.id), brief: t.brief || lastUserText, keyframes: false, scene_count: chain.length },
              msgId,
            );
            if (note) notes.push(note);
            if (t.id === rootId) rootId = finalVideoFor(boardRef.current, t.id);
          }
          const targets = generationTargets(boardRef.current, rootId);
          const ids = targets.map((t) => t.id);
          if (pendingKeyframes(ids).length && referenceImageDebit(boardRef.current, ids) && askedMode !== 'keyframes') {
            updateBoard((b) => {
              const drop = ids.flatMap((id) => chainForVideo(b, id).filter((n) => !n.phase.imageUrl).map((n) => n.id));
              return drop.length ? removeNodes(b, drop) : b;
            });
            notes.push('A entrada está em Referências. O vídeo usa as fotos da tela, sem quadros novos.');
          }
          if (pendingKeyframes(ids).length) {
            notes.push('Faltam keyframes — gere as imagens antes do vídeo.');
            rememberDebit({ kind: 'image', videoIds: ids }, msgId);
          } else if (!ids.every((id) => allKeyframesReady(flowPhases(id)))) {
            notes.push('Cada trecho precisa de pelo menos uma imagem pronta.');
          } else {
            const withModel = targets.find((t) => t.videoModelId && getVideoModelById(t.videoModelId));
            if (withModel?.videoModelId) {
              const { videoModelId, resolutionId, generateAudio } = withModel;
              setVideoPick((prev) => ({
                ...prev,
                videoModelId,
                resolutionId: resolutionId || prev.resolutionId,
                generateAudio: generateAudio ?? prev.generateAudio,
              }));
            }
            if (await syncScripts(ids)) notes.push('Roteiro atualizado para a ordem das setas e para as imagens como estão agora.');
            rememberDebit({ kind: 'video', videoIds: ids }, msgId);
          }
        } else if (intent.type === 'normalize_images') {
          notes.push(await normalizeImages(intent, msgId));
        } else if (intent.type === 'compose_image') {
          notes.push(openCompose(intent.source_node_ids || [], intent.instruction, msgId, intent.prompt_en));
        } else if (
          intent.type === 'edit_images'
          && !ordered.some((i) => i.type === 'compose_image')
          && isComposeImageRequest(intent.instruction || lastUserText)
        ) {
          notes.push(openCompose(
            [...(intent.node_ids || []), ...(intent.reference_node_ids || [])],
            intent.instruction || lastUserText,
            msgId,
          ));
        } else if (intent.type === 'edit_images' || intent.type === 'apply_background') {
          const step = intent.type === 'edit_images'
            ? narrowStandaloneImageEdit(boardRef.current, selectedIds, intent)
            : intent;
          const targetSet = new Set(
            (step.node_ids || []).filter(Boolean),
          );
          const readyPlate = (id: string) => {
            const n = nodeById(boardRef.current, id);
            return isImage(n) && Boolean(n.phase.imageUrl) && !targetSet.has(id);
          };
          const uniq = (ids: string[]) => Array.from(new Set(ids));
          const regenHint = step.regenerate_video_id
            ? resolveVideoId(step.regenerate_video_id)
            : undefined;
          const instruction = step.type === 'edit_images' ? step.instruction : 'Aplicar foto de fundo';
          const needsPlate = /(com base|basead[oa]|usando |como (nesta|nessa|esta|essa)|igual |estilo |refer[eê]ncia|@|fundo|ambiente|cen[aá]rio|desta foto|dessa foto|deste v[ií]deo|desse v[ií]deo|por est|pela foto|pelo v[ií]deo|a partir d)/i
            .test(instruction);
          const solidBgAsk = /(fundo|background|ambiente|cen[aá]rio|c[oô]modo)/i.test(instruction)
            || intent.type === 'apply_background';

          let bgId = intent.type === 'apply_background' ? intent.background_node_id : '';
          let refIds = step.type === 'edit_images'
            ? uniq((step.reference_node_ids || []).filter(readyPlate))
            : [];
          if (!bgId && !refIds.length) {
            const fromSelection = selectedIds.filter(readyPlate);
            const fromOtherVideos = selectedIds.flatMap((id) => {
              const v = nodeById(boardRef.current, id);
              if (!isVideo(v) || v.id === regenHint) return [];
              return chainForVideo(boardRef.current, v.id)
                .filter((n) => n.phase.imageUrl && !targetSet.has(n.id))
                .slice(0, 2)
                .map((n) => n.id);
            });
            refIds = uniq([...fromSelection, ...fromOtherVideos]);
            if (!refIds.length && (regenHint || needsPlate || solidBgAsk)) {
              const chain = new Set(
                (regenHint ? chainForVideo(boardRef.current, regenHint) : []).map((n) => n.id),
              );
              const outside = boardRef.current.nodes
                .filter(isImage)
                .filter((n) => n.phase.imageUrl && !chain.has(n.id) && !targetSet.has(n.id))
                .map((n) => n.id);
              if (outside.length === 1) refIds = outside;
            }
          }
          if (!bgId && refIds.length === 1 && solidBgAsk) bgId = refIds[0];

          // Prefer exact local composite over generative invent when we have a plate photo.
          if (bgId && solidBgAsk) {
            const bg = nodeById(boardRef.current, bgId);
            if (!isImage(bg) || !bg.phase.imageUrl) {
              notes.push('Não achei a foto de fundo para o composite.');
              continue;
            }
            if (!orgId) {
              notes.push('Entre na conta para aplicar o fundo.');
              continue;
            }
            const bNow = boardRef.current;
            const withChain = (id?: string) => {
              if (!id) return undefined;
              const v = nodeById(bNow, id);
              if (isVideo(v) && !v.edit && !v.fromGallery && chainForVideo(bNow, id).length) return id;
              for (const up of upstreamVideos(bNow, id)) {
                if (!up.edit && !up.fromGallery && chainForVideo(bNow, up.id).length) return up.id;
              }
              return undefined;
            };
            const intentVideo = step.type === 'apply_background' ? step.video_node_id : undefined;
            const stillsOnly = step.type === 'apply_background'
              && Boolean(step.node_ids?.length)
              && !step.video_node_id;
            const readyClip = (id?: string) => {
              if (!id) return undefined;
              const v = nodeById(bNow, id);
              if (isVideo(v) && !v.edit && !v.fromGallery && (v.videoUrl || v.clipUrl)) return id;
              return withChain(id);
            };
            // node_ids without video_node_id = stills only (e.g. complexify one logo plate for a V2V test).
            const flowVid = readyClip(intentVideo)
              || (stillsOnly ? undefined : (
                readyClip(regenHint)
                || selectedIds.map((id) => readyClip(id)).find(Boolean)
                || (step.node_ids || []).map((id) => videoForImage(bNow, id)?.id).map(readyClip).find(Boolean)
                || bNow.nodes.filter(isVideo).map((v) => readyClip(v.id)).find(Boolean)
                || bNow.nodes.filter(isVideo).map((v) => withChain(v.id)).find(Boolean)
              ));
            const flowNode = flowVid ? nodeById(bNow, flowVid) : undefined;
            const clipUrl = isVideo(flowNode) ? (flowNode.videoUrl || flowNode.clipUrl) : undefined;
            const bgUrl = bg.phase.imageUrl;

            /** Newest solid still that is not already a room composite. */
            const pickSolidSubjectUrl = async (node: BoardImageNode): Promise<string | undefined> => {
              const versions = imageVersions(node);
              const ranked = [
                ...versions
                  .filter((v) => v.imageModelId !== 'local-composite')
                  .reverse(),
                ...(versions.length ? [] : [{ imageUrl: node.phase.imageUrl as string }]),
              ];
              for (const v of ranked) {
                if (!v.imageUrl) continue;
                try {
                  const probe = await probeSolidBackground(v.imageUrl);
                  if (probe.solid) return v.imageUrl;
                } catch { /* try next */ }
              }
              const original = versions[0]?.imageUrl || node.phase.imageUrl;
              if (original) {
                try {
                  const probe = await probeSolidBackground(original);
                  if (probe.solid) return original;
                } catch { /* */ }
              }
              return undefined;
            };

            // Ready clip → operate on the video (matte or V2V). Never rewrite keyframes + regen.
            if (isVideo(flowNode) && clipUrl) {
              const chain = chainForVideo(bNow, flowVid!).filter((n) => n.id !== bg.id && n.phase.imageUrl);
              let solid = false;
              let keyHex = '0xFFFFFF';
              let variance = 20;
              // Prefer the actual clip: a prior local-composite (room plate) is complex even when
              // keyframes still show the old solid logo. Keyframes only fill in when the clip
              // probe fails to load, or to supply the key color when the clip is solid.
              let clipProbe: Awaited<ReturnType<typeof probeSolidVideoClip>> | undefined;
              try {
                clipProbe = await probeSolidVideoClip(clipUrl);
              } catch { /* try keyframes */ }
              if (clipProbe?.solid) {
                solid = true;
                variance = clipProbe.variance;
                keyHex = rgbToFfmpegHex(clipProbe.key);
              } else if (!clipProbe && chain.length) {
                // Clip unreadable — fall back to solid stills (logo v2 on white, etc.).
                for (const n of [...chain].reverse()) {
                  const src = await pickSolidSubjectUrl(n);
                  if (!src) continue;
                  try {
                    const p = await probeSolidBackground(src);
                    if (p.solid) {
                      solid = true;
                      variance = p.variance;
                      keyHex = rgbToFfmpegHex(p.key);
                      break;
                    }
                  } catch { /* skip */ }
                }
              } else if (clipProbe?.solid === false && chain.length) {
                // Clip is complex → V2V. Optionally refine key from a solid still if we ever
                // key anyway; left unused here so we do not force local matte on a room plate.
              }

              connectNodes(bg.id, flowVid!, 'ref');

              if (solid) {
                const clearV2v = (node: typeof flowNode) => {
                  if (!isVideo(node) || !node.generator?.v2vOnly) return node.generator;
                  const { v2vOnly: _drop, ...rest } = node.generator;
                  return { ...rest, referenceVideo: undefined, attachments: undefined };
                };
                patchVideo(flowVid!, {
                  status: 'joining',
                  progress: 'Aplicando fundo no vídeo…',
                  error: undefined,
                  generator: clearV2v(flowNode),
                });
                try {
                  await withFfmpeg(async () => {
                    if (!ffmpeg.loaded) await ffmpeg.load();
                    const [vidRes, bgRes] = await Promise.all([fetch(clipUrl), fetch(bgUrl)]);
                    if (!vidRes.ok || !bgRes.ok) throw new Error('Falha ao baixar o vídeo ou a foto de fundo.');
                    const out = await ffmpeg.compositeVideoOnBackground(
                      await vidRes.blob(),
                      await bgRes.blob(),
                      { keyHex, variance },
                    );
                    if (!out) throw new Error('Falha ao aplicar o fundo no vídeo.');
                    const file = new File([out], `tela_em_branco_fundo_${Date.now()}.mp4`, { type: 'video/mp4' });
                    const asset = await uploadToGallery(orgId, file, {
                      title: `${projectTitleRef.current || 'Tela em branco'} · fundo`,
                      status: 'ready',
                      generation_context: 'tela_em_branco_bg',
                    });
                    const url = toMediaUrl(asset.video_url) || asset.video_url;
                    const current = nodeById(boardRef.current, flowVid!);
                    if (!isVideo(current)) return;
                    const seconds = videoSeconds(boardRef.current, flowVid!) || current.totalDurationSeconds;
                    patchVideo(flowVid!, {
                      ...withNewVideoVersion(current, {
                        clipUrl: url,
                        createdAt: Date.now(),
                        seconds,
                        videoModelId: 'local-composite',
                      }),
                      videoUrl: url,
                      clipUrl: url,
                      status: 'ready',
                      progress: undefined,
                      error: undefined,
                      generator: clearV2v(current),
                    });
                  });
                  notes.push('Fundo aplicado no vídeo (composite local, sem crédito). A versão anterior fica nas setas ‹ ›.');
                  void settleVideo(flowVid!);
                } catch (err) {
                  patchVideo(flowVid!, {
                    status: 'ready',
                    progress: undefined,
                    error: undefined,
                  });
                  notes.push(friendlyError(err, 'Falha ao aplicar o fundo no vídeo.'));
                }
                continue;
              }

              // Complex / non-solid plate → V2V with the current clip as reference.
              const v2vModel = listVideoReferenceModels().find((m) => m.id === videoPick.videoModelId)
                || listVideoReferenceModels().find((m) => m.id === videoReferenceSiblingId(videoPick.videoModelId || ''))
                || preferredVideoReferenceModel()
                || listVideoReferenceModels()[0];
              if (!v2vModel) {
                notes.push('Não há modelo V2V disponível para trocar um fundo complexo.');
                continue;
              }
              const seconds = videoSeconds(bNow, flowVid!) || flowNode.totalDurationSeconds || videoPick.durationSeconds;
              const prompt = 'Replace the background of this video with the attached room/environment photo. Keep the main subject, motion, framing and timing unchanged. Match the reference room lighting and materials. Photoreal, seamless.';
              patchVideo(flowVid!, {
                totalDurationSeconds: seconds,
                videoModelId: v2vModel.id,
                resolutionId: flowNode.resolutionId
                  || v2vModel.resolutions.find((r) => r.id === videoPick.resolutionId)?.id
                  || v2vModel.resolutions[0]?.id,
                generator: {
                  ...(flowNode.generator || {}),
                  prompt,
                  promptSource: 'user',
                  v2vOnly: true,
                  referenceVideo: { url: clipUrl, label: flowNode.title || 'Vídeo atual' },
                  attachments: [{ url: bgUrl, label: bg.phase.label || 'Fundo' }],
                  startImage: undefined,
                  endImage: undefined,
                },
              });
              setVideoPick((cur) => ({
                ...cur,
                videoModelId: v2vModel.id,
                resolutionId: flowNode.resolutionId
                  || v2vModel.resolutions.find((r) => r.id === cur.resolutionId)?.id
                  || v2vModel.resolutions[0]?.id
                  || cur.resolutionId,
                durationSeconds: seconds,
              }));
              notes.push('O fundo do vídeo não é cor sólida — escolha o modelo V2V e confirme o débito para editar o clipe (sem reescrever as cenas).');
              rememberDebit({ kind: 'video', videoIds: [flowVid!] }, msgId);
              continue;
            }

            // No ready clip → still composite / AI on images (existing path).
            let targets: BoardImageNode[] = [];
            if (flowVid) {
              targets = chainForVideo(bNow, flowVid)
                .filter((n) => n.phase.imageUrl && n.id !== bg.id);
            }
            if (!targets.length) {
              targets = editableImages(step.node_ids || []).filter((n) => n.id !== bg.id);
            }
            if (!targets.length) {
              notes.push('Não há imagens para receber o fundo.');
              continue;
            }
            const regenId = flowVid || withChain(regenHint);
            let done = 0;
            const needAi: BoardImageNode[] = [];
            let lastError = '';
            for (const node of targets) {
              const display = node.phase.imageUrl as string;
              patchPhase(node.id, { status: 'generating', error: 'Aplicando fundo…' });
              try {
                const source = await pickSolidSubjectUrl(node);
                if (!source) {
                  needAi.push(node);
                  patchPhase(node.id, { status: 'ready', imageUrl: display, error: undefined }, { ifInflight: true });
                  continue;
                }
                const { blob, width, height } = await compositeSubjectOnBackground({
                  subjectUrl: source,
                  backgroundUrl: bgUrl,
                });
                const url = await uploadCanvasStill(orgId, blob, {
                  aspect: closestBoardAspect(width, height),
                  title: `${node.phase.label || 'Imagem'} · fundo`,
                });
                const current = nodeById(boardRef.current, node.id);
                if (!isImage(current)) continue;
                const version = withNewImageVersion(current, {
                  imageUrl: url,
                  createdAt: Date.now(),
                  width,
                  height,
                  instruction: 'Fundo composto (matte de cor sólida)',
                  imageModelId: 'local-composite',
                  credits: 0,
                });
                updateBoard((b) => ({
                  ...b,
                  nodes: b.nodes.map((n) => (n.id === node.id && isImage(n)
                    ? {
                        ...n,
                        ...version,
                        width,
                        height,
                        phase: { ...n.phase, status: 'ready' as const, imageUrl: url, error: undefined },
                      }
                    : n)),
                }));
                done += 1;
              } catch (err) {
                lastError = friendlyError(err, 'Falha ao aplicar o fundo.');
                needAi.push(node);
                patchPhase(node.id, { status: 'ready', imageUrl: display, error: undefined }, { ifInflight: true });
              }
            }
            if (regenId) connectNodes(bg.id, regenId, 'ref');
            if (needAi.length) {
              notes.push(
                done
                  ? `${done} cena(s) com composite local. ${needAi.length} sem fundo de cor sólida — escolha o modelo e confirme o débito para editar com a foto de referência.`
                  : `${needAi.length} cena(s) sem fundo de cor sólida — escolha o modelo e confirme o débito; a foto do cômodo entra como referência.`,
              );
              offerImageStep({
                kind: 'edit-image',
                nodeIds: needAi.map((n) => n.id),
                instruction: 'Substitua o fundo pela foto de referência do cômodo. Mantenha o sujeito/logo e o enquadramento.',
                promptEn: 'Replace the background of the first image with the environment from the reference room photo. Keep the main subject, logo and framing unchanged. Match the reference room lighting and materials. Photoreal, seamless.',
                referenceNodeIds: [bg.id],
                ...(regenId ? { regenerateVideoId: regenId } : {}),
              });
            } else {
              notes.push(
                lastError
                  ? `${done}/${targets.length} imagem(ns) com fundo. ${lastError}`
                  : `${done} imagem(ns) com a foto de fundo aplicada (composite local, sem crédito). A original fica nas setas ‹ ›.`,
              );
              if (done && regenId && !lastError) {
                notes.push('Confirme o débito para gerar uma nova versão do vídeo com essas cenas.');
                const ids = generationTargets(boardRef.current, regenId).map((t) => t.id);
                rememberDebit({ kind: 'video', videoIds: ids.length ? ids : [regenId] }, msgId);
              }
            }
            continue;
          }

          if (step.type !== 'edit_images') {
            notes.push('Para aplicar o fundo, selecione também a foto do ambiente.');
            continue;
          }
          const targets = editableImages(step.node_ids);
          if (!targets.length) {
            notes.push('Não há imagens prontas para editar.');
            continue;
          }
          const promptEn = refIds.length && needsPlate && /(fundo|background|ambiente|cen[aá]rio)/i.test(step.instruction)
            ? 'Replace the solid-color background of the first image with the environment from the reference image(s). Keep the main subject, logo and framing from the first image unchanged. Match the reference room exactly. Photoreal, seamless.'
            : refIds.length && !step.prompt_en
            ? `Edit the first input photo using the additional reference image(s) only for the requested change. Keep everything unrelated in the first photo unchanged. Request: ${step.instruction}`
            : step.prompt_en;
          if (regenHint && refIds.length) {
            for (const refId of refIds) connectNodes(refId, regenHint, 'ref');
          }
          offerImageStep({
            kind: 'edit-image',
            nodeIds: targets.map((n) => n.id),
            instruction: step.instruction,
            ...(promptEn ? { promptEn } : {}),
            ...(refIds.length ? { referenceNodeIds: refIds } : {}),
            ...(regenHint ? { regenerateVideoId: regenHint } : {}),
          });
          if (regenHint) {
            notes.push(
              refIds.length
                ? 'A mídia de referência entra como placa. Depois da edição, confirme o débito da nova versão do vídeo.'
                : 'Depois da edição das cenas, confirme o débito para gerar uma nova versão deste vídeo.',
            );
          }
        } else if (intent.type === 'connect') {
          connectNodes(intent.from, intent.to, intent.role === 'ref' ? 'ref' : 'flow');
        } else if (intent.type === 'disconnect') {
          updateBoard((b) => disconnect(b, intent.from, intent.to));
        } else if (intent.type === 'join_videos') {
          const b = boardRef.current;
          const sources = intent.video_node_ids.map((id) => nodeById(b, id)).filter(isVideo);
          if (sources.length < 2) continue;
          const join = {
            ...createVideoNode(
              Math.max(...sources.map((s) => s.x)) + 380,
              Math.round(sources.reduce((sum, s) => sum + s.y, 0) / sources.length),
            ),
            joinOrder: sources.map((s) => s.id),
          };
          updateBoard((x) => {
            let next: Board = { ...x, nodes: [...x.nodes, join] };
            for (const s of sources) next = connect(next, s.id, join.id);
            return next;
          });
          void settleVideo(join.id);
          notes.push(`Juntando ${sources.length} vídeos num novo nó.`);
        } else if (intent.type === 'add_audio') {
          const vid = resolveVideoId(intent.video_node_id);
          const root = vid ? finalVideoFor(boardRef.current, vid) : undefined;
          const edit = root ? ensureEditNode(root) : undefined;
          if (!edit) {
            notes.push('Ainda não há vídeo para receber o som. Gere o vídeo primeiro.');
            continue;
          }
          const b = boardRef.current;
          const stacked = audioInputs(b, edit.id).length;
          const node: BoardAudioNode = {
            ...createAudioNode(
              edit.x - AUDIO_NODE_W - 80,
              edit.y + VIDEO_NODE_H + 48 + stacked * (AUDIO_NODE_H + 24),
              intent.audio_kind,
              intent.text,
            ),
            ...(intent.title ? { title: intent.title } : {}),
            ...(intent.start_offset != null ? { startOffset: Math.max(0, intent.start_offset) } : {}),
            ...(intent.loop != null ? { loop: intent.loop } : {}),
            ...(intent.volume != null ? { volume: Math.max(0, Math.min(1.5, intent.volume)) } : {}),
          };
          const over = intent.over_voice_id ? nodeById(b, intent.over_voice_id) : undefined;
          if (intent.audio_kind === 'music' && isAudio(over) && over.audioKind === 'voice' && over.audioUrl) {
            node.sourceAudioId = over.id;
            const instrumental = instrumentalPick();
            if (instrumental) setAudioPicks((p) => ({ ...p, music: instrumental }));
          }
          updateBoard((x) => connect({ ...x, nodes: [...x.nodes, node] }, node.id, edit.id));
          if (!edit.videoUrl) void settleVideo(edit.id);
          openAudioModal(node.id);
          notes.push(`“${node.title}” ligado ao vídeo editado — escolha o modelo e confirme o débito.`);
        } else if (intent.type === 'regenerate_audio') {
          const node = nodeById(boardRef.current, intent.audio_node_id);
          if (!isAudio(node)) continue;
          if (intent.text?.trim()) patchAudio(node.id, { text: intent.text.trim() });
          openAudioModal(node.id);
          notes.push('Escolha o modelo e confirme o débito para gerar uma nova versão.');
        } else if (intent.type === 'update_audio') {
          const node = nodeById(boardRef.current, intent.audio_node_id);
          if (!isAudio(node)) continue;
          updateAudioTrack(node.id, {
            volume: intent.volume,
            startOffset: intent.start_offset,
            loop: intent.loop,
            muted: intent.muted,
            speed: intent.speed,
          });
          notes.push(`“${node.title || 'Som'}” ajustado — remixando o vídeo editado (sem custo).`);
        } else if (intent.type === 'edit_video') {
          const vid = resolveVideoId(intent.video_node_id);
          const edit = vid ? ensureEditNode(finalVideoFor(boardRef.current, vid)) : undefined;
          if (!edit) {
            notes.push('Ainda não há vídeo para editar.');
            continue;
          }
          const current = nodeById(boardRef.current, edit.id);
          const prev: BoardVideoEdit = isVideo(current) && current.edit ? current.edit : { sourceVolume: 1 };
          const next: BoardVideoEdit = {
            ...prev,
            ...(intent.source_volume != null ? { sourceVolume: Math.max(0, Math.min(1.5, intent.source_volume)) } : {}),
            ...(intent.captions != null ? { captions: intent.captions } : {}),
            ...(intent.caption_position ? { captionPosition: intent.caption_position } : {}),
            ...(intent.fade != null ? { fade: intent.fade } : {}),
            ...(intent.grade != null ? { grade: intent.grade } : {}),
            ...(intent.grayscale != null ? { grayscale: intent.grayscale } : {}),
          };
          if (next.grayscale) next.grade = false;
          else if (next.grade) next.grayscale = false;
          patchVideo(edit.id, { edit: next });
          if (next.captions && !audioInputs(boardRef.current, edit.id).some((a) => a.audioKind === 'voice' && a.audioUrl)) {
            notes.push('As legendas usam o texto da locução — peça uma locução para elas aparecerem.');
          }
          void settleVideo(edit.id);
          notes.push(
            next.grayscale
              ? 'Aplicando preto e branco no vídeo editado (sem custo).'
              : next.grade
                ? 'Aplicando cor cinematográfica no vídeo editado (sem custo).'
                : 'Remixando o vídeo editado (sem custo).',
          );
        } else if (intent.type === 'join_transition') {
          const vid = resolveVideoId(intent.video_node_id);
          const join = vid ? joinNodeFor(boardRef.current, vid) : undefined;
          if (!join) {
            notes.push('Não há junção de vídeos na tela para aplicar a transição.');
            continue;
          }
          const joinTransition: BoardJoinTransition | undefined = intent.transition === 'none'
            ? undefined
            : { kind: intent.transition, seconds: Math.max(0.2, Math.min(1.5, intent.seconds ?? 0.4)) };
          patchVideo(join.id, { joinTransition });
          void settleVideo(join.id);
          notes.push(joinTransition
            ? `Refazendo “${join.title || 'Vídeo final'}” com ${joinTransition.kind === 'crossfade' ? 'transição suave' : 'fade'} de ${joinTransition.seconds}s entre os vídeos (sem custo).`
            : `Refazendo “${join.title || 'Vídeo final'}” com cortes secos (sem custo).`);
        } else if (intent.type === 'set_aspect_ratio') {
          const aspect = intent.aspect_ratio === 'original' ? photoAspect() : intent.aspect_ratio;
          if (!aspect) {
            notes.push('Não achei fotos no quadro para copiar a proporção.');
            continue;
          }
          updateBoard((b) => ({ ...b, aspectRatio: aspect, aspectChosen: true }));
          notes.push(`Próximas gerações em ${aspect}.`);
        } else if (intent.type === 'set_image_mode') {
          const built = !intent.video_node_id && builtImageId ? videoForImage(boardRef.current, builtImageId) : undefined;
          const vid = resolveVideoId(intent.video_node_id || built?.id);
          const targets = vid ? generationTargets(boardRef.current, vid) : [];
          if (!targets.length) {
            notes.push('Não há vídeo com imagens ligadas para mudar o modo.');
            continue;
          }
          const staying = intent.image_mode === 'keyframes'
            ? targets.filter((t) => keepsReferenceImages(t.imageMode, askedMode))
            : [];
          const apply = targets.filter((t) => !staying.some((l) => l.id === t.id));
          if (!apply.length) {
            if (staying.length && !turnBuilds) {
              notes.push('A entrada continua em Referências. As fotos entram juntas, sem quadros novos.');
            }
            continue;
          }
          for (const t of apply) setImageMode(t.id, intent.image_mode);
          if (staying.length && !turnBuilds) {
            notes.push('A entrada continua em Referências. As fotos entram juntas, sem quadros novos.');
          }
          notes.push(intent.image_mode === 'keyframes'
            ? 'Modo contínuo: o vídeo passa pelas imagens na ordem das setas. Dá para criar keyframes entre elas.'
            : 'Modo referências: as imagens guiam o visual juntas, sem posição no tempo.');
        } else if (intent.type === 'delete_nodes') {
          removeBoardNodes(intent.node_ids);
        } else if (intent.type === 'reorder_images') {
          const note = await reorderImages(intent, msgId);
          if (note) notes.push(note);
        } else if (intent.type === 'layout_board') {
          notes.push(layoutBoardNow()
            ? 'Fluxo organizado no quadro.'
            : 'O fluxo já estava alinhado.');
        }
      } catch (err) {
        notes.push(friendlyError(err, 'Falha ao executar o pedido.'));
      }
    }
    patchMessage(msgId, {
      status: notes.length ? notes.join(' · ') : undefined,
      busy: false,
    });
  }, [orgId, buildStoryboard, pendingKeyframes, resolveVideoId, flowPhases, syncScripts, normalizeImages, editableImages, offerImageStep, connectNodes, updateBoard, settleVideo, friendlyError, patchMessage, patchPhase, ensureEditNode, openAudioModal, patchAudio, patchVideo, updateAudioTrack, photoAspect, setImageMode, removeBoardNodes, reorderImages, layoutBoardNow, rememberDebit, selectedIds, withFfmpeg, ffmpeg, videoPick, openCompose]);

  /** A "não" recommendation never opens the form: the photos become the video. */
  const declinedKeyframeFlight = useRef(new Set<string>());
  const continueDeclinedKeyframes = useCallback(async (msg: CanvasChatMessage, thread: CanvasChatMessage[]) => {
    if (declinedKeyframeFlight.current.has(msg.id)) return;
    if (!msg.ask || msg.askResolved || !keyframeAdviceDeclines(msg.ask, msg.askAnswers)) return;
    if (!boardHasSource(boardRef.current)) return;
    declinedKeyframeFlight.current.add(msg.id);
    try {
      const photoIds = photosForNewVideo(boardRef.current, selectedIds);
      const brief = briefBeforeAsk(thread, msg.id);
      const existingId = plannedVideoForPhotos(boardRef.current, photoIds);
      patchMessage(msg.id, {
        ask: undefined,
        askResolved: true,
        text: DIRECT_VIDEO_NOTE,
        suggestions: undefined,
        busy: true,
        status: existingId ? undefined : 'Montando roteiro…',
      });
      if (photoIds.length < 2) {
        patchMessage(msg.id, {
          busy: false,
          status: undefined,
          error: true,
          text: 'Para criar o vídeo sem keyframes preciso de pelo menos duas imagens na tela.',
        });
        return;
      }
      if (existingId) {
        await runIntents([{ type: 'generate_video', video_node_id: existingId }], msg.id, [brief]);
      } else {
        await runIntents([
          {
            type: 'build_storyboard',
            image_node_ids: photoIds,
            brief,
            keyframes: false,
            scene_count: photoIds.length,
          },
        ], msg.id, [brief]);
        const videoId = photoIds
          .map((id) => videoForImage(boardRef.current, id)?.id)
          .find((id): id is string => Boolean(id));
        if (videoId) {
          await runIntents([{ type: 'generate_video', video_node_id: videoId }], msg.id, [brief]);
        }
      }
      // The shared copy otherwise keeps the question, and the next open shows it again.
      void syncOpsRef.current.push(true).catch(() => undefined);
    } finally {
      declinedKeyframeFlight.current.delete(msg.id);
    }
  }, [patchMessage, runIntents, selectedIds]);

  useEffect(() => {
    const pending = messages.find((m) => (
      m.role === 'assistant'
      && m.ask
      && !m.askResolved
      && keyframeAdviceDeclines(m.ask, m.askAnswers)
      && !declinedKeyframeFlight.current.has(m.id)
    ));
    if (!pending) return;
    void continueDeclinedKeyframes(pending, messages);
  }, [messages, continueDeclinedKeyframes]);

  // ── Context menu ─────────────────────────────────────────────

  const addNode = useCallback((node: BoardVideoNode | BoardAudioNode) => {
    updateBoard((b) => ({ ...b, nodes: [...b.nodes, node] }));
    setSelectedIds([node.id]);
  }, [updateBoard]);

  const [focusCommentId, setFocusCommentId] = useState<string | null>(null);
  const updateComment = useCallback((id: string, patch: { text?: string; w?: number; h?: number }) => {
    updateBoard((b) => ({
      ...b,
      nodes: b.nodes.map((n) => (n.id === id && isComment(n) ? { ...n, ...patch } : n)),
    }), patch.text != null ? { coalesce: `comment-text:${id}` } : undefined);
  }, [updateBoard]);

  const runCanvasAction = useCallback((action: CanvasAction, at: { x: number; y: number }) => {
    const x = Math.round(at.x);
    const y = Math.round(at.y);
    if (action === 'gallery') openGallery('both');
    else if (action === 'gen-image') {
      genImagePromptRef.current = '';
      setGenImagePrompt('');
      rememberDebit({ kind: 'gen-image', at: { x, y } });
    } else if (action === 'gen-video') addNode(createVideoNode(x, y));
    else if (action === 'edit-video') addNode(createEditNode(x, y));
    else if (action === 'comment') {
      const node = createCommentNode(x, y);
      updateBoard((b) => ({ ...b, nodes: [...b.nodes, node] }));
      setSelectedIds([node.id]);
      setFocusCommentId(node.id);
    }
    else if (action === 'voice' || action === 'music' || action === 'sfx') {
      const node = createAudioNode(x, y, action, '');
      addNode(node);
      openAudioModal(node.id);
    } else if (action === 'assistant') promptRef.current?.focus();
  }, [openGallery, addNode, openAudioModal, rememberDebit, updateBoard]);

  const runGenImage = useCallback(async (prompt: string, at: { x: number; y: number }) => {
    setCreditModal(null);
    const text = prompt.trim();
    if (!orgId || !text) return;
    const node = createImageNode('', at.x, at.y, text.slice(0, 40));
    node.phase = { ...node.phase, status: 'generating', imageUrl: undefined, error: 'Gerando imagem…', still_prompt_en: text };
    updateBoard((b) => ({ ...b, imageModelId, nodes: [...b.nodes, node] }));
    setSelectedIds([node.id]);
    try {
      const { url, jobId } = await generateImageFromPrompt({
        orgId,
        prompt: text,
        imageModelId,
        aspect: boardRef.current.aspectRatio,
        title: text.slice(0, 60),
        onJob: (j) => patchPhase(node.id, { jobId: j }),
        onStatus: (label) => patchPhase(node.id, { error: label }),
      });
      const size = await readImageSize(url).catch(() => null);
      if (size) patchImageNode(node.id, { width: size.width, height: size.height }, { ifInflight: true });
      patchPhase(node.id, { status: 'ready', imageUrl: url, jobId, error: undefined }, { ifInflight: true });
      pushMessage({ role: 'assistant', text: 'Imagem gerada e colocada no quadro.', generationCredits: imageCredits, generationKind: 'image' });
    } catch (err) {
      const msg = friendlyError(err, 'Falha ao gerar imagem.');
      patchPhase(node.id, { status: 'failed', error: msg }, { ifInflight: true });
      setError(msg);
    }
    void refreshOrg(false);
  }, [orgId, imageModelId, imageCredits, updateBoard, patchPhase, patchImageNode, pushMessage, friendlyError, refreshOrg]);

  const runEditImages = useCallback(async (
    nodeIds: string[],
    prompt: string,
    instruction: string,
    opts?: { referenceNodeIds?: string[]; regenerateVideoId?: string },
  ) => {
    setCreditModal(null);
    const targets = editableImages(nodeIds);
    if (!orgId || !prompt.trim() || !targets.length) return;
    const referenceUrls = (opts?.referenceNodeIds || [])
      .map((id) => {
        const n = nodeById(boardRef.current, id);
        return isImage(n) ? n.phase.imageUrl : undefined;
      })
      .filter((u): u is string => Boolean(u));
    const credits = pickImageModel(editModelId).creditsPerImage;
    let edited = 0;
    let lastError = '';
    let outOfCredits = false;
    const reshaped: string[] = [];
    const queue = [...targets];
    const runOne = async (node: BoardImageNode) => {
      const source = node.phase.imageUrl as string;
      patchPhase(node.id, { status: 'generating', error: 'Editando…' });
      try {
        const before = node.width && node.height
          ? { width: node.width, height: node.height }
          : await readImageSize(source).catch(() => null);
        const { url, jobId } = await editStill({
          orgId,
          sourceUrl: source,
          instruction: prompt,
          imageModelId: editModelId,
          aspect: askedAspect(instruction) || (before ? closestBoardAspect(before.width, before.height) : boardRef.current.aspectRatio),
          title: `${node.phase.label || 'Imagem'} · editada`,
          ...(referenceUrls.length ? { referenceUrls } : {}),
          onJob: (j) => patchPhase(node.id, { jobId: j }),
          onStatus: (label) => patchPhase(node.id, { error: label }),
        });
        const size = await readImageSize(url).catch(() => null);
        if (before && size && Math.abs(Math.log((size.width / size.height) / (before.width / before.height))) > EDIT_ASPECT_TOLERANCE) {
          reshaped.push(node.phase.label || 'Imagem');
        }
        const current = nodeById(boardRef.current, node.id);
        if (!isImage(current)) return;
        const version = withNewImageVersion(current, {
          imageUrl: url,
          createdAt: Date.now(),
          ...(size ? { width: size.width, height: size.height } : {}),
          instruction,
          imageModelId: editModelId,
          credits,
        });
        updateBoard((b) => {
          let hit = false;
          const nodes = b.nodes.map((n) => {
            if (n.id !== node.id || !isImage(n) || !nodeInflight(n)) return n;
            hit = true;
            return { ...n, ...version, phase: { ...n.phase, status: 'ready' as const, imageUrl: url, jobId, error: undefined } };
          });
          return hit ? { ...b, nodes } : b;
        });
        edited += 1;
      } catch (err) {
        lastError = friendlyError(err, 'Falha ao editar a imagem.');
        patchPhase(node.id, { status: 'ready', imageUrl: source, error: undefined }, { ifInflight: true });
        if (isCreditError(lastError) || lastError === insufficientMsg()) outOfCredits = true;
      }
    };
    await Promise.all(Array.from({ length: Math.min(NORMALIZE_CONCURRENCY, queue.length) }, async () => {
      while (!outOfCredits && queue.length) await runOne(queue.shift()!);
    }));
    void refreshOrg(false);
    const parts = [
      lastError
        ? `${edited}/${targets.length} imagem(ns) editada(s). ${lastError}`
        : `${edited} imagem(ns) editada(s). A original continua guardada: use as setas ‹ › no nó para voltar a ela.`,
    ];
    if (reshaped.length) {
      parts.push(`O modelo devolveu ${reshaped.join(', ')} em outra proporção; se não servir, volte à versão anterior e tente outro modelo (Seedream, Kling O3 ou Nano Banana).`);
    }
    const regenVid = opts?.regenerateVideoId ? resolveVideoId(opts.regenerateVideoId) : undefined;
    if (edited && regenVid && !lastError) {
      parts.push('Confirme o débito para gerar uma nova versão do vídeo com essas cenas.');
    } else if (edited && !regenVid) {
      const feedsVideo = targets.some((n) => videoForImage(boardRef.current, n.id));
      if (feedsVideo) parts.push('Para atualizar o vídeo, peça “Gere o vídeo” (nova versão no mesmo nó).');
    }
    pushMessage({
      role: 'assistant',
      text: parts.join(' '),
      ...(edited ? { generationCredits: edited * credits, generationKind: 'image' as const } : {}),
      ...(edited && !regenVid && targets.some((n) => videoForImage(boardRef.current, n.id))
        ? { suggestions: [GENERATE_VIDEO_SUGGESTION] }
        : {}),
      error: Boolean(lastError),
    });
    if (edited && regenVid && !lastError) {
      const ids = generationTargets(boardRef.current, regenVid).map((t) => t.id);
      rememberDebitRef.current({ kind: 'video', videoIds: ids.length ? ids : [regenVid] });
    }
  }, [orgId, editModelId, editableImages, patchPhase, updateBoard, pushMessage, friendlyError, insufficientMsg, refreshOrg, resolveVideoId]);

  const runComposeImage = useCallback(async (
    nodeId: string,
    sourceIds: string[],
    instruction: string,
    prompt: string,
  ) => {
    setCreditModal(null);
    const sources = sourceIds
      .map((id) => nodeById(boardRef.current, id))
      .filter(isImage)
      .filter((n) => Boolean(n.phase.imageUrl));
    const target = nodeById(boardRef.current, nodeId);
    const sent = prompt.trim();
    if (!orgId || !instruction.trim() || !sent || sources.length < 2 || !isImage(target)) {
      setError('Não achei a imagem nova ou as fotos de referência.');
      return;
    }
    const base = sources[0];
    const referenceUrls = sources.slice(1).map((n) => n.phase.imageUrl as string);
    const before = base.width && base.height
      ? { width: base.width, height: base.height }
      : await readImageSize(base.phase.imageUrl as string).catch(() => null);
    const aspect = askedAspect(instruction)
      || askedAspect(sent)
      || (before ? closestBoardAspect(before.width, before.height) : boardRef.current.aspectRatio);
    const credits = pickImageModel(editModelId).creditsPerImage;
    patchPhase(nodeId, { status: 'generating', error: 'Gerando imagem…', still_prompt_en: sent });
    try {
      const { url, jobId } = await editStill({
        orgId,
        sourceUrl: base.phase.imageUrl as string,
        instruction: sent,
        verbatimPrompt: true,
        imageModelId: editModelId,
        aspect,
        title: instruction.slice(0, 60),
        referenceUrls,
        onJob: (j) => patchPhase(nodeId, { jobId: j }),
        onStatus: (label) => patchPhase(nodeId, { error: label }),
      });
      const size = await readImageSize(url).catch(() => null);
      const current = nodeById(boardRef.current, nodeId);
      if (!isImage(current)) return;
      const version = withNewImageVersion(current, {
        imageUrl: url,
        createdAt: Date.now(),
        ...(size ? { width: size.width, height: size.height } : {}),
        instruction,
        imageModelId: editModelId,
        credits,
      });
      updateBoard((b) => ({
        ...b,
        nodes: b.nodes.map((n) => (n.id === nodeId && isImage(n) && nodeInflight(n)
          ? { ...n, ...version, phase: { ...n.phase, status: 'ready' as const, imageUrl: url, jobId, error: undefined } }
          : n)),
      }));
      pushMessage({
        role: 'assistant',
        text: 'Imagem gerada. As setas tracejadas continuam só como referência e não entram no vídeo. Para usar uma dessas fotos no vídeo, ligue com a seta contínua.',
        generationCredits: credits,
        generationKind: 'image',
      });
    } catch (err) {
      const msg = friendlyError(err, 'Falha ao gerar a imagem.');
      patchPhase(nodeId, { status: 'failed', error: msg }, { ifInflight: true });
      setError(msg);
      pushMessage({ role: 'assistant', text: msg, error: true });
    }
    void refreshOrg(false);
  }, [orgId, editModelId, patchPhase, updateBoard, pushMessage, friendlyError, refreshOrg]);

  const selectImageVersion = useCallback((imageId: string, versionId: string) => {
    const node = nodeById(boardRef.current, imageId);
    if (!isImage(node) || node.phase.status === 'generating') return;
    const version = imageVersions(node).find((v) => v.id === versionId);
    if (!version || version.imageUrl === node.phase.imageUrl) return;
    updateBoard((b) => ({
      ...b,
      nodes: b.nodes.map((n) => (n.id === imageId && isImage(n)
        ? {
            ...n,
            ...(version.width && version.height ? { width: version.width, height: version.height } : {}),
            phase: { ...n.phase, imageUrl: version.imageUrl, error: undefined },
          }
        : n)),
    }));
  }, [updateBoard]);

  // ── Agent turn ───────────────────────────────────────────────

  /** Previews of photos that still need a first script, so the agent can see them and choose the order. */
  const storyboardThumbs = useCallback(async () => {
    const b = boardRef.current;
    const ready = b.nodes.filter(isImage).filter((n) => Boolean(n.phase.imageUrl) && n.phase.status === 'ready');
    const byPos = (p: BoardImageNode, q: BoardImageNode) => p.x - q.x || p.y - q.y;
    const selected = ready.filter((n) => selectedIds.includes(n.id)).sort(byPos);
    const loose = looseImages(b).filter((n) => ready.includes(n));
    const pool = selected.length >= 2 ? selected : loose.length ? loose : ready.sort(byPos);
    const images = pool.filter((n) => !videoForImage(b, n.id)?.planned).slice(0, 10);
    if (images.length < 2) return undefined;
    const ok = await thumbsFor(images);
    return ok.length ? ok : undefined;
  }, [selectedIds, thumbsFor]);

  /** What the agent receives as `selected_ids`, shown on the prompt. */
  const promptReferences = useMemo<PromptReference[]>(() => (
    selectedIds
      .map((id) => nodeById(board, id))
      .filter((n): n is BoardImageNode | BoardVideoNode | BoardAudioNode => isImage(n) || isVideo(n) || isAudio(n))
      .map((n) => (isImage(n)
        ? { id: n.id, kind: 'image' as const, label: n.phase.label || 'Imagem', url: n.phase.imageUrl }
        : isVideo(n)
        ? { id: n.id, kind: 'video' as const, label: n.title || 'Vídeo' }
        : { id: n.id, kind: 'audio' as const, label: n.title || 'Som' }))
  ), [board, selectedIds]);

  /** Opens the credit window for videos that already have a photo and a prompt. */
  const openQuotedVideos = useCallback((ids: string[], msgId: string) => {
    const videos = ids.map((id) => nodeById(boardRef.current, id)).filter(isVideo);
    const withModel = videos.find((t) => t.videoModelId && getVideoModelById(t.videoModelId));
    if (withModel?.videoModelId) {
      const { videoModelId, resolutionId, generateAudio } = withModel;
      setVideoPick((prev) => ({
        ...prev,
        videoModelId,
        resolutionId: resolutionId || prev.resolutionId,
        generateAudio: generateAudio ?? prev.generateAudio,
      }));
    }
    rememberDebit({ kind: 'video', videoIds: ids }, msgId);
  }, [rememberDebit]);

  const sendTurn = useCallback(async (
    input: { text?: string; audio?: { base64: string; mimeType: string; seconds: number }; answers?: Record<string, string> },
    /** Nodes the turn is about (the generator card), instead of the canvas selection. */
    focusIds?: string[],
  ) => {
    if (!orgId || sending) return;
    if (input.text && !input.audio && !input.answers && isLayoutRequest(input.text)) {
      const refs = promptReferences.map((r) => r.label);
      pushMessage({ role: 'user', text: input.text, ...(refs.length ? { refs } : {}) });
      const moved = layoutBoardNow();
      pushMessage({
        role: 'assistant',
        text: moved
          ? 'Organizei o fluxo no quadro: imagens em sequência, vídeo à direita, junção e edição em seguida, som embaixo.'
          : 'O fluxo já estava alinhado. Arraste os nós e peça de novo se quiser outra organização.',
      });
      return;
    }
    if (input.text && !input.audio && !input.answers && isVideoPerImageRequest(input.text)) {
      const refs = promptReferences.map((r) => r.label);
      pushMessage({ role: 'user', text: input.text, ...(refs.length ? { refs } : {}) });
      const result = applyVideoPerImage(boardRef.current, { text: input.text, selectedIds: focusIds || selectedIds });
      if (!result || (!result.created && !result.updated)) {
        pushMessage({ role: 'assistant', text: 'Selecione as imagens, ou solte fotos no quadro, para eu criar um vídeo em cada uma.' });
        return;
      }
      updateBoard(() => result.board);
      window.setTimeout(() => canvasRef.current?.fitView(), 60);
      const replyId = pushMessage({ role: 'assistant', text: perImageReply(result.created, result.updated) });
      if (result.videoIds.length) openQuotedVideos(result.videoIds, replyId);
      return;
    }
    if (input.text && !input.audio && !input.answers && isComposeImageRequest(input.text)) {
      const refs = promptReferences.map((r) => r.label);
      pushMessage({ role: 'user', text: input.text, ...(refs.length ? { refs } : {}) });
      const replyId = pushMessage({ role: 'assistant', text: 'Preparando a imagem…', busy: true });
      if (wantsPromptUntouched(input.text)) {
        const note = openCompose(focusIds || selectedIds, input.text, replyId, verbatimImagePrompt(input.text));
        patchMessage(replyId, { text: note, busy: false });
        return;
      }
      setSending(true);
      try {
        const sourceIds = composeSourceIds(boardRef.current, focusIds || selectedIds, input.text);
        const sources = sourceIds
          .map((id) => nodeById(boardRef.current, id))
          .filter((n): n is BoardImageNode => Boolean(n && isImage(n) && n.phase.imageUrl));
        const thumbs = await thumbsFor(sources);
        const written = await writeComposeImagePrompt({
          organization_id: orgId,
          message: input.text,
          image_thumbs: thumbs,
        });
        const note = openCompose(focusIds || selectedIds, input.text, replyId, written.prompt_en);
        patchMessage(replyId, {
          text: note,
          busy: false,
          ...(written.credits_charged ? { credits: written.credits_charged } : {}),
          ...(written.input_tokens ? { inputTokens: written.input_tokens } : {}),
          ...(written.output_tokens ? { outputTokens: written.output_tokens } : {}),
        });
        void refreshOrg(false);
      } catch (err) {
        const note = openCompose(focusIds || selectedIds, input.text, replyId);
        patchMessage(replyId, {
          text: note,
          busy: false,
          status: friendlyError(err, 'O agente não respondeu. A caixa ficou com o texto do pedido.'),
        });
      } finally {
        setSending(false);
      }
      return;
    }
    if (input.text && !input.audio && !input.answers && isGenerateVideoRequest(input.text)) {
      const ids = quoteVideoIds(boardRef.current, focusIds || selectedIds);
      if (ids.length) {
        const refs = promptReferences.map((r) => r.label);
        pushMessage({ role: 'user', text: input.text, ...(refs.length ? { refs } : {}) });
        const replyId = pushMessage({
          role: 'assistant',
          text: ids.length === 1
            ? 'Confirme o débito na janela para gerar o vídeo.'
            : `Confirme o débito na janela para gerar os ${ids.length} vídeos.`,
        });
        openQuotedVideos(ids, replyId);
        return;
      }
    }
    // Captured before the new bubble is pushed, so the current turn goes out as `message`, not twice.
    const history = agentHistory();
    const liveIds = new Set(boardRef.current.nodes.map((n) => n.id));
    const removed = removedRef.current.filter((r) => !liveIds.has(r.id));
    removedRef.current = [];
    const refs = promptReferences.map((r) => r.label);
    const userMsgId = (input.text || input.audio)
      ? pushMessage({
        role: 'user',
        text: input.text || '',
        audioSeconds: input.audio?.seconds,
        ...(input.answers ? { formAnswer: true } : {}),
        ...(!input.answers && refs.length ? { refs } : {}),
      })
      : null;
    setSending(true);
    try {
      // Captions exist so a later storyboard can order scenes without re-seeing pixels.
      // Skipping them on edit / single-image turns avoids an extra vision round before the reply.
      const focus = focusIds || selectedIds;
      const uncaptioned = boardRef.current.nodes
        .filter(isImage)
        .filter((n) => Boolean(n.phase.imageUrl) && n.phase.status === 'ready' && !imageCaption(n))
        .slice(0, 12);
      const sentUrls = new Map(uncaptioned.map((n) => [n.id, n.phase.imageUrl || '']));
      const editingStills = looksLikeImageEdit(input.text || '')
        && focus.some((id) => isImage(nodeById(boardRef.current, id)));
      const needCaptions = uncaptioned.length >= 2 && !editingStills;
      const [imageThumbs, captionThumbs] = await Promise.all([
        storyboardThumbs(),
        needCaptions ? thumbsFor(uncaptioned) : Promise.resolve([] as Array<{ node_id: string; data: string }>),
      ]);
      const res = await sendCanvasAgentTurn({
        organization_id: orgId,
        message: input.text,
        audio: input.audio ? { mime_type: input.audio.mimeType, data: input.audio.base64 } : undefined,
        history,
        board: removed.length
          ? { ...boardSummaryForAgent(boardRef.current, focusIds || selectedIds), removed_since_last_turn: removed }
          : boardSummaryForAgent(boardRef.current, focusIds || selectedIds),
        answers: input.answers,
        image_thumbs: imageThumbs,
        caption_thumbs: captionThumbs.length ? captionThumbs : undefined,
      });
      if (userMsgId && res.transcript) patchMessage(userMsgId, { text: res.transcript });
      if (res.captions?.length) {
        const captions = res.captions;
        updateBoard((b) => ({
          ...b,
          nodes: b.nodes.map((n) => {
            const c = captions.find((x) => x.node_id === n.id);
            const url = sentUrls.get(n.id);
            return c && url && isImage(n) && n.phase.imageUrl === url ? { ...n, caption: c.text, captionUrl: url } : n;
          }),
        }));
      }
      const plainVideos = boardRef.current.nodes.filter((n): n is BoardVideoNode => isVideo(n) && !n.edit && !n.fromGallery);
      const selectedVideo = plainVideos.find((v) => focus.includes(v.id));
      const linkedVideo = focus.map((id) => videoForImage(boardRef.current, id)).filter(isVideo).pop();
      const anchorVideo = selectedVideo || linkedVideo || (plainVideos.length === 1 ? plainVideos[0] : undefined);
      const askedNow = requestedImageMode(input.answers ? [] : [input.text || '']);
      const onReferenceCard = (anchorVideo?.imageMode || 'keyframes') === 'reference' && askedNow !== 'keyframes';
      const droppedKeyframeAsk = Boolean(onReferenceCard && res.ask && !dropKeyframeAsk(res.ask));
      const ask = onReferenceCard ? dropKeyframeAsk(res.ask) : res.ask;
      const readyImages = boardRef.current.nodes.filter(isImage).filter((n) => n.phase.imageUrl && n.phase.status === 'ready');
      const pickedImages = readyImages.filter((n) => (focusIds || selectedIds).includes(n.id));
      const loose = looseImages(boardRef.current).filter((n) => readyImages.includes(n));
      const flowPhotos = readingOrder(pickedImages.length ? pickedImages : loose.length ? loose : readyImages);
      const pickedOfferPhotos = (res.exploreOffer?.imageNodeIds || [])
        .map((id) => readyImages.find((n) => n.id === id) || flowPhotos.find((n) => n.id === id))
        .filter((n): n is typeof flowPhotos[number] => Boolean(n?.phase.imageUrl));
      const explorePhotos = pickedOfferPhotos.length ? pickedOfferPhotos : flowPhotos;
      const exploreOffer = res.exploreOffer
        ? {
          ...res.exploreOffer,
          imageUrls: explorePhotos.map((n) => n.phase.imageUrl!).filter(Boolean),
        }
        : undefined;
      const reply = onReferenceCard ? withoutFrameRecommendation(res.text) : res.text;
      const photoIds = referencePhotoIds(boardRef.current, anchorVideo?.id, focus);
      const revisesTemplate = res.intents.some((intent) => intent.type === 'revise_template');
      const buildReferenceScript = !exploreOffer && !revisesTemplate && shouldBuildReferenceScript({
        onReference: onReferenceCard,
        droppedKeyframeAsk,
        reply,
        hasStoryboard: res.intents.some((i) => i.type === 'build_storyboard'),
      }) && photoIds.length >= 2;
      const intents: CanvasIntent[] = exploreOffer || revisesTemplate
        ? res.intents.filter((intent) => !['build_storyboard', 'generate_video', 'generate_keyframes', 'reorder_images'].includes(intent.type))
        : buildReferenceScript
          ? [
            {
              type: 'build_storyboard',
              image_node_ids: photoIds,
              brief: (res.transcript || input.text || '').slice(0, 2000),
              keyframes: false,
              scene_count: photoIds.length,
            },
            ...res.intents.filter((i) => i.type !== 'generate_keyframes'),
          ]
          : res.intents;
      const rawSuggestions = !exploreOffer && res.suggestions && !intents.some((i) => i.type === 'generate_video')
        ? withGenerateFirst(
          res.suggestions,
          boardRef.current,
          intents.some((i) => i.type === 'build_storyboard' || i.type === 'generate_keyframes'),
        )
        : res.suggestions;
      const msgId = pushMessage({
        role: 'assistant',
        text: buildReferenceScript && reply === REFERENCE_PHOTOS_NOTE
          ? 'Vou montar o roteiro com as fotos da tela.'
          : reply,
        ask,
        askAnswers: recommendedAnswers(ask),
        suggestions: exploreOffer ? undefined : onReferenceCard ? withoutExtraFrameSuggestions(rawSuggestions) : rawSuggestions,
        exploreOffer,
        credits: res.credits_charged,
        inputTokens: res.input_tokens,
        outputTokens: res.output_tokens,
      });
      void refreshOrg(false);
      setSending(false);
      if (intents.length) {
        const current = input.answers ? '' : res.transcript || input.text || '';
        const texts = turnUserTexts(messagesRef.current, msgId);
        await runIntents(intents, msgId, current ? appendRequest(texts, current) : texts);
      }
    } catch (err) {
      removedRef.current = [...removed, ...removedRef.current];
      pushMessage({ role: 'assistant', text: friendlyError(err, 'Falha no agente.'), error: true });
      setSending(false);
    }
  }, [orgId, sending, pushMessage, patchMessage, selectedIds, promptReferences, refreshOrg, runIntents, friendlyError, storyboardThumbs, agentHistory, thumbsFor, updateBoard, layoutBoardNow, openQuotedVideos, openCompose]);

  const openExplore = useCallback((messageId: string) => {
    const msg = messagesRef.current.find((m) => m.id === messageId);
    if (!msg?.exploreOffer || msg.exploreOffer.resolved === 'declined') return;
    setExploreModalId(messageId);
  }, []);

  const declineExplore = useCallback((messageId: string) => {
    const msg = messagesRef.current.find((m) => m.id === messageId);
    if (!msg?.exploreOffer || msg.exploreOffer.resolved) return;
    patchMessage(messageId, { exploreOffer: { ...msg.exploreOffer, resolved: 'declined' } });
    void sendTurn({ text: 'Não usar o modelo. Siga com o roteiro normal.' });
  }, [patchMessage, sendTurn]);

  const pendingTemplateVideoRef = useRef('');

  const startTemplateOnBoard = useCallback((messageId: string, result: CanvasRemixResult) => {
    const msg = messagesRef.current.find((m) => m.id === messageId);
    const model = getVideoModelById(result.model.videoModelId);
    const locks = templateLocks(result.lockModel, model?.resolutions.length || 1);
    setExploreModalId(null);

    if (result.phase === 'failed') {
      const pendingId = pendingTemplateVideoRef.current;
      pendingTemplateVideoRef.current = '';
      if (pendingId) {
        patchVideo(pendingId, {
          status: 'failed',
          progress: undefined,
          error: result.error || 'Não foi possível criar o vídeo.',
        });
      }
      setError(result.error || 'Não foi possível criar o vídeo.');
      pushMessage({ role: 'assistant', text: result.error || 'Não foi possível criar o vídeo.', error: true });
      return;
    }

    if (result.phase === 'preparing' || (!result.jobId && result.phase !== 'ready')) {
      const awaitingStages = Boolean(result.awaitingStages);
      const pendingId = pendingTemplateVideoRef.current;
      const pending = pendingId ? nodeById(boardRef.current, pendingId) : undefined;
      // Partial stage URLs while the batch is still running — paint cards as they finish.
      if (pendingId && isVideo(pending) && (result.stageUrls || []).some(Boolean)) {
        const filled = fillTemplateStagePlaceholders(boardRef.current, pendingId, result.stageUrls || []);
        if (!('error' in filled)) {
          updateBoard(() => filled.board);
          const n = (result.stageUrls || []).filter(Boolean).length;
          const total = result.stageCount || Math.max(n, (result.stageUrls || []).length);
          patchVideo(pendingId, {
            status: 'generating',
            progress: `Stages ${n}/${total}…`,
            error: undefined,
          });
        }
        return;
      }
      const placed = placeTemplateGenerator(boardRef.current, {
        title: result.title,
        aspectRatio: result.aspectRatio,
        imageUrls: result.imageUrls,
        labels: result.imageLabels,
        includeUserImages: result.includeUserImages,
        pendingStageCount: awaitingStages ? result.stageCount : undefined,
        model: result.model,
        locks,
        progress: awaitingStages ? 'Gerando stages…' : 'Enviando…',
      });
      if ('error' in placed) {
        setError(placed.error);
        return;
      }
      pendingTemplateVideoRef.current = placed.videoId;
      updateBoard(() => placed.board);
      if (msg?.exploreOffer) patchMessage(messageId, { exploreOffer: { ...msg.exploreOffer, resolved: 'opened' } });
      pushMessage({
        role: 'assistant',
        text: awaitingStages
          ? 'Gerando as stages do modelo no fluxo…'
          : 'O vídeo do modelo está sendo preparado no fluxo…',
      });
      window.setTimeout(() => canvasRef.current?.fitView(), 60);
      return;
    }

    const pendingId = pendingTemplateVideoRef.current;
    const pending = pendingId ? nodeById(boardRef.current, pendingId) : undefined;
    if (pendingId && isVideo(pending)) {
      pendingTemplateVideoRef.current = '';
      const attached = attachTemplateStages(boardRef.current, pendingId, result.stageUrls || [], result.jobId);
      if ('error' in attached) {
        setError(attached.error);
        return;
      }
      updateBoard(() => attached.board);
      patchVideo(pendingId, {
        status: 'generating',
        progress: 'Enviando…',
        remixJobId: result.jobId,
        templateJobId: result.jobId,
        error: undefined,
      });
      pushMessage({ role: 'assistant', text: 'O vídeo do modelo está sendo gerado no fluxo.' });
      void finishRemixJob(pendingId, result.jobId);
      return;
    }

    const placed = placeTemplateGenerator(boardRef.current, {
      title: result.title,
      aspectRatio: result.aspectRatio,
      imageUrls: result.imageUrls,
      labels: result.imageLabels,
      stageUrls: result.stageUrls,
      includeUserImages: result.includeUserImages,
      model: result.model,
      locks,
      remixJobId: result.jobId,
    });
    if ('error' in placed) {
      setError(placed.error);
      return;
    }
    updateBoard(() => placed.board);
    if (msg?.exploreOffer) patchMessage(messageId, { exploreOffer: { ...msg.exploreOffer, resolved: 'opened' } });
    pushMessage({ role: 'assistant', text: 'O vídeo do modelo está sendo gerado no fluxo.' });
    void finishRemixJob(placed.videoId, result.jobId);
    window.setTimeout(() => canvasRef.current?.fitView(), 60);
  }, [updateBoard, patchMessage, pushMessage, finishRemixJob, patchVideo]);

  const onAskChange = useCallback((messageId: string, fieldId: string, value: string) => {
    setThread((m) => m.map((x) => (x.id === messageId ? { ...x, askAnswers: { ...(x.askAnswers || {}), [fieldId]: value } } : x)));
  }, [setThread]);

  const onAskConfirm = useCallback((messageId: string) => {
    const msg = messagesRef.current.find((m) => m.id === messageId);
    if (!msg?.ask || !boardHasSource(boardRef.current)) return;
    const submitted = resolveAskSubmission(msg.ask, msg.askAnswers);
    patchMessage(messageId, { askResolved: true, askAnswers: submitted.answers });
    const summary = submitted.fields
      .map((f) => f.options.find((o) => o.value === submitted.answers[f.id])?.label)
      .filter(Boolean)
      .join(' · ');
    void sendTurn({ text: summary, answers: submitted.answers });
  }, [patchMessage, sendTurn]);

  // ── Mic ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!recording) return;
    setRecordSeconds(0);
    const t = window.setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [recording]);

  const stopMic = useCallback(async () => {
    const session = micRef.current;
    micRef.current = null;
    setRecording(false);
    if (!session) return;
    try {
      const rec = await session.stop();
      void sendTurn({ audio: { base64: rec.base64, mimeType: rec.mimeType, seconds: rec.durationSeconds } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao gravar áudio.');
    }
  }, [sendTurn]);

  /**
   * "Gerar" on the generator card. Text the user typed in the card's prompt box goes to the agent exactly as if
   * typed in the chat (it writes the script and the box then shows the final prompt); otherwise it just quotes credits.
   */
  const generateFromCard = useCallback(async (videoId: string) => {
    const video = nodeById(boardRef.current, videoId);
    if (!isVideo(video)) return;
    const gen = video.generator;
    const text = gen?.promptSource === 'user' ? (gen.prompt || '').trim() : '';
    const plansFromText = Boolean(text) && !gen?.referenceVideo?.url && !video.edit && !video.fromGallery;
    const referenceTake = video.imageMode === 'reference'
      && chainForVideo(boardRef.current, videoId).some((n) => n.phase.status === 'ready' && Boolean(n.phase.imageUrl));
    if (!plansFromText || referenceTake) {
      askGenerateVideo(videoId);
      return;
    }
    if (sending) {
      setError('Aguarde o agente terminar a resposta anterior.');
      return;
    }
    // Images dropped on the card become nodes in the flow, so the agent (and the arrows) can see them.
    if (!chainForVideo(boardRef.current, videoId).length && gen && generatorHasImages(video)) {
      const items = [
        ...(gen.startImage?.url ? [{ url: gen.startImage.url, label: gen.startImage.label || 'Início' }] : []),
        ...(gen.attachments || []).filter((a) => a.url).map((a, i) => ({ url: a.url, label: a.label || `Anexo ${i + 1}` })),
        ...(gen.endImage?.url ? [{ url: gen.endImage.url, label: gen.endImage.label || 'Fim' }] : []),
      ];
      const nodes = items.map((item, i) =>
        createImageNode(item.url, Math.round(video.x - (items.length - i) * (IMAGE_NODE_W + 28)), Math.round(video.y), item.label));
      const base: Board = {
        ...boardRef.current,
        nodes: [
          ...boardRef.current.nodes.map((n) => (n.id === videoId && isVideo(n)
            ? { ...n, generator: { ...gen, startImage: undefined, attachments: [], endImage: undefined } }
            : n)),
          ...nodes,
        ],
      };
      const result = connectImagesTo(base, nodes.map((n) => n.id), videoId);
      if ('error' in result) {
        setError(result.error);
        return;
      }
      updateBoard(() => result.board);
    }
    await sendTurn({ text }, [videoId]);
    const after = nodeById(boardRef.current, videoId);
    if (!creditModalRef.current && isVideo(after) && after.planned && after.generator?.promptSource === 'script') {
      askGenerateVideo(videoId);
    }
  }, [askGenerateVideo, sending, sendTurn, updateBoard]);

  const stopMicRef = useRef(stopMic);
  stopMicRef.current = stopMic;

  const toggleMic = useCallback(async () => {
    if (micRef.current) {
      await stopMic();
      return;
    }
    try {
      micRef.current = await startMicRecording(() => void stopMicRef.current());
      setRecording(true);
    } catch (err) {
      setError(err instanceof Error && err.name === 'NotAllowedError'
        ? 'Permita o acesso ao microfone no navegador.'
        : err instanceof Error ? err.message : 'Não consegui acessar o microfone.');
    }
  }, [stopMic]);

  const cancelMic = useCallback(() => {
    micRef.current?.cancel();
    micRef.current = null;
    setRecording(false);
  }, []);

  useEffect(() => () => micRef.current?.cancel(), []);

  // ── Derived view data ────────────────────────────────────────

  const { sceneIndex, sceneRole, videoMeta } = useMemo(() => {
    const scenes = new Map<string, number>();
    const roles = new Map<string, SceneImageRole>();
    const meta = new Map<string, VideoMeta>();
    for (const v of board.nodes.filter(isVideo)) {
      const chain = chainForVideo(board, v.id);
      chain.forEach((n, i) => { if (!scenes.has(n.id)) scenes.set(n.id, i + 1); });
      if (!v.edit && !v.fromGallery && chain.length > 1) {
        const phases = chain.map((n) => n.phase);
        const r = sceneImageRoles(flowParts(phases, videoPick.videoModelId, v.imageMode || 'keyframes'), chain.length);
        chain.forEach((n, i) => { if (r[i] && !roles.has(n.id)) roles.set(n.id, r[i]); });
      }
      meta.set(v.id, v.edit
        ? { scenes: 0, seconds: Math.round(videoSeconds(board, v.id)), joins: 0, tracks: audioInputs(board, v.id).length }
        : {
            scenes: chain.length,
            seconds: v.planned ? chain[chain.length - 1]?.phase.t_end || 0 : 0,
            joins: upstreamVideos(board, v.id).length,
            label: chain[0]?.phase.label || undefined,
            refs: chain.map((n) => ({ id: n.id, url: n.phase.imageUrl, label: n.phase.label })),
            prompt: v.planned && !v.fromGallery && chain.length ? scriptPrompt(v, chain.map((n) => n.phase)) : undefined,
          });
    }
    return { sceneIndex: scenes, sceneRole: roles, videoMeta: meta };
  }, [board, videoPick.videoModelId, scriptPrompt]);

  const modalFlowItems = creditModal?.kind === 'video'
    ? creditModal.videoIds
      .map((id) => {
        const v = nodeById(board, id);
        return { phases: flowPhases(id), mode: (isVideo(v) && v.imageMode) || 'keyframes' as BoardImageMode };
      })
      .filter((f) => f.phases.length)
    : [];
  const modalFlows = modalFlowItems.map((f) => f.phases);
  /** V2V debit: filter picker to models with `referenceMedia` / modality v2v (PoYo Seedance/Wan…). */
  const modalV2vOnly = creditModal?.kind === 'video'
    && creditModal.videoIds.some((id) => {
      const v = nodeById(board, id);
      return isVideo(v) && Boolean(v.generator?.v2vOnly || v.generator?.referenceVideo?.url);
    });
  const modalV2vPreferredIds = modalV2vOnly
    ? rankVideoModelsForRequest({ modality: 'v2v', complexity: 'complex' }).map((m) => m.id)
    : undefined;
  /** Source clip length for V2V — edit must keep the original duration (locked in the modal). */
  const modalV2vSourceSeconds = modalV2vOnly && creditModal?.kind === 'video'
    ? Math.max(0, ...creditModal.videoIds.map((id) => {
      const v = nodeById(board, id);
      if (!isVideo(v)) return 0;
      return Math.round((v.totalDurationSeconds || videoSeconds(board, id) || 0) * 10) / 10;
    }))
    : 0;
  const flowSeconds = (p: FragmentadorSessionPhase[]) => p[p.length - 1]?.t_end || 0;
  const modalMaxScenes = Math.max(0, ...modalFlows.map((p) => p.length));
  const modalScenes = modalFlows.reduce((sum, p) => sum + p.length, 0);
  const modalTotal = modalFlows.reduce((sum, p) => sum + flowSeconds(p), 0);
  const modalDuration = modalTotal || (modalV2vOnly && modalV2vSourceSeconds) || videoPick.durationSeconds;
  const modalPick = { ...videoPick, durationSeconds: modalDuration };
  const modalPartCount = (id: string) => modalFlowItems.reduce((sum, f) => sum + Math.max(1, flowParts(f.phases, id, f.mode).length), 0);
  const modalParts = creditModal?.kind === 'video' ? modalPartCount(videoPick.videoModelId) : 0;
  const modalQuote = (pick: VideoModelPickerValue) => {
    if (modalFlowItems.length || creditModal?.kind !== 'video') {
      return modalFlowItems.reduce(
        (sum, f) => sum + estimateFlowVideoCredits(f.phases, { ...pick, durationSeconds: flowSeconds(f.phases) }, f.mode),
        0,
      );
    }
    return creditModal.videoIds.reduce((sum, id) => {
      const v = nodeById(board, id);
      if (!isVideo(v)) return sum;
      const model = getVideoModelById(pick.videoModelId);
      const resolution = model?.resolutions.find((r) => r.id === pick.resolutionId) || model?.resolutions[0];
      if (!model || !resolution) return sum;
      // Output seconds follow the picker (and stay locked for V2V). Ref length is the source clip
      // for billedOutputPlusRef — never reuse a stale node field that ignores chip changes.
      const outputSeconds = modalV2vOnly && modalV2vSourceSeconds
        ? modalV2vSourceSeconds
        : (pick.durationSeconds || v.totalDurationSeconds || 5);
      const refSeconds = model.billedOutputPlusRef
        ? (v.totalDurationSeconds || videoSeconds(board, id) || outputSeconds)
        : 0;
      return sum + estimateCredits({
        creditsPerSecond: rateForGeneratedAudio(resolution, Boolean(pick.generateAudio) && supportsGeneratedAudio(model)).creditsPerSecond,
        durationSeconds: outputSeconds,
        referenceDurationSeconds: refSeconds,
        billedOutputPlusRef: model.billedOutputPlusRef,
      });
    }, 0);
  };
  const modalAllParts = modalFlowItems.flatMap((f) => flowParts(f.phases, videoPick.videoModelId, f.mode));
  const modalStretched = modalAllParts.some((p) => p.durationSeconds > p.plannedSeconds + 0.01);
  const modalBilledSeconds = Math.round(modalAllParts.reduce((sum, p) => sum + p.durationSeconds, 0) * 10) / 10;
  const modalSplitNotes = modalFlowItems
    .map((f) => ({ parts: flowParts(f.phases, videoPick.videoModelId, f.mode).length, why: flowSplitReason(f.phases, videoPick.videoModelId) }))
    .filter((s) => s.parts > 1 && s.why);
  const modalModel = getVideoModelById(videoPick.videoModelId);
  const klingAspectBlocked = creditModal?.kind === 'video'
    && ['4:3', '3:4'].includes(board.aspectRatio)
    && Boolean(modalModel && (modalModel.provider === 'kling' || /^kling/i.test(modalModel.poyoModel || '')));
  const modalPending = creditModal?.kind === 'image' ? pendingKeyframes(creditModal.videoIds).length : 0;
  const modalReferences = creditModal?.kind === 'image' && referenceImageDebit(board, creditModal.videoIds);
  const outpaintCredits = pickImageModel(outpaintModelId).creditsPerImage;
  const recomposeCredits = pickImageModel(recomposeModelId).creditsPerImage;
  const modalExpand = creditModal?.kind === 'outpaint' ? creditModal.jobs.filter((j) => j.outpaint).length : 0;
  const modalRecompose = outpaintMode === 'recompose';
  const modalCrop = outpaintMode === 'crop';
  const modalExpandCredits = modalCrop ? 0 : modalRecompose ? recomposeCredits : outpaintCredits;
  const outpaintModeOptions: Array<[OutpaintMode, string, string]> = [
    ['recompose', 'Recompor com IA', `A IA refaz a cena no novo formato · ${recomposeCredits} cr`],
    ...(creditModal?.kind === 'outpaint' && creditModal.preferRecompose
      ? []
      : [['expand', 'Expandir a foto', `Foto intacta, a IA completa as bordas · ${outpaintCredits} cr`] as [OutpaintMode, string, string]]),
    ['crop', 'Recortar', `Você escolhe onde fica o ${creditModal?.kind === 'outpaint' ? creditModal.summary : 'formato'} · grátis`],
  ];
  const aspectItems = creditModal?.kind === 'video' && aspectCheck?.key === creditModal.videoIds.join(',') ? aspectCheck.items : null;
  const aspectPending = creditModal?.kind === 'video' && (aspectItems === null || (aspectItems.length > 0 && !aspectFix));
  const aspectExpandCredits = aspectItems?.length && aspectFix === 'expand' ? aspectItems.length * outpaintCredits : 0;
  const modalAudio = creditModal?.kind === 'audio' ? nodeById(board, creditModal.audioId) : undefined;
  const audioNode = isAudio(modalAudio) ? modalAudio : undefined;
  const audioPick = audioNode ? audioPicks[audioNode.audioKind] : undefined;
  const audioModel = audioNode && audioPick ? pickAudioModel(audioPick.voiceModelId, audioNode.audioKind) : undefined;
  const audioEdit = audioNode ? editNodeForAudio(board, audioNode.id) : undefined;
  const audioVideoSeconds = audioEdit ? Math.round(videoSeconds(board, audioEdit.id)) : 0;
  const audioSources = audioNode ? voiceSources(audioNode.id) : [];
  const audioNeedsSource = Boolean(audioModel?.requiresSourceAudio);
  const audioWords = audioText.trim() ? audioText.trim().split(/\s+/).length : 0;
  const audioChars = audioModel ? billableCharUnits(audioText || ' ', audioModel.maxChars).chars : 0;
  const audioCredits = audioModel ? quoteAudioCredits(audioModel.id, audioText || ' ') : 0;
  const audioBlocked = creditModal?.kind === 'audio' && (!audioText.trim() || (audioNeedsSource && !audioSources.some((s) => s.id === audioSourceId)));

  const editCount = creditModal?.kind === 'edit-image' ? creditModal.nodeIds.length : 0;
  const editCredits = pickImageModel(editModelId).creditsPerImage;
  const reviseNode = creditModal?.kind === 'revise-template' ? nodeById(board, creditModal.videoId) : undefined;
  const reviseQuote = isVideo(reviseNode)
    ? quoteTemplateCredits({
      video_model_id: reviseNode.videoModelId || '',
      resolution_id: reviseNode.resolutionId || '',
      duration_seconds: reviseNode.totalDurationSeconds || 5,
      generate_audio: Boolean(reviseNode.generateAudio),
    })
    : null;

  const modalCredits = creditModal?.kind === 'revise-template'
    ? reviseQuote || 0
    : creditModal?.kind === 'audio'
    ? audioCredits
    : creditModal?.kind === 'edit-image' || creditModal?.kind === 'compose-image'
    ? (creditModal.kind === 'compose-image' ? 1 : editCount) * editCredits
    : creditModal?.kind === 'gen-image'
    ? imageCredits
    : creditModal?.kind === 'video'
    ? modalQuote(modalFlows.length || modalV2vOnly ? modalPick : videoPick) + aspectExpandCredits
    : creditModal?.kind === 'outpaint'
      ? modalExpand * modalExpandCredits
      : modalPending * imageCredits;
  const modalTitle = audioNode
    ? AUDIO_LABELS[audioNode.audioKind].title
    : creditModal?.kind === 'edit-image'
    ? editCount > 1 ? `Editar ${editCount} imagens` : 'Editar imagem'
    : creditModal?.kind === 'compose-image'
    ? 'Gerar imagem a partir das referências'
    : creditModal?.kind === 'gen-image'
    ? 'Gerar imagem'
    : creditModal?.kind === 'revise-template'
    ? 'Ajustar o modelo'
    : creditModal?.kind === 'video' ? 'Gerar vídeo' : creditModal?.kind === 'outpaint' ? 'Mudar proporção' : modalReferences ? 'Gerar referências' : 'Gerar keyframes';
  const modalSubtitle = audioNode
    ? audioNode.audioKind === 'voice'
      ? `${audioChars} caracteres · ~${Math.round(audioWords / WORDS_PER_SECOND)}s de fala${audioVideoSeconds ? ` para um vídeo de ${audioVideoSeconds}s` : ''} · mixada com o áudio original`
      : `${audioNode.audioKind === 'music' ? 'Trilha' : 'Som'}${audioVideoSeconds ? ` para ${audioVideoSeconds}s de vídeo` : ''} · volume ${Math.round(audioNode.volume * 100)}% sobre o áudio original · preço por geração`
    : creditModal?.kind === 'edit-image'
    ? `${editCount} imagem(ns) · ${editCount} × ${editCredits} = ${modalCredits} créditos · a original fica guardada como v1 no nó`
    : creditModal?.kind === 'compose-image'
    ? `1 imagem nova · ${editCredits} créditos · as fotos de origem não mudam e a seta tracejada não entra no vídeo`
    : creditModal?.kind === 'gen-image'
    ? `1 imagem em ${board.aspectRatio} · ${imageCredits} créditos`
    : creditModal?.kind === 'revise-template'
    ? `O prompt do modelo do Explorar será ajustado: ${creditModal.change} O restante permanece.`
    : creditModal?.kind === 'video'
    ? modalFlows.length === 0
    ? `Vídeo de referência · ${modalDuration}s${modalModel?.billedOutputPlusRef ? ' · o débito inclui a duração do vídeo de referência' : ''}`
    : modalFlows.length > 1
      ? `${modalFlows.length} trechos · ${modalScenes} cenas · ${modalTotal}s · gerados juntos e juntados no vídeo final`
      : modalParts > 1
        ? `${modalScenes} cenas · ${modalParts} partes · ${modalTotal}s · juntadas ao final`
        : `${modalScenes} ${modalScenes === 1 ? 'cena' : 'cenas'} · ${modalTotal}s · escolha o modelo antes de debitar`
    : creditModal?.kind === 'outpaint'
      ? modalCrop
        ? `${modalExpand} imagem(ns) para ${creditModal.summary} · recorte sem IA, sem custo`
        : `${modalExpand} imagem(ns) para ${creditModal.summary} · ${modalExpand} × ${modalExpandCredits} = ${modalCredits} créditos`
      : modalReferences
        ? `${modalPending} referência(s) para instruir o modelo · ${modalPending} × ${imageCredits} = ${modalCredits} créditos`
        : `${modalPending} keyframe(s) · ${modalPending} × ${imageCredits} = ${modalCredits} créditos`;
  const modalConfirm = audioNode
    ? `${AUDIO_LABELS[audioNode.audioKind].confirm} · ${modalCredits} cr`
    : creditModal?.kind === 'edit-image'
    ? `Editar · ${modalCredits} cr`
    : creditModal?.kind === 'compose-image'
    ? `Gerar imagem · ${modalCredits} cr`
    : creditModal?.kind === 'gen-image'
    ? `Gerar imagem · ${modalCredits} cr`
    : creditModal?.kind === 'revise-template'
    ? `Gerar de novo · ${modalCredits} cr`
    : creditModal?.kind === 'video'
    ? 'Debitar e gerar vídeo'
    : creditModal?.kind === 'outpaint'
      ? modalCrop ? 'Recortar · grátis' : `${modalRecompose ? 'Recompor' : 'Expandir'} · ${modalCredits} cr`
      : modalReferences ? `Gerar referências · ${modalCredits} cr` : `Gerar keyframes · ${modalCredits} cr`;

  const chatsLocked = sending || recording || messages.some((m) => m.busy);
  const rememberChatBook = useCallback((book: CanvasChatBook<CanvasChatMessage>) => {
    chatBookRef.current = book;
    setChatBook(book);
    const org = loadedOrgRef.current;
    const projectId = activeProjectIdRef.current;
    if (org && projectId) saveChatBook(org, projectId, book);
  }, []);
  const showChat = useCallback((book: CanvasChatBook<CanvasChatMessage>) => {
    const thread = activeChatMessages(book).map((m) => ({ ...m, busy: false }));
    rememberChatBook(book);
    messagesRef.current = thread;
    setMessages(thread);
    removedRef.current = [];
  }, [rememberChatBook]);
  const startNewChat = useCallback(() => {
    if (sending || recording || messagesRef.current.some((m) => m.busy)) return;
    const current = replaceActiveChat(chatBookRef.current, messagesRef.current.map((m) => (m.busy ? { ...m, busy: false } : m)));
    const next = openNewChat(current);
    if (next === current) return;
    showChat(next);
  }, [sending, recording, showChat]);
  const selectChatTab = useCallback((id: string) => {
    if (sending || recording || messagesRef.current.some((m) => m.busy)) return;
    if (id === chatBookRef.current.activeId) return;
    const current = replaceActiveChat(chatBookRef.current, messagesRef.current.map((m) => (m.busy ? { ...m, busy: false } : m)));
    const next = selectChat(current, id);
    if (next === current) return;
    showChat(next);
  }, [sending, recording, showChat]);
  const closeChatTab = useCallback((id: string) => {
    if (sending || recording || messagesRef.current.some((m) => m.busy)) return;
    const current = replaceActiveChat(chatBookRef.current, messagesRef.current.map((m) => (m.busy ? { ...m, busy: false } : m)));
    const next = closeChat(current, id);
    if (next === current) return;
    if (next.activeId !== current.activeId) showChat(next);
    else rememberChatBook(next);
  }, [sending, recording, showChat, rememberChatBook]);
  const chatTabs = chatBook.chats.map((c) => ({
    id: c.id,
    title: chatTabLabel(c.id === chatBook.activeId ? messages : c.messages),
  }));
  const newChatDisabled = chatsLocked || !chatHasContent(messages) || chatBook.chats.length >= CHAT_TAB_MAX;
  const newChatHint = chatsLocked
    ? 'Espere a resposta terminar'
    : chatBook.chats.length >= CHAT_TAB_MAX
      ? 'Este projeto já tem 12 chats'
      : !chatHasContent(messages)
        ? 'Este chat já está vazio'
        : 'Começar um chat zerado';

  const doneFlows = (keyframesDone || []).filter((id) => isVideo(nodeById(board, id)) && flowPhases(id).length);
  const doneAsReferences = referenceImageDebit(board, doneFlows);
  const doneCredits = doneFlows.reduce((sum, id) => {
    const v = nodeById(board, id);
    const phases = flowPhases(id);
    const mode = (isVideo(v) && v.imageMode) || 'keyframes';
    return sum + estimateFlowVideoCredits(phases, { ...videoPick, durationSeconds: flowSeconds(phases) }, mode);
  }, 0);

  return (
    <div className={`tb-page${trayCollapsed && projectsCollapsed && (generationsCollapsed || !generations.length) ? '' : ' has-tray'}`}>
      <BoardCanvas
        ref={canvasRef}
        board={board}
        selectedIds={selectedIds}
        sceneIndex={sceneIndex}
        sceneRole={sceneRole}
        videoMeta={videoMeta}
        projectTitle={activeOwnMeta?.title || activeShared?.title}
        onSelect={setSelectedIds}
        onMoveNodes={moveNodes}
        onResizeNode={resizeNode}
        onSetImageMode={setImageMode}
        onPatchVideo={patchVideo}
        onUploadGenerator={(videoId, slot, file) => { void uploadGeneratorFile(videoId, slot, file); }}
        onGenerateVideo={(videoId) => { void generateFromCard(videoId); }}
        assemblingIds={assemblingIds}
        aspect={board.aspectRatio}
        onAspect={(aspect) => setBoardAspect(aspect)}
        onAction={runCanvasAction}
        onConnect={connectNodes}
        onConnectImages={connectImages}
        onDeleteEdge={(edgeId) => {
          const edge = boardRef.current.edges.find((e) => e.id === edgeId);
          updateBoard((b) => ({ ...b, edges: b.edges.filter((e) => e.id !== edgeId) }));
          const to = edge ? nodeById(boardRef.current, edge.to) : undefined;
          if (isVideo(to) && to.edit) void settleVideo(to.id);
        }}
        onRemoveNodes={removeBoardNodes}
        onCopySelection={copyBoardSelection}
        onPasteClipboard={(at) => { void pasteBoardClipboard(at); }}
        onSelectAudioVersion={selectAudioVersion}
        onSelectImageVersion={selectImageVersion}
        onOpenMedia={setViewerId}
        onUpdateAudio={updateAudioTrack}
        onRegenerateAudio={openAudioModal}
        onDropFiles={dropFiles}
        onDropUrls={(urls, at) => void dropUrls(urls, at)}
        onOpenGallery={() => openGallery('both')}
        onDropGallery={placeGalleryItems}
        onSelectVersion={selectVideoVersion}
        readOnly={readOnly}
        onUndo={undoBoard}
        onRedo={redoBoard}
        canUndo={canUndo}
        canRedo={canRedo}
        onLayout={layoutBoardNow}
        onUpdateComment={updateComment}
        focusCommentId={focusCommentId}
        onCommentFocused={() => setFocusCommentId(null)}
      />

      <div className="tb-side">
        <CanvasProjectsPanel
          projects={projects}
          shared={sharedWithMe}
          activeId={activeProjectId}
          busy={projectBusy}
          collapsed={projectsCollapsed}
          onCollapsedChange={changeProjectsCollapsed}
          onOpen={openProject}
          onOpenShared={openSharedProject}
          onNew={newProject}
          onRename={renameProject}
          onDelete={deleteProject}
        />
        <CanvasGenerationsPanel
          generations={generations}
          busyVideoIds={busyVideoIds}
          projectTitle={activeOwnMeta?.title || activeShared?.title}
          collapsed={generationsCollapsed}
          onCollapsedChange={changeGenerationsCollapsed}
          onActivate={selectVideoVersion}
        />
        <CanvasGalleryTray
          orgId={orgId}
          role={role}
          refreshKey={boardUrls.size}
          boardUrls={boardUrls}
          collapsed={trayCollapsed}
          onCollapsedChange={changeTrayCollapsed}
          onAdd={placeAtViewCenter}
          onOpenGallery={openGallery}
        />
      </div>

      {readOnly ? (
        <div className="tb-readonly-bar" onPointerDown={(e) => e.stopPropagation()}>
          <Eye size={16} />
          <span>
            <strong>Somente visualização</strong>
            <small>Compartilhado por {activeShared?.ownerName || sync?.ownerName || 'alguém da empresa'} · use o ícone “i” de cada nó para ver o roteiro</small>
          </span>
          <button type="button" onClick={duplicateShared}>
            <Copy size={13} /> Duplicar para mim
          </button>
        </div>
      ) : (
      <CanvasPrompt
        ref={promptRef}
        messages={messages}
        sending={sending}
        recording={recording}
        recordSeconds={recordSeconds}
        balance={balance}
        suggestions={starterSuggestions(board)}
        onSend={(text) => void sendTurn({ text })}
        onToggleMic={() => void toggleMic()}
        onCancelMic={cancelMic}
        onAttach={() => openGallery('both')}
        askBlockedReason={hasSource ? undefined : 'As imagens desta pergunta não estão mais no quadro. Arraste imagens e peça de novo.'}
        onAskChange={onAskChange}
        onAskConfirm={onAskConfirm}
        onReopenDebit={reopenDebit}
        onOpenExplore={openExplore}
        onDeclineExplore={declineExplore}
        references={promptReferences}
        onRemoveReference={(id) => setSelectedIds((ids) => ids.filter((x) => x !== id))}
        onClearReferences={() => setSelectedIds([])}
        onDockChange={setChatDocked}
        chatWidth={chatWidth}
        onChatWidthChange={setChatWidthClamped}
        chats={chatTabs}
        activeChatId={chatBook.activeId}
        onNewChat={startNewChat}
        onSelectChat={selectChatTab}
        onCloseChat={closeChatTab}
        chatsLocked={chatsLocked}
        newChatDisabled={newChatDisabled}
        newChatHint={newChatHint}
      />
      )}

      {error && <p className="tb-toast-error" role="alert">{error}</p>}

      {doneFlows.length > 0 && !creditModal && (
        <div className="tb-done-card" role="dialog" aria-labelledby="tb-done-title" onPointerDown={(e) => e.stopPropagation()}>
          <CheckCircle2 size={28} className="tb-done-icon" />
          <h3 id="tb-done-title">{doneAsReferences ? 'Referências prontas' : 'Keyframes prontas'}</h3>
          <p>Confira as imagens no quadro. Quando estiver tudo certo, gere o vídeo final.</p>
          <div className="tb-done-actions">
            <button
              type="button"
              className="tb-done-primary"
              onClick={() => {
                const ids = doneFlows;
                setKeyframesDone(null);
                rememberDebit({ kind: 'video', videoIds: ids });
                void syncScripts(ids);
              }}
            >
              <Clapperboard size={15} /> Gerar vídeo final · ~{doneCredits} cr
            </button>
            <button type="button" className="tb-done-secondary" onClick={() => setKeyframesDone(null)}>OK</button>
          </div>
        </div>
      )}

      <div className="tb-top-actions" onPointerDown={(e) => e.stopPropagation()}>
        {!readOnly && (
          <label className="tb-aspect-select" title="Formato das próximas imagens e vídeos">
            <span>Formato</span>
            <select
              value={board.aspectRatio}
              onChange={(e) => setBoardAspect(e.target.value === 'original' ? 'original' : (e.target.value as BoardAspect))}
            >
              {BOARD_ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
              <option value="original">Igual à foto</option>
            </select>
          </label>
        )}
        {activeShared ? (
          <span className="tb-share-chip" title={`Projeto de ${activeShared.ownerName}`}>
            <Users size={13} /> {activeShared.ownerName} · {sync?.role === 'edit' || (!sync && activeShared.role === 'edit') ? 'pode editar' : 'pode ver'}
          </span>
        ) : (
          <button
            type="button"
            className={`tb-help-btn${canShare ? '' : ' is-locked'}${activeOwnMeta?.shared ? ' is-on' : ''}`}
            onClick={() => setShareOpen(true)}
            title={canShare ? 'Compartilhar com pessoas da empresa' : 'Disponível no plano Empresarial'}
          >
            {canShare ? <Users size={15} /> : <Lock size={14} />}
            {activeOwnMeta?.shared ? 'Compartilhado' : 'Compartilhar'}
          </button>
        )}
        <button
          type="button"
          className="tb-help-btn"
          onClick={() => setTutorialOpen(true)}
          title="Ver tutorial em vídeo"
        >
          <PlayCircle size={15} /> Tutorial
        </button>
      </div>

      <CanvasTutorialModal open={tutorialOpen} onClose={closeTutorial} />
      {viewerNode && (
        <CanvasMediaViewer
          board={board}
          node={viewerNode}
          sceneIndex={sceneIndex}
          readOnly={readOnly}
          onClose={closeViewer}
          onSelectImageVersion={selectImageVersion}
          onSelectVideoVersion={selectVideoVersion}
        />
      )}

      <CanvasShareModal
        open={shareOpen}
        orgId={orgId}
        projectId={activeProjectId}
        projectTitle={activeOwnMeta?.title || 'Projeto'}
        canShare={canShare}
        hasChat={messages.some((m) => Boolean(m.text?.trim()) && !m.contextBreak)}
        onClose={() => setShareOpen(false)}
        onSave={saveShare}
      />

      <GalleryMediaPicker
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        accept={galleryAccept}
        multiple
        title="Adicionar ao quadro"
        onSelect={({ asset }) => placeAtViewCenter({
          url: toMediaUrl(asset.video_url),
          title: asset.title || (isVideoAsset(asset) ? 'Vídeo' : 'Imagem'),
          kind: isVideoAsset(asset) ? 'video' : 'image',
        })}
      />

      {exploreModalId && messages.some((m) => m.id === exploreModalId && m.exploreOffer) && (
        <CanvasExploreModal
          templateId={messages.find((m) => m.id === exploreModalId)?.exploreOffer?.templateId || ''}
          texts={messages.find((m) => m.id === exploreModalId)?.exploreOffer?.texts}
          imageUrls={messages.find((m) => m.id === exploreModalId)?.exploreOffer?.imageUrls || []}
          durationSeconds={messages.find((m) => m.id === exploreModalId)?.exploreOffer?.durationSeconds}
          onClose={() => setExploreModalId(null)}
          onGenerated={(result) => {
            const offerId = exploreModalId;
            if (offerId) startTemplateOnBoard(offerId, result);
          }}
        />
      )}

      <CreditConfirmModal
        open={Boolean(creditModal)}
        title={modalTitle}
        subtitle={modalSubtitle}
        credits={modalCredits}
        balance={balance}
        confirmLabel={modalConfirm}
        allowFree={creditModal?.kind === 'outpaint' && modalCrop}
        confirmDisabled={
          aspectPending || audioBlocked || klingAspectBlocked
          || (creditModal?.kind === 'gen-image' && !genImagePrompt.trim())
          || ((creditModal?.kind === 'edit-image' || creditModal?.kind === 'compose-image') && !editText.trim())
          || (creditModal?.kind === 'revise-template' && reviseQuote == null)
        }
        onCancel={() => {
          const owner = debitOwnerRef.current;
          if (owner && creditModal?.kind === 'gen-image') {
            const prompt = genImagePrompt;
            setThread((m) => m.map((x) => (
              x.id === owner && x.debit?.kind === 'gen-image' ? { ...x, debit: { ...x.debit, prompt } } : x
            )));
          }
          if (owner && (creditModal?.kind === 'edit-image' || creditModal?.kind === 'compose-image')) {
            const instruction = editText.trim() || creditModal.instruction;
            const kind = creditModal.kind;
            setThread((m) => m.map((x) => (
              x.id === owner && x.debit?.kind === kind ? { ...x, debit: { ...x.debit, instruction } } : x
            )));
          }
          if (creditModal?.kind === 'audio') nextAudioModal();
          setCreditModal(null);
        }}
        onConfirm={() => {
          if (!creditModal) return;
          markDebitDone();
          if (creditModal.kind === 'image') void runKeyframes(creditModal.videoIds);
          else if (creditModal.kind === 'outpaint') {
            const { jobs, aspect, summary } = creditModal;
            const mode = outpaintMode;
            const picks = cropPicks;
            runImageStep(() => runNormalize(jobs, aspect, summary, mode, picks));
          } else if (creditModal.kind === 'audio') void runAudio(creditModal.audioId);
          else if (creditModal.kind === 'gen-image') void runGenImage(genImagePrompt, creditModal.at);
          else if (creditModal.kind === 'edit-image') {
            const text = editText.trim();
            const asked = text === creditModal.instruction.trim();
            const { nodeIds, promptEn, referenceNodeIds, regenerateVideoId } = creditModal;
            runImageStep(() => runEditImages(
              nodeIds,
              asked && promptEn ? promptEn : text,
              text,
              {
                ...(referenceNodeIds?.length ? { referenceNodeIds } : {}),
                ...(regenerateVideoId ? { regenerateVideoId } : {}),
              },
            ));
          } else if (creditModal.kind === 'compose-image') {
            const text = editText.trim();
            const { nodeId, sourceNodeIds, instruction } = creditModal;
            runImageStep(() => runComposeImage(
              nodeId,
              sourceNodeIds,
              instruction,
              text,
            ));
          } else if (creditModal.kind === 'revise-template') void runReviseTemplate(creditModal.videoId, creditModal.change);
          else void runVideoPrepared(creditModal.videoIds);
        }}
      >
        {creditModal?.kind === 'edit-image' && (
          <div className="tb-audio-modal">
            <div className="tb-edit-thumbs">
              {creditModal.nodeIds.map((id) => {
                const n = nodeById(board, id);
                return isImage(n) && n.phase.imageUrl
                  ? <img key={id} src={n.phase.imageUrl} alt={n.phase.label} title={n.phase.label} />
                  : null;
              })}
            </div>
            {Boolean(creditModal.referenceNodeIds?.length) && (
              <>
                <div className="tb-field-label">Referência</div>
                <div className="tb-edit-thumbs">
                  {creditModal.referenceNodeIds!.map((id) => {
                    const n = nodeById(board, id);
                    return isImage(n) && n.phase.imageUrl
                      ? <img key={id} src={n.phase.imageUrl} alt={n.phase.label} title={n.phase.label} />
                      : null;
                  })}
                </div>
              </>
            )}
            {creditModal.regenerateVideoId ? (
              <p className="tb-edit-regen-hint">Depois da edição, abre o débito para gerar uma nova versão do vídeo neste nó.</p>
            ) : null}
            <div className="tb-field-label">O que mudar</div>
            <textarea
              className="tb-audio-text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="Ex.: tire todas as letras e logotipos da imagem"
              rows={3}
              maxLength={1500}
              autoFocus
            />
            <div className="tb-field-label">Modelo de edição</div>
            <ImageModelPicker value={editModelId} onChange={setEditModelId} useCase="edit" compact />
          </div>
        )}
        {creditModal?.kind === 'compose-image' && (
          <div className="tb-audio-modal">
            <div className="tb-field-label">Referências (não são editadas)</div>
            <div className="tb-edit-thumbs">
              {creditModal.sourceNodeIds.map((id) => {
                const n = nodeById(board, id);
                return isImage(n) && n.phase.imageUrl
                  ? <img key={id} src={n.phase.imageUrl} alt={n.phase.label} title={n.phase.label} />
                  : null;
              })}
            </div>
            <p className="tb-edit-regen-hint">A seta tracejada ciano liga cada foto à imagem nova e não entra no vídeo. A seta contínua lilás é a do vídeo: você pode ligar as duas no mesmo par.</p>
            <div className="tb-field-label">Prompt</div>
            {(creditModal.promptEn || '').trim() !== creditModal.instruction.trim() && !wantsPromptUntouched(creditModal.instruction) && (
              <p className="tb-edit-regen-hint">O agente deixou a intenção mais clara. O texto desta caixa é o que o modelo recebe.</p>
            )}
            <textarea
              className="tb-audio-text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="Ex.: cômodo mobiliado com os móveis da segunda imagem"
              rows={6}
              maxLength={2000}
              autoFocus
            />
            <div className="tb-field-label">Modelo de imagem</div>
            <ImageModelPicker value={editModelId} onChange={setEditModelId} useCase="edit" compact />
          </div>
        )}
        {creditModal?.kind === 'gen-image' && (
          <div className="tb-audio-modal">
            <div className="tb-field-label">Descreva a imagem</div>
            <textarea
              className="tb-audio-text"
              value={genImagePrompt}
              onChange={(e) => setGenImagePrompt(e.target.value)}
              placeholder="Ex.: sala de estar ampla com luz natural, sofá de linho, plantas…"
              rows={4}
              maxLength={1500}
              autoFocus
            />
            <div className="tb-field-label">Modelo de imagem</div>
            <ImageModelPicker value={imageModelId} onChange={setImageModelId} useCase="t2i" compact />
          </div>
        )}
        {audioNode && audioPick && audioModel && (
          <div className="tb-audio-modal">
            <div className="tb-field-label">
              {audioNode.audioKind === 'voice' ? 'Modelo de voz' : audioNode.audioKind === 'music' ? 'Modelo de música' : 'Modelo de som'}
            </div>
            <AudioModelPicker
              kind={audioNode.audioKind}
              value={audioPick}
              onChange={(next) => setAudioPicks((p) => ({ ...p, [audioNode.audioKind]: next }))}
              allowSourceModels={audioNode.audioKind === 'music'}
            />
            {audioNeedsSource && (
              <label className="tb-audio-row">
                <span className="tb-field-label">Locução de base</span>
                {audioSources.length ? (
                  <select value={audioSourceId} onChange={(e) => setAudioSourceId(e.target.value)}>
                    {audioSources.map((s) => (
                      <option key={s.id} value={s.id}>{s.title || 'Locução'}</option>
                    ))}
                  </select>
                ) : (
                  <span className="tb-audio-warn">Add Instrumental precisa de uma locução pronta ligada ao mesmo vídeo editado.</span>
                )}
              </label>
            )}
            {audioModel.poyoModel === 'generate-sounds' && (
              <label className="tb-audio-check">
                <input type="checkbox" checked={audioLoop} onChange={(e) => setAudioLoop(e.target.checked)} />
                Som contínuo (loop)
              </label>
            )}
            <div className="tb-field-label">{AUDIO_LABELS[audioNode.audioKind].field}</div>
            <textarea
              className="tb-audio-text"
              value={audioText}
              onChange={(e) => setAudioText(e.target.value)}
              placeholder={AUDIO_LABELS[audioNode.audioKind].placeholder}
              rows={audioNode.audioKind === 'voice' ? 6 : 3}
              maxLength={audioModel.maxChars}
            />
            {audioNode.audioKind === 'voice' && audioVideoSeconds > 0 && audioWords / WORDS_PER_SECOND > audioVideoSeconds * 1.1 && (
              <p className="tb-audio-warn">
                Esse texto dá ~{Math.round(audioWords / WORDS_PER_SECOND)}s de fala e o vídeo tem {audioVideoSeconds}s — encurte para não cortar o fim.
              </p>
            )}
          </div>
        )}
        {creditModal?.kind === 'video' && aspectItems === null && (
          <p className="tb-aspect-note">Conferindo a proporção das fotos…</p>
        )}
        {creditModal?.kind === 'video' && aspectItems && aspectItems.length > 0 && (
          <div className="tb-aspect-ask">
            <div className="tb-field-label">
              {aspectItems.length} foto(s) com faixas ou fora de {board.aspectRatio}
            </div>
            <p className="tb-aspect-note">Sem ajuste, o vídeo sai com faixas cinza nas laterais.</p>
            <div className="tb-aspect-options" role="radiogroup" aria-label="Como ajustar as fotos">
              {([
                ['crop', 'Recortar', 'grátis · corta um pouco das bordas'],
                ['expand', 'Expandir com IA', `${aspectItems.length} × ${outpaintCredits} cr · foto inteira preservada`],
                ['keep', 'Manter', 'vídeo com faixas nas laterais'],
              ] as const).map(([id, title, hint]) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={aspectFix === id}
                  className={`tb-aspect-option${aspectFix === id ? ' is-on' : ''}`}
                  onClick={() => setAspectFix(id)}
                >
                  <strong>{title}</strong>
                  <span>{hint}</span>
                </button>
              ))}
            </div>
            {aspectFix === 'expand' && (
              <div style={{ marginTop: '0.6rem' }}>
                <div className="tb-field-label">Modelo de expansão</div>
                <ImageModelPicker value={outpaintModelId} onChange={setOutpaintModelId} useCase="outpaint" compact />
              </div>
            )}
          </div>
        )}
        {creditModal?.kind === 'image' && (
          <div>
            <div className="tb-field-label">Modelo de imagem</div>
            <ImageModelPicker value={imageModelId} onChange={setImageModelId} useCase="t2i" compact />
          </div>
        )}
        {creditModal?.kind === 'outpaint' && (
          <div>
            <div className="tb-aspect-ask">
              <div className="tb-field-label">Como mudar a proporção</div>
              {creditModal.preferRecompose && (
                <p className="tb-aspect-note">
                  Mudança de orientação (ex. horizontal/quadrado → 9:16): expandir bordas não serve — use recompor (modelo de edição) ou recortar.
                </p>
              )}
              <div className="tb-aspect-options" role="radiogroup" aria-label="Como mudar a proporção">
                {outpaintModeOptions.map(([id, title, hint]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={outpaintMode === id}
                    className={`tb-aspect-option${outpaintMode === id ? ' is-on' : ''}`}
                    onClick={() => setOutpaintMode(id)}
                  >
                    <strong>{title}</strong>
                    <span>{hint}</span>
                  </button>
                ))}
              </div>
            </div>
            {modalCrop ? (
              <div className="tb-crop-list">
                <p className="tb-aspect-note">Arraste a moldura para escolher a parte da foto que fica.</p>
                {creditModal.jobs.filter((j) => j.outpaint).map((job) => {
                  const n = nodeById(board, job.nodeId);
                  if (!isImage(n) || !n.phase.imageUrl) return null;
                  return (
                    <CropPicker
                      key={job.nodeId}
                      src={n.phase.imageUrl}
                      ratio={job.frame.width / job.frame.height}
                      value={cropPicks[job.nodeId] ?? CENTER_CROP}
                      onChange={(next) => setCropPicks((p) => ({ ...p, [job.nodeId]: next }))}
                      label={modalExpand > 1 ? n.phase.label || 'Imagem' : undefined}
                    />
                  );
                })}
              </div>
            ) : (
              <>
                <div className="tb-field-label">{modalRecompose ? 'Modelo para recompor' : 'Modelo de expansão'}</div>
                {modalRecompose
                  ? <ImageModelPicker value={recomposeModelId} onChange={setRecomposeModelId} useCase="recompose" compact />
                  : <ImageModelPicker value={outpaintModelId} onChange={setOutpaintModelId} useCase="outpaint" compact />}
              </>
            )}
          </div>
        )}
        {creditModal?.kind === 'video' && (
          <div>
            <div className="tb-field-label">{modalV2vOnly ? 'Modelo V2V (vídeo de referência)' : 'Modelo de vídeo'}</div>
            <VideoModelPicker
              modality={modalV2vOnly ? 'v2v' : 'i2v'}
              value={modalFlows.length || modalV2vOnly ? modalPick : videoPick}
              onChange={(next) => setVideoPick(
                modalFlows.length || modalV2vOnly ? { ...next, durationSeconds: modalDuration } : next,
              )}
              durationSeconds={modalFlows.length || modalV2vOnly ? modalDuration : videoPick.durationSeconds}
              onDurationChange={
                modalFlows.length || modalV2vOnly
                  ? undefined
                  : (seconds) => setVideoPick((prev) => ({ ...prev, durationSeconds: seconds }))
              }
              referenceDurationSeconds={modalV2vOnly && modalV2vSourceSeconds ? modalV2vSourceSeconds : undefined}
              minReferenceImages={modalV2vOnly ? undefined : modalMaxScenes}
              minDurationSeconds={modalFlows.length ? modalDuration : undefined}
              sceneCount={modalV2vOnly ? undefined : modalMaxScenes}
              partCount={modalV2vOnly ? undefined : modalPartCount}
              quoteCredits={modalQuote}
              allowMultiClip={!modalV2vOnly}
              preferredModelIds={modalV2vPreferredIds}
              lockDuration={modalFlows.length > 0 || modalV2vOnly}
              lockDurationTitle={
                modalV2vOnly
                  ? `Edição V2V mantém os ${modalDuration}s do vídeo original`
                  : modalFlows.length > 0
                    ? scriptDurationTitle(modalTotal)
                    : undefined
              }
              lockDurationHint={modalV2vOnly ? 'vídeo original' : 'roteiro'}
              compact
            />
            {(modalSplitNotes.length > 0 || modalStretched) && (
              <p className="tb-aspect-note">
                {modalSplitNotes.map((s) => `Não cabe em um vídeo (${s.why}): o fluxo vira ${s.parts} trechos na tela, juntados num vídeo final.`).join(' ')}
                {modalStretched && ` Trechos mais curtos que o mínimo do modelo serão alongados (${modalBilledSeconds}s cobrados).`}
              </p>
            )}
            {klingAspectBlocked && (
              <p className="tb-audio-warn">
                O Kling só gera em 16:9, 9:16 ou 1:1 e o quadro está em {board.aspectRatio}. Escolha Seedance ou Wan, ou troque o formato no topo.
              </p>
            )}
          </div>
        )}
      </CreditConfirmModal>

      <style>{tbCss}</style>
    </div>
  );
}

const tbCss = `
.tb-page {
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background:
    radial-gradient(ellipse 55% 45% at 8% 0%, rgba(99,102,241,0.16), transparent 70%),
    radial-gradient(ellipse 50% 45% at 100% 100%, rgba(34,211,238,0.09), transparent 70%),
    #0f1115;
}
.tb-viewport {
  position: absolute;
  inset: 0;
  transition: right 0.45s cubic-bezier(.2,.8,.2,1);
  overflow: hidden;
  touch-action: none;
  cursor: grab;
  background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0);
}
.tb-viewport.is-panning { cursor: grabbing; }
.tb-viewport.is-drop { box-shadow: inset 0 0 0 2px rgba(129,140,248,0.55); background-color: rgba(99,102,241,0.05); }
.tb-world { position: absolute; left: 0; top: 0; transform-origin: 0 0; will-change: transform; }
.tb-edges { position: absolute; left: 0; top: 0; overflow: visible; pointer-events: none; }
.tb-edge-hit { fill: none; stroke: transparent; stroke-width: 16; pointer-events: stroke; cursor: pointer; }
.tb-edge-line { fill: none; stroke: #818cf8; stroke-width: 2.5; stroke-linecap: round; pointer-events: none; }
.tb-edge.is-fresh .tb-edge-line {
  filter: drop-shadow(0 0 5px rgba(167, 139, 250, 0.55));
}
.tb-edge.is-join.is-fresh .tb-edge-line {
  filter: drop-shadow(0 0 5px rgba(52, 211, 153, 0.6));
}
.tb-edge.is-audio.is-fresh .tb-edge-line {
  filter: drop-shadow(0 0 5px rgba(232, 121, 249, 0.6));
}
.tb-edge-energy {
  fill: none;
  stroke-linecap: round;
  pointer-events: none;
  stroke-dashoffset: 100;
  animation: tb-energy 1.05s cubic-bezier(0.42, 0.04, 0.18, 1) forwards;
}
.tb-edge-energy-haze {
  stroke: rgba(167, 139, 250, 0.8);
  stroke-width: 16;
  filter: blur(9px);
  stroke-dasharray: 36 64;
}
.tb-edge-energy-bloom {
  stroke: #e9d5ff;
  stroke-width: 6;
  filter: blur(3.5px);
  stroke-dasharray: 22 78;
}
.tb-edge-energy-core {
  stroke: #ffffff;
  stroke-width: 2.4;
  filter: drop-shadow(0 0 8px rgba(233, 213, 255, 1)) drop-shadow(0 0 18px rgba(139, 92, 246, 0.9));
  stroke-dasharray: 10 90;
}
.tb-edge.is-join .tb-edge-energy-haze { stroke: rgba(52, 211, 153, 0.75); }
.tb-edge.is-join .tb-edge-energy-bloom { stroke: #a7f3d0; }
.tb-edge.is-join .tb-edge-energy-core {
  stroke: #f0fdf4;
  filter: drop-shadow(0 0 8px rgba(167, 243, 208, 1)) drop-shadow(0 0 18px rgba(16, 185, 129, 0.85));
}
.tb-edge.is-audio .tb-edge-energy-haze { stroke: rgba(232, 121, 249, 0.75); }
.tb-edge.is-audio .tb-edge-energy-bloom { stroke: #f5d0fe; }
.tb-edge.is-audio .tb-edge-energy-core {
  stroke: #fdf4ff;
  filter: drop-shadow(0 0 8px rgba(245, 208, 254, 1)) drop-shadow(0 0 18px rgba(217, 70, 239, 0.85));
}
@keyframes tb-energy {
  0% { stroke-dashoffset: 100; opacity: 0; }
  10% { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 1; }
}
.tb-edge-energy.is-loop {
  animation-name: tb-energy-loop;
  animation-iteration-count: infinite;
}
@keyframes tb-energy-loop {
  0% { stroke-dashoffset: 100; opacity: 0; }
  10% { opacity: 1; }
  82% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0; }
}
.tb-receive-ring {
  position: absolute;
  inset: -10px;
  width: calc(100% + 20px);
  height: calc(100% + 20px);
  pointer-events: none;
  z-index: 6;
  overflow: visible;
}
.tb-receive-ring rect {
  x: 10px;
  y: 10px;
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  rx: 16px;
  ry: 16px;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: tb-border-fill 1.2s cubic-bezier(0.22, 0.75, 0.2, 1) forwards;
}
.tb-node--gen .tb-receive-ring rect { rx: 22px; ry: 22px; }
.tb-receive-ring.is-loop rect {
  animation: tb-energy-loop 1.05s cubic-bezier(0.42, 0.04, 0.18, 1) infinite;
}
.tb-receive-ring.is-loop .tb-receive-haze { stroke-dasharray: 36 64; }
.tb-receive-ring.is-loop .tb-receive-bloom { stroke-dasharray: 22 78; }
.tb-receive-ring.is-loop .tb-receive-line { stroke-dasharray: 10 90; }
.tb-receive-haze {
  stroke: rgba(139, 92, 246, 0.72);
  stroke-width: 18;
  filter: blur(16px);
}
.tb-receive-bloom {
  stroke: rgba(196, 181, 253, 0.95);
  stroke-width: 7;
  filter: blur(7px);
}
.tb-receive-line {
  stroke: #f8f5ff;
  stroke-width: 1.6;
  filter: drop-shadow(0 0 10px rgba(216, 180, 254, 0.95)) drop-shadow(0 0 28px rgba(129, 140, 248, 0.7));
}
.tb-node.is-receive {
  animation: tb-receive-aura 1.2s ease forwards;
}
.tb-node.is-receive.is-generating { animation-iteration-count: infinite; }
@keyframes tb-border-fill {
  0% { stroke-dashoffset: 100; opacity: 0; }
  14% { opacity: 1; }
  70% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0; }
}
@keyframes tb-receive-aura {
  0% { box-shadow: 0 18px 40px rgba(0,0,0,0.45), 0 0 0 0 rgba(167,139,250,0); }
  58% {
    box-shadow:
      0 22px 48px rgba(0,0,0,0.5),
      0 0 0 1px rgba(237, 233, 254, 0.55),
      0 0 24px 6px rgba(167, 139, 250, 0.62),
      0 0 56px 16px rgba(129, 140, 248, 0.34),
      0 0 96px 28px rgba(99, 102, 241, 0.2);
  }
  100% { box-shadow: 0 18px 40px rgba(0,0,0,0.45); }
}
.tb-edge.is-join .tb-edge-line { stroke: #34d399; }
.tb-edge.is-audio .tb-edge-line { stroke: #e879f9; stroke-dasharray: 2 5; }
.tb-edge.is-ref .tb-edge-line { stroke: #22d3ee; stroke-dasharray: 2 7; stroke-width: 2.25; }
.tb-edge.is-ref.is-fresh .tb-edge-line { filter: drop-shadow(0 0 5px rgba(34, 211, 238, 0.7)); }
.tb-edge.is-ref.is-selected .tb-edge-line { stroke: #67e8f9; stroke-width: 3.5; }
.tb-edge-tag.is-ref { border-color: rgba(34,211,238,0.65); color: #a5f3fc; background: rgba(8,32,40,0.94); }
.tb-edit-regen-hint { margin: 0 0 10px; font-size: 12px; line-height: 1.4; color: #94a3b8; }
.tb-node--edit { border-color: rgba(232,121,249,0.35); }
.tb-node--comment {
  padding: 0;
  gap: 0;
  background: #1c1914;
  border-color: rgba(251, 191, 36, 0.42);
  user-select: text;
}
.tb-comment-head {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 12px 4px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: #fcd34d; cursor: grab;
}
.tb-comment-text {
  flex: 1 1 auto; width: 100%; min-height: 0; resize: none;
  border: 0; outline: none; background: transparent;
  color: #f5f5f4; font: inherit; font-size: 13px; line-height: 1.45;
  padding: 4px 12px 12px; cursor: text; user-select: text;
}
.tb-comment-text::placeholder { color: rgba(253, 230, 138, 0.45); }
.tb-node--audio { gap: 8px; padding: 10px 12px; }
.tb-node--audio.is-voice { border-color: rgba(129,140,248,0.4); }
.tb-node--audio.is-music { border-color: rgba(232,121,249,0.4); }
.tb-node--audio.is-sfx { border-color: rgba(34,211,238,0.4); }
.tb-node--audio.is-muted .tb-audio-body { opacity: 0.45; }
.tb-audio-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; }
.tb-audio-title { display: inline-flex; align-items: center; gap: 6px; min-width: 0; font-size: 0.74rem; font-weight: 700; color: #f5f3ff; }
.tb-audio-title span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-node--audio.is-voice .tb-audio-title svg { color: #a5b4fc; }
.tb-node--audio.is-music .tb-audio-title svg { color: #f0abfc; }
.tb-node--audio.is-sfx .tb-audio-title svg { color: #67e8f9; }
.tb-audio-body { height: 34px; display: flex; align-items: center; }
.tb-audio-body audio { width: 100%; height: 32px; }
.tb-audio-status {
  display: inline-flex; align-items: center; gap: 6px; width: 100%; min-width: 0;
  font-size: 0.68rem; font-weight: 600; color: #c7d2fe;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.tb-audio-status.is-action {
  border: 1px dashed rgba(129,140,248,0.5); border-radius: 8px; background: rgba(99,102,241,0.08);
  padding: 7px 8px; cursor: pointer; font-family: inherit;
}
.tb-audio-status.is-error { border-color: rgba(248,113,113,0.5); color: #fca5a5; background: rgba(248,113,113,0.06); }
.tb-audio-controls { display: flex; align-items: center; gap: 6px; }
.tb-audio-controls button {
  width: 24px; height: 24px; flex: 0 0 auto; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 7px; border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.05);
  color: #e0e7ff; cursor: pointer;
}
.tb-audio-controls button:disabled { opacity: 0.4; cursor: default; }
.tb-audio-controls input[type="range"] { flex: 1 1 auto; min-width: 0; accent-color: #a78bfa; }
.tb-audio-vol { width: 34px; text-align: right; font-size: 0.64rem; font-variant-numeric: tabular-nums; color: var(--text-muted); }
.tb-audio-modal textarea.tb-audio-text {
  width: 100%; box-sizing: border-box; resize: vertical;
  border-radius: 10px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.28);
  color: #fff; font-size: 0.8rem; line-height: 1.45; padding: 10px; font-family: inherit;
}
.tb-audio-modal select {
  width: 100%; border-radius: 8px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.28);
  color: #fff; font-size: 0.78rem; padding: 8px 10px; font-family: inherit;
}
.tb-audio-row { display: block; margin-bottom: 10px; }
.tb-audio-check { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 0.74rem; color: rgba(255,255,255,0.75); cursor: pointer; }
.tb-audio-warn { margin: 6px 0 0; font-size: 0.72rem; color: #fca5a5; }
.tb-edge.is-selected .tb-edge-line { stroke: #c7d2fe; stroke-width: 3.5; }
.tb-edge-line.is-draft { stroke: #a5b4fc; stroke-dasharray: 4 4; }
.tb-edge.is-cut .tb-edge-line { stroke: #fbbf24; stroke-dasharray: 7 6; }
.tb-edge-tag {
  position: absolute;
  transform: translate(-50%, -50%);
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid rgba(129,140,248,0.45);
  background: rgba(17,18,40,0.92);
  color: #c7d2fe;
  font-size: 10px; font-weight: 600; letter-spacing: 0.02em;
  white-space: nowrap;
  pointer-events: none;
  z-index: 3;
}
.tb-edge-tag.is-cut { border-color: rgba(251,191,36,0.55); color: #fcd34d; background: rgba(38,28,8,0.92); }
.tb-edge-tag.is-fade { border-color: rgba(52,211,153,0.55); color: #6ee7b7; background: rgba(6,32,24,0.92); }
.tb-node-fx {
  position: absolute; top: 6px; left: 6px; right: 6px; z-index: 1;
  display: flex; flex-wrap: wrap; gap: 4px; pointer-events: none;
}
.tb-node-fx-chip {
  display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px; border-radius: 999px;
  font-size: 0.6rem; font-weight: 700; white-space: nowrap; pointer-events: auto;
  color: #6ee7b7; background: rgba(6,32,24,0.88); border: 1px solid rgba(52,211,153,0.5);
}
.tb-edge-x {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 22px; height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(248,113,113,0.5);
  background: rgba(20,10,12,0.92);
  color: #fca5a5;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; padding: 0; z-index: 4;
}
.tb-node {
  position: absolute;
  box-sizing: border-box;
  padding: 12px;
  border-radius: 16px;
  background: rgba(24,26,33,0.96);
  border: 1.5px solid rgba(255,255,255,0.08);
  box-shadow: 0 18px 40px rgba(0,0,0,0.45);
  cursor: grab;
  touch-action: none;
  user-select: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tb-viewport.is-moving, .tb-viewport.is-moving .tb-node { cursor: grabbing; }
.tb-node.is-selected { border-color: #818cf8; box-shadow: 0 0 0 4px rgba(99,102,241,0.18), 0 18px 40px rgba(0,0,0,0.45); }
.tb-node.tb-node--comment.is-selected {
  border-color: #fbbf24;
  box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16), 0 18px 40px rgba(0,0,0,0.45);
}
.tb-node.is-link-target:hover { border-color: #34d399; }
.tb-node.is-pending .tb-node-media { border: 1.5px dashed rgba(129,140,248,0.45); background: rgba(99,102,241,0.06); }
.tb-node-media {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
  border-radius: 10px;
  overflow: hidden;
  background: #171a21;
  display: flex; align-items: center; justify-content: center;
}
.tb-node-media img { width: 100%; height: 100%; object-fit: contain; pointer-events: none; }
.tb-node-media--video {
  flex: 0 0 auto;
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
}
.tb-node-media--video video { width: 100%; height: 100%; object-fit: contain; background: #000; cursor: pointer; }
.tb-node-placeholder, .tb-node-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  padding: 8px; text-align: center; font-size: 0.7rem; font-weight: 600; color: #a5b4fc;
}
.tb-node-placeholder small { font-size: 0.62rem; font-weight: 500; color: var(--text-muted); }
.tb-node-loading {
  position: absolute; inset: 0; z-index: 3; color: #c7d2fe;
  background:
    radial-gradient(circle at 50% 42%, rgba(99,102,241,0.28), transparent 58%),
    rgba(8,8,14,0.82);
}
.tb-gen-stage > .tb-node-loading {
  z-index: 5;
  background: rgba(8, 8, 14, 0.82);
}
.tb-gen-stage > .tb-node-loading.is-over-frames {
  background: rgba(8, 8, 14, 0.82);
}
.tb-gen-stage > .tb-node-loading .tb-pixel-label {
  text-shadow: 0 1px 4px rgba(0,0,0,0.7);
}
.tb-pixel { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.tb-pixel-grid {
  display: grid; grid-template-columns: repeat(6, 7px); gap: 3px;
}
.tb-pixel.is-compact .tb-pixel-grid { grid-template-columns: repeat(6, 5px); gap: 2px; }
.tb-pixel-grid i {
  width: 7px; height: 7px; border-radius: 1px;
  background: #6366f1; opacity: 0.2;
  animation: tb-pixel-pop 1.15s ease-in-out infinite;
}
.tb-pixel.is-compact .tb-pixel-grid i { width: 5px; height: 5px; }
.tb-pixel-grid i:nth-child(3n) { background: #22d3ee; }
.tb-pixel-grid i:nth-child(4n) { background: #e0e7ff; }
.tb-pixel-label { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.01em; color: #e0e7ff; }
@keyframes tb-pixel-pop {
  0%, 100% { opacity: 0.18; transform: scale(0.55); }
  45% { opacity: 1; transform: scale(1); box-shadow: 0 0 6px currentColor; }
}
.tb-gen-lock { display: inline-flex; cursor: help; }
.tb-mode-ask { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.tb-mode-ask p { margin: 0; font-size: 0.74rem; line-height: 1.45; color: #e5e7eb; }
.tb-mode-ask-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.tb-mode-ask-actions button {
  border: 1px solid rgba(129,140,248,0.45); border-radius: 10px; padding: 7px 12px;
  background: rgba(255,255,255,0.04); color: #e5e7eb; font: inherit; font-size: 0.72rem; font-weight: 700;
  cursor: pointer;
}
.tb-mode-ask-actions button:first-child { background: rgba(99,102,241,0.22); color: #e0e7ff; }
.tb-mode-ask-actions button.is-on { background: rgba(99,102,241,0.4); border-color: #818cf8; color: #fff; }
.tb-mode-ask-actions button:disabled { cursor: default; opacity: 0.55; }
.tb-mode-ask-actions button.is-on:disabled { opacity: 1; }
.tb-node-meta { display: flex; align-items: center; gap: 8px; min-width: 0; }
.tb-node-index {
  flex: 0 0 auto;
  min-width: 22px; height: 22px; padding: 0 6px; box-sizing: border-box;
  border-radius: 999px; background: rgba(99,102,241,0.3); color: #e0e7ff;
  font-size: 0.66rem; font-weight: 800; display: inline-flex; align-items: center; justify-content: center;
}
.tb-node-label { font-size: 0.76rem; font-weight: 600; color: #f3f4f6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tb-node--video {
  background: linear-gradient(180deg, rgba(34,30,58,0.96), rgba(22,24,32,0.96));
  border-color: rgba(139,92,246,0.55);
}
.tb-node--video .tb-node-media { background: #12141b; }
.tb-node--gen {
  padding: 0;
  gap: 0;
  background: #0c0c0e;
  border-color: rgba(255,255,255,0.16);
  border-radius: 22px;
}
.tb-gen-head {
  position: absolute; top: 8px; left: 10px; right: 48px; z-index: 4;
  display: flex; align-items: center; gap: 8px; min-width: 0;
  pointer-events: none;
}
.tb-gen-head .tb-video-download,
.tb-gen-head .tb-video-versions { pointer-events: auto; }
.tb-gen-title {
  display: inline-flex; align-items: center; gap: 6px; min-width: 0;
  max-width: 100%;
  padding: 3px 8px; border-radius: 999px;
  font-size: 0.78rem; font-weight: 650; color: #fff;
  background: rgba(8,8,10,0.78); border: 1px solid rgba(255,255,255,0.28);
  backdrop-filter: blur(8px);
}
.tb-gen-title svg { flex: 0 0 auto; color: #fff; }
.tb-gen-title > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-gen-head .tb-video-download,
.tb-gen-head .tb-video-versions { margin-left: auto; }
.tb-gen-head .tb-video-download + .tb-video-versions { margin-left: 0; }
.tb-gen-stage {
  position: absolute; inset: 0; z-index: 0; width: auto; min-height: 0;
  border-radius: 22px; overflow: hidden;
  background: #1a1a1a; display: flex; align-items: center; justify-content: center;
}
.tb-gen-stage > video {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; background: #000;
}
.tb-gen-expand {
  position: absolute; left: 50%; top: 50%; z-index: 6;
  width: 42px; height: 42px; padding: 0; border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.4);
  background: rgba(8,8,10,0.72); color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; opacity: 0; pointer-events: none;
  transform-origin: center; backdrop-filter: blur(8px);
  transition: opacity 0.12s ease;
}
.tb-node--gen:hover .tb-gen-expand,
.tb-node--gen:focus-within .tb-gen-expand {
  opacity: 1; pointer-events: auto;
}
.tb-gen-expand:hover { background: rgba(8,8,10,0.9); }
.tb-gen-crossfade { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
.tb-gen-crossfade img {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; opacity: 0; z-index: 0;
}
.tb-gen-stage > .tb-node-placeholder {
  position: absolute; inset: 0; z-index: 1; pointer-events: none;
  padding: 72px 52px 150px 16px;
}
.tb-gen-top {
  position: absolute; top: 40px; left: 10px; right: 46px; z-index: 2;
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px; pointer-events: none;
}
.tb-gen-chip {
  padding: 3px 8px; border-radius: 999px; font-size: 0.62rem; font-weight: 650; color: #fff;
  background: rgba(8,8,10,0.78); border: 1px solid rgba(255,255,255,0.28);
  backdrop-filter: blur(8px);
}
.tb-gen-chip.is-bad { color: #fecaca; border-color: rgba(248,113,113,0.7); background: rgba(40,8,10,0.82); }
.tb-gen-rail {
  position: absolute; top: 8px; right: 8px; z-index: 5;
  display: flex; flex-direction: column; gap: 6px;
}
.tb-gen-rail button {
  width: 30px; height: 30px; border-radius: 10px; padding: 0; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; background: rgba(8,8,10,0.78); border: 1px solid rgba(255,255,255,0.32);
  backdrop-filter: blur(8px);
}
.tb-gen-rail button.is-on { color: #fff; border-color: #93c5fd; background: rgba(29,78,216,0.88); }
.tb-gen-rail button:disabled { opacity: 0.45; cursor: default; }
.tb-gen-panel {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  display: flex; flex-direction: column; align-items: stretch; gap: 10px;
  min-width: 0;
  padding: 36px 12px 14px;
  border-radius: 0 0 22px 22px;
  background: linear-gradient(180deg, rgba(8,8,10,0) 0%, rgba(8,8,10,0.8) 18%, rgba(8,8,10,0.94) 42%, rgba(8,8,10,0.97) 100%);
}
.tb-gen-panel-top {
  display: flex; flex-direction: row; align-items: flex-end; gap: 8px; min-width: 0;
}
.tb-gen-main {
  flex: 1 1 auto; min-width: 0;
  display: flex; flex-direction: column; gap: 6px;
  padding: 4px 8px; border-radius: 10px;
  background: rgba(8,8,10,0.78); border: 1px solid rgba(255,255,255,0.32);
  backdrop-filter: blur(8px);
}
.tb-gen-controls-stack {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
  padding-right: 46px;
}
.tb-gen-thumbs { display: flex; gap: 4px; overflow: hidden; }
.tb-gen-thumb {
  position: relative; flex: 0 0 auto; width: 36px; height: 28px; border-radius: 6px; overflow: hidden;
  border: 1px solid rgba(255,255,255,0.2);
}
.tb-gen-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tb-gen-thumb em {
  position: absolute; left: 2px; bottom: 1px; font-style: normal; font-size: 0.5rem; font-weight: 700; color: #fff;
  text-shadow: 0 1px 2px #000;
}
.tb-gen-thumb button, .tb-gen-files button {
  position: absolute; top: 1px; right: 1px; width: 12px; height: 12px; padding: 0; border: none; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  background: rgba(0,0,0,0.7); color: #fff;
}
.tb-gen-files { display: flex; flex-wrap: wrap; gap: 4px; }
.tb-gen-files > span {
  position: relative; display: inline-flex; align-items: center; gap: 4px; max-width: 140px;
  padding: 2px 16px 2px 6px; border-radius: 999px; font-size: 0.6rem; color: #e5e7eb;
  background: rgba(255,255,255,0.08); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.tb-gen-panel textarea {
  width: 100%; box-sizing: border-box; resize: none; border: none; outline: none;
  background: transparent; color: #fff; font: inherit; font-size: 0.72rem; line-height: 1.35;
  padding: 0; max-height: 5.4em; overflow-y: auto; scrollbar-width: thin;
  text-shadow: 0 1px 2px rgba(0,0,0,0.9);
}
.tb-gen-panel textarea::placeholder { color: rgba(255,255,255,0.86); }
.tb-gen-select {
  display: inline-flex; align-items: center; gap: 3px; max-width: 118px; height: 24px; padding: 0 5px 0 7px;
  border-radius: 8px; cursor: pointer; border: 1px solid rgba(255,255,255,0.42); background: rgba(8,8,10,0.82);
  color: #fff; font: inherit; font-size: 0.62rem; font-weight: 650; text-align: left;
  backdrop-filter: blur(8px);
}
.tb-gen-select.is-wide { max-width: 160px; }
.tb-gen-select > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-gen-select > svg { flex: 0 0 auto; opacity: 0.75; }
.tb-gen-select:hover:not(:disabled), .tb-gen-select.is-open { background: rgba(255,255,255,0.14); border-color: rgba(255,255,255,0.26); }
.tb-gen-select:disabled { opacity: 0.45; cursor: default; }
.tb-gen-lock .tb-gen-select {
  opacity: 0.55; border-style: dashed; color: rgba(243,244,246,0.55); cursor: help;
}
.tb-gen-lock .tb-gen-select:disabled { pointer-events: none; opacity: 0.72; }
.tb-gen-lock .tb-gen-select > svg { display: none; }
.tb-gen-menu {
  position: fixed; z-index: 1200; display: flex; flex-direction: column; gap: 1px;
  max-height: 260px; overflow-y: auto; scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.22) transparent; padding: 4px;
  border-radius: 10px; border: 1px solid rgba(255,255,255,0.14); background: #161618;
  box-shadow: 0 12px 32px rgba(0,0,0,0.55);
}
.tb-gen-menu > button {
  display: block; width: 100%; text-align: left; padding: 6px 10px; border: none; border-radius: 7px; cursor: pointer;
  background: transparent; color: #e5e7eb; font: inherit; font-size: 0.72rem; white-space: nowrap;
}
.tb-gen-menu > button:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-gen-menu > button.is-on { background: #2563eb; color: #fff; }
.tb-gen-switch {
  height: 24px; padding: 0 8px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(255,255,255,0.42); background: rgba(8,8,10,0.82); color: #fff;
  font: inherit; font-size: 0.62rem; font-weight: 650;
  backdrop-filter: blur(8px);
}
.tb-gen-switch.is-on { background: #2563eb; border-color: #3b82f6; color: #fff; }
.tb-gen-switch:disabled { opacity: 0.45; cursor: default; }
.tb-gen-download {
  position: static; pointer-events: auto;
  display: inline-flex; align-items: center; gap: 4px;
  height: 22px; padding: 0 8px; border-radius: 999px; border: none; cursor: pointer;
  background: #f4f4f5; color: #111; font: inherit; font-size: 0.62rem; font-weight: 700;
}
.tb-gen-download:hover { background: #fff; }
.tb-gen-go {
  position: absolute; right: 8px; bottom: 8px; z-index: 6;
  width: 36px; height: 36px; border-radius: 999px; padding: 0; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; background: #f4f4f5; color: #111;
  box-shadow: 0 6px 16px rgba(0,0,0,0.45);
}
.tb-gen-go:disabled { opacity: 0.4; cursor: default; }
.tb-video-foot { display: flex; align-items: center; gap: 8px; min-width: 0; }
.tb-video-title { display: inline-flex; align-items: center; gap: 6px; font-size: 0.8rem; font-weight: 700; color: #f3f4f6; }
.tb-video-title svg { flex: 0 0 auto; color: #a78bfa; }
.tb-video-title { min-width: 0; }
.tb-video-title > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-video-info { margin-left: auto; font-size: 0.64rem; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tb-handle {
  position: absolute; top: 50%;
  width: 16px; height: 16px; margin-top: -8px;
  border-radius: 999px; box-sizing: border-box;
  background: #0f1115; border: 2.5px solid rgba(129,140,248,0.9);
}
.tb-handle--in { left: -9px; pointer-events: none; }
.tb-handle--in.is-target { border-color: #34d399; background: #064e3b; pointer-events: auto; }
.tb-handle--in.is-reference { border-color: #22d3ee; }
.tb-input-mode {
  position: absolute; top: 50%; left: 12px; transform: translateY(-50%); z-index: 6;
  display: flex; flex-direction: column; gap: 4px;
}
.tb-input-mode button {
  border: 1px solid rgba(255,255,255,0.55); background: rgba(8,8,10,0.86); color: #fff;
  font: inherit; font-size: 0.58rem; font-weight: 700; border-radius: 999px;
  padding: 3px 8px; cursor: pointer; white-space: nowrap; text-align: left;
  backdrop-filter: blur(8px);
}
.tb-input-mode button.is-on { color: #fff; border-color: #818cf8; background: #4f46e5; }
.tb-input-mode button.is-on.is-reference { border-color: #22d3ee; background: #0e7490; }
.tb-input-mode button:disabled { opacity: 0.45; cursor: default; }
.tb-input-mode.tb-input-mode--in-panel {
  position: static; top: auto; left: auto; transform: none; z-index: auto;
  flex-direction: row; flex-wrap: wrap; gap: 8px;
  align-self: flex-start;
}
.tb-handle--out { right: -9px; cursor: crosshair; }
.tb-handle--out:hover { background: #6366f1; transform: scale(1.2); }
.tb-node-remove {
  position: absolute; top: -10px; right: -10px;
  width: 22px; height: 22px; border-radius: 999px; padding: 0;
  border: 1px solid rgba(255,255,255,0.16); background: #22252e; color: #e5e7eb;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.4);
}
.tb-node-resize {
  position: absolute; right: -8px; bottom: -8px; z-index: 8;
  width: 16px; height: 16px; padding: 0; border-radius: 4px;
  border: 1.5px solid #818cf8; background: #fff; cursor: nwse-resize;
  transform-origin: bottom right;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.tb-node-resize::before {
  content: '';
  position: absolute; right: 3px; bottom: 3px;
  width: 6px; height: 6px;
  border-right: 1.5px solid #4f46e5; border-bottom: 1.5px solid #4f46e5;
}
.tb-node-resize:hover { background: #e0e7ff; }
.tb-empty {
  position: absolute; left: 50%; top: 26%; transform: translate(-50%, -50%);
  display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center;
  color: var(--text-muted); pointer-events: none; max-width: 22rem;
}
.tb-empty strong { color: #e5e7eb; font-size: 0.95rem; font-weight: 600; }
.tb-empty span { font-size: 0.78rem; line-height: 1.45; }
.tb-empty-btn {
  pointer-events: auto; cursor: pointer;
  padding: 0.45rem 1rem; border-radius: 999px;
  font: inherit; font-size: 0.8rem; font-weight: 600; color: #c7d2fe;
  background: rgba(99,102,241,0.14); border: 1px solid rgba(129,140,248,0.45);
}
.tb-empty-btn:hover { background: rgba(99,102,241,0.28); color: #fff; }
.tb-zoom-bar {
  position: absolute; left: 84px; bottom: 12px; z-index: 8;
  display: flex; align-items: center; gap: 2px; padding: 4px;
  border-radius: 10px; background: rgba(14,15,20,0.92); border: 1px solid rgba(255,255,255,0.1);
}
.tb-zoom-btn {
  width: 28px; height: 28px; border: none; border-radius: 7px; background: transparent; color: #e5e7eb;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0;
}
.tb-zoom-btn:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
.tb-zoom-btn:disabled { opacity: 0.3; cursor: default; }
.tb-zoom-sep { width: 1px; height: 16px; margin: 0 3px; background: rgba(255,255,255,0.12); }
.tb-zoom-label { min-width: 40px; text-align: center; font-size: 0.66rem; color: var(--text-muted); font-variant-numeric: tabular-nums; }

.tb-prompt {
  position: absolute; left: 50%; bottom: 20px; transform: translateX(-50%);
  width: min(760px, calc(100% - 32px)); z-index: 10;
  display: flex; flex-direction: column; gap: 10px;
  pointer-events: none;
  box-sizing: border-box; border: 1px solid transparent; border-radius: 22px; background: transparent;
  transition: left 0.45s cubic-bezier(.2,.8,.2,1), transform 0.45s cubic-bezier(.2,.8,.2,1), width 0.45s cubic-bezier(.2,.8,.2,1),
    padding 0.45s cubic-bezier(.2,.8,.2,1), background-color 0.3s ease, border-color 0.3s ease;
}
.tb-prompt > * { pointer-events: auto; }
/* While centered, stay clear of the expanded panels on the left. */
.tb-page.has-tray .tb-prompt:not(.is-docked) { width: min(760px, max(380px, calc(100% - 820px))); }
/* Centered only until the first message; then the conversation lives on the right edge. */
.tb-prompt.is-docked {
  left: calc(100% - var(--tb-chat-w, 440px)); transform: none;
  top: 0; bottom: 0;
  width: var(--tb-chat-w, 440px);
  pointer-events: auto; padding: 10px 10px 12px; gap: 8px;
  background: #15171e; border: 1px solid rgba(255,255,255,0.09); border-right: none; border-radius: 0;
  box-shadow: -16px 0 48px rgba(0,0,0,0.45);
}
.tb-prompt.is-docked .tb-thread-wrap { flex: 1; min-height: 0; }
.tb-prompt.is-docked .tb-thread { flex: 1; max-height: none; padding: 4px 4px 6px; mask-image: none; -webkit-mask-image: none; }
/* Top-right controls slide left while the conversation panel takes the right edge. */
body.tb-chat-docked .tb-top-actions { right: calc(104px + var(--tb-chat-w, 440px)); }
body.tb-chat-docked .floating-user-menu { right: calc(14px + var(--tb-chat-w, 440px)); }
/* The board sits beside the conversation, never underneath it. */
body.tb-chat-docked .tb-viewport { right: var(--tb-chat-w, 440px); }
body.tb-no-anim .tb-viewport, body.tb-no-anim .tb-prompt, body.tb-no-anim .tb-top-actions, body.tb-no-anim .floating-user-menu,
body.tb-chat-dragging .tb-viewport, body.tb-chat-dragging .tb-prompt, body.tb-chat-dragging .tb-top-actions, body.tb-chat-dragging .floating-user-menu { transition: none !important; }
.tb-chat-resize {
  position: absolute; top: 0; bottom: 0; left: -3px; width: 6px; z-index: 3;
  cursor: col-resize; touch-action: none;
}
.tb-chat-resize:hover, body.tb-chat-dragging .tb-chat-resize { background: rgba(129,140,248,0.55); }
.tb-prompt.is-docked .tb-bubble { max-width: 92%; box-shadow: none; }
.tb-prompt.is-docked .tb-bubble--assistant { background: rgba(255,255,255,0.05); }
.tb-prompt.is-docked .tb-composer { box-shadow: none; }
.tb-prompt.is-docked .tb-composer:focus-within { box-shadow: 0 0 0 4px rgba(99,102,241,0.16); }
.tb-thread-wrap { position: relative; display: flex; flex-direction: column; }
.tb-chat-head {
  display: flex; align-items: center; gap: 4px; padding: 2px 2px 6px 6px; min-width: 0;
  border-bottom: 1px solid rgba(255,255,255,0.07); margin-bottom: 4px;
}
.tb-chat-title {
  display: inline-flex; align-items: center; gap: 6px; margin-right: auto;
  font-size: 0.7rem; font-weight: 600; letter-spacing: 0.02em; color: rgba(255,255,255,0.7);
}
.tb-chat-head-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 8px; padding: 0; cursor: pointer;
  border: none; background: transparent; color: #9ca3af;
}
.tb-chat-head-btn:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-chat-reopen-row {
  position: absolute; top: -36px; right: 0;
  display: flex; align-items: center; gap: 6px;
}
.tb-chat-reopen {
  display: inline-flex; align-items: center; gap: 6px; padding: 0 10px; height: 28px; cursor: pointer;
  border-radius: 999px; font-size: 0.68rem; font-weight: 600;
  border: 1px solid rgba(255,255,255,0.1); background: rgba(24,26,33,0.96); color: #d1d5db;
}
.tb-chat-reopen:hover:not(:disabled) { border-color: rgba(129,140,248,0.6); color: #fff; }
.tb-chat-reopen:disabled { opacity: 0.4; cursor: default; }
.tb-chat-tabs {
  display: flex; align-items: center; gap: 4px; min-width: 0; flex: 1;
  overflow-x: auto; scrollbar-width: none;
}
.tb-chat-tabs::-webkit-scrollbar { display: none; }
.tb-chat-tab {
  flex: 0 0 auto; max-width: 148px;
  display: inline-flex; align-items: center; gap: 2px;
  height: 26px; padding: 0 2px 0 2px; border-radius: 8px;
  border: 1px solid transparent; background: transparent;
}
.tb-chat-tab.is-active { background: rgba(129,140,248,0.16); border-color: rgba(129,140,248,0.4); }
.tb-chat-tab-label {
  max-width: 108px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  height: 22px; padding: 0 6px; border: none; border-radius: 6px; cursor: pointer;
  background: transparent; color: rgba(255,255,255,0.55); font-size: 0.68rem; font-weight: 600;
}
.tb-chat-tab.is-active .tb-chat-tab-label { color: #fff; }
.tb-chat-tab-label:hover:not(:disabled) { color: #fff; }
.tb-chat-tab-label:disabled { cursor: default; }
.tb-chat-tab-x {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; margin-right: 2px; padding: 0; border: none; border-radius: 4px;
  background: transparent; color: rgba(255,255,255,0.4); cursor: pointer;
}
.tb-chat-tab-x:hover:not(:disabled) { background: rgba(255,255,255,0.1); color: #fff; }
.tb-chat-tab-x:disabled { cursor: default; opacity: 0.4; }
.tb-chat-new {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 4px;
  height: 26px; padding: 0 8px; border-radius: 8px; cursor: pointer;
  border: 1px solid rgba(129,140,248,0.4);
  background: rgba(99,102,241,0.16); color: #c7d2fe;
  font-size: 0.68rem; font-weight: 600;
}
.tb-chat-new:hover:not(:disabled) { background: rgba(99,102,241,0.28); color: #fff; }
.tb-chat-new:disabled { opacity: 0.4; cursor: default; }
.tb-thread-empty {
  margin: 28px 8px; text-align: center;
  font-size: 0.75rem; line-height: 1.45; color: rgba(255,255,255,0.45);
}
.tb-thread {
  max-height: 50vh; overflow-y: auto; padding: 16px 2px 4px; background: transparent;
  display: flex; flex-direction: column; gap: 10px;
  scrollbar-width: none;
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 24px);
  mask-image: linear-gradient(to bottom, transparent 0, #000 24px);
}
.tb-thread::-webkit-scrollbar { display: none; }
.tb-bubble {
  max-width: 82%; padding: 11px 15px; box-sizing: border-box;
  font-size: 0.8rem; line-height: 1.45; color: #f3f4f6;
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.4);
  animation: tb-bubble-in 0.22s ease-out;
}
.tb-bubble--user {
  align-self: flex-end;
  background: rgba(39,41,83,0.97); border: 1px solid rgba(129,140,248,0.45);
}
.tb-bubble--assistant {
  align-self: flex-start;
  background: rgba(24,26,33,0.96); border: 1px solid rgba(255,255,255,0.08);
}
@keyframes tb-bubble-in { from { opacity: 0; transform: translateY(8px); } }
.tb-bubble.is-error { border-color: rgba(248,113,113,0.4); color: #fecaca; }
.tb-bubble--typing { display: inline-flex; align-items: center; gap: 6px; color: var(--text-muted); }
.tb-bubble-text { white-space: pre-wrap; }
.tb-bubble-audio { display: inline-flex; align-items: center; gap: 4px; margin-bottom: 4px; font-size: 0.6rem; color: rgba(255,255,255,0.65); }
.tb-bubble-status { margin-top: 4px; font-size: 0.62rem; color: #a5b4fc; }
.tb-script-loading {
  display: flex; align-items: center; gap: 8px; margin-top: 8px;
  font-size: 0.72rem; font-weight: 600; color: #c7d2fe;
}
.tb-script-spin {
  position: relative; width: 16px; height: 16px; flex: 0 0 auto;
}
.tb-script-spin i {
  position: absolute; left: 50%; top: 50%; width: 4px; height: 4px; margin: -2px 0 0 -2px;
  border-radius: 999px; background: #a5b4fc;
  animation: tb-script-orbit 0.9s linear infinite;
}
.tb-script-spin i:nth-child(1) { animation-delay: -0.6s; background: #818cf8; }
.tb-script-spin i:nth-child(2) { animation-delay: -0.3s; background: #c4b5fd; }
.tb-script-spin i:nth-child(3) { background: #e0e7ff; }
@keyframes tb-script-orbit {
  0% { transform: rotate(0deg) translateX(6px) scale(0.7); opacity: 0.45; }
  50% { transform: rotate(180deg) translateX(6px) scale(1); opacity: 1; }
  100% { transform: rotate(360deg) translateX(6px) scale(0.7); opacity: 0.45; }
}
.tb-debit-reopen {
  display: flex; width: fit-content; align-items: center; margin-top: 10px;
  border: 1px solid rgba(129,140,248,0.45);
  border-radius: 10px; padding: 7px 12px; background: rgba(99,102,241,0.16); color: #e0e7ff;
  font-size: 0.72rem; font-weight: 700; font-family: inherit; cursor: pointer;
}
.tb-debit-reopen:hover:not(:disabled) { background: rgba(99,102,241,0.32); color: #fff; }
.tb-debit-reopen:disabled { opacity: 0.45; cursor: default; border-color: rgba(255,255,255,0.08); color: rgba(255,255,255,0.45); background: transparent; }
.tb-bubble-chips { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; }
.tb-ask { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.tb-ask-label { font-size: 0.64rem; font-weight: 600; letter-spacing: 0.02em; color: rgba(255,255,255,0.5); margin-bottom: 6px; }
.tb-ask-options { display: flex; flex-wrap: wrap; gap: 6px; }
.tb-ask-option {
  border: 1.5px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: #e5e7eb;
  border-radius: 10px; padding: 6px 12px; font-size: 0.72rem; font-weight: 600; font-family: inherit; cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.tb-ask-option:hover:not(:disabled) { border-color: rgba(129,140,248,0.5); }
.tb-ask-option.is-active { background: rgba(99,102,241,0.3); border-color: #818cf8; color: #fff; }
.tb-ask-option:disabled { cursor: default; opacity: 0.6; }
.tb-ask-option.is-active:disabled { opacity: 1; }
.tb-ask-rec {
  margin-left: 6px; padding: 1px 6px; border-radius: 999px; vertical-align: 1px;
  font-size: 0.56rem; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
  color: #6ee7b7; background: rgba(16,185,129,0.14); border: 1px solid rgba(16,185,129,0.35);
}
.tb-ask-note {
  padding: 8px 10px; border-radius: 10px; font-size: 0.7rem; line-height: 1.45;
  color: #d1fae5; background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.25);
}
.tb-ask-confirm {
  align-self: flex-start; border: none; border-radius: 10px; padding: 8px 18px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff;
  font-size: 0.74rem; font-weight: 700; font-family: inherit; cursor: pointer;
  box-shadow: 0 8px 20px rgba(99,102,241,0.35);
}
.tb-ask-confirm:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
.tb-ask-blocked {
  padding: 8px 10px; border-radius: 10px; font-size: 0.7rem; line-height: 1.4;
  color: #fde68a; background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.3);
}
.tb-suggestions { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
.tb-refs {
  display: flex; flex-wrap: wrap; align-items: center; gap: 4px;
  margin-bottom: -6px; padding: 0 14px;
}
.tb-refs-title {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 0.62rem; color: rgba(255,255,255,0.45);
}
.tb-ref-chip {
  display: inline-flex; align-items: center; gap: 4px; max-width: 150px; padding: 1px 2px 1px 1px;
  border-radius: 999px; background: rgba(24,26,33,0.85); border: 1px solid rgba(255,255,255,0.08);
  font-size: 0.62rem; color: rgba(255,255,255,0.7);
}
.tb-ref-chip img, .tb-ref-icon {
  flex: 0 0 auto; width: 16px; height: 16px; border-radius: 999px; object-fit: cover;
  display: inline-flex; align-items: center; justify-content: center; background: #12141b;
}
.tb-ref-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-ref-chip button, .tb-refs-clear {
  display: inline-flex; align-items: center; justify-content: center; border: none; cursor: pointer;
  background: transparent; color: rgba(255,255,255,0.35); font-family: inherit;
}
.tb-ref-chip button { width: 14px; height: 14px; padding: 0; border-radius: 999px; }
.tb-ref-chip button:hover, .tb-refs-clear:hover { color: rgba(255,255,255,0.85); }
.tb-refs-clear { padding: 0 4px; font-size: 0.6rem; }
.tb-bubble-refs {
  display: inline-flex; align-items: center; gap: 4px; margin-bottom: 4px;
  font-size: 0.6rem; color: rgba(255,255,255,0.45);
}
.tb-suggestion {
  border: 1px solid rgba(255,255,255,0.1); background: rgba(24,26,33,0.92); color: #d1d5db;
  border-radius: 999px; padding: 7px 14px; font-size: 0.74rem; font-family: inherit; cursor: pointer;
  box-shadow: 0 8px 20px rgba(0,0,0,0.3);
}
.tb-suggestion:hover { border-color: rgba(129,140,248,0.55); color: #fff; }
.tb-bubble-suggestions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.tb-explore-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; align-items: center; }
.tb-explore-note {
  flex: 1 1 100%;
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(226, 232, 240, 0.78);
}
.tb-explore-skip {
  display: inline-flex; align-items: center; text-align: left;
  border: 1px solid rgba(255,255,255,0.12); background: transparent; color: rgba(255,255,255,0.72);
  border-radius: 999px; padding: 5px 11px; font-size: 0.7rem; font-family: inherit; cursor: pointer;
}
.tb-explore-skip:hover:not(:disabled) { border-color: rgba(255,255,255,0.28); color: #fff; }
.tb-explore-skip:disabled { opacity: 0.45; cursor: default; }
.tb-bubble-suggestion {
  display: inline-flex; align-items: center; gap: 5px; text-align: left;
  border: 1px solid rgba(129,140,248,0.35); background: rgba(99,102,241,0.1); color: #c7d2fe;
  border-radius: 999px; padding: 5px 11px; font-size: 0.7rem; font-family: inherit; cursor: pointer;
}
.tb-bubble-suggestion:hover:not(:disabled) { border-color: rgba(129,140,248,0.75); background: rgba(99,102,241,0.2); color: #fff; }
.tb-bubble-suggestion:disabled { opacity: 0.45; cursor: default; }
.tb-composer {
  display: flex; align-items: flex-end; gap: 6px; padding: 8px 8px 8px 10px;
  border-radius: 24px; background: rgba(24,26,33,0.97);
  border: 1.5px solid rgba(129,140,248,0.35);
  box-shadow: 0 20px 50px rgba(0,0,0,0.5), 0 0 0 4px rgba(99,102,241,0.06);
}
.tb-composer:focus-within { border-color: rgba(129,140,248,0.75); box-shadow: 0 20px 50px rgba(0,0,0,0.5), 0 0 0 4px rgba(99,102,241,0.16); }
.tb-composer.is-recording { border-color: rgba(248,113,113,0.6); }
.tb-composer textarea {
  flex: 1; min-width: 0; resize: none; border: none; outline: none; background: transparent;
  color: #fff; font-family: inherit; font-size: 0.9rem; line-height: 1.45; padding: 9px 4px; max-height: 120px;
  scrollbar-width: none;
}
.tb-composer textarea::-webkit-scrollbar { display: none; }
.tb-icon-btn, .tb-send-btn {
  flex: 0 0 auto; width: 40px; height: 40px; border: none; padding: 0;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.tb-icon-btn { border-radius: 999px; background: transparent; color: #d1d5db; }
.tb-icon-btn:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
.tb-composer > .tb-icon-btn:not(:first-child) { background: rgba(255,255,255,0.06); }
.tb-icon-btn.is-active { background: rgba(239,68,68,0.2) !important; color: #fca5a5; }
.tb-send-btn {
  border-radius: 14px; color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  box-shadow: 0 8px 20px rgba(99,102,241,0.35);
}
.tb-icon-btn:disabled, .tb-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.tb-recording { flex: 1; display: flex; align-items: center; gap: 8px; padding: 0 6px; height: 36px; font-size: 0.8rem; color: #fecaca; }
.tb-rec-dot { width: 9px; height: 9px; border-radius: 999px; background: #ef4444; animation: tb-pulse 1s ease-in-out infinite; }
.tb-rec-cancel { margin-left: auto; border: none; background: transparent; color: var(--text-muted); cursor: pointer; display: inline-flex; }
@keyframes tb-pulse { 50% { opacity: 0.35; } }
.tb-prompt-foot { display: flex; justify-content: center; }
.tb-toast-error {
  position: absolute; left: 50%; top: 16px; transform: translateX(-50%); z-index: 20; margin: 0;
  color: #fecaca; background: rgba(127,29,29,0.92); border: 1px solid rgba(248,113,113,0.4);
  padding: 0.45rem 0.75rem; border-radius: 8px; font-size: 0.75rem; max-width: min(90vw, 460px);
}
.tb-field-label { font-size: 0.68rem; color: var(--text-muted); margin-bottom: 4px; }
.tb-aspect-ask { margin-bottom: 0.85rem; }
.tb-aspect-note { font-size: 0.72rem; color: var(--text-muted); margin: 0 0 8px; }
.tb-aspect-options { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.tb-crop-list { display: flex; flex-direction: column; gap: 12px; }
.tb-crop-stage {
  position: relative; width: fit-content; max-width: 100%; margin: 0 auto; overflow: hidden;
  border-radius: 10px; touch-action: none; user-select: none; cursor: ew-resize;
}
.tb-crop-stage.is-vertical { cursor: ns-resize; }
.tb-crop-stage img { display: block; max-width: 100%; max-height: 260px; pointer-events: none; }
.tb-crop-frame {
  position: absolute; outline: 2px solid #fff; outline-offset: -1px; cursor: grab;
  box-shadow: 0 0 0 9999px rgba(8,9,12,0.62);
}
.tb-crop-stage.is-dragging .tb-crop-frame { cursor: grabbing; }
.tb-crop-frame:focus-visible { outline-color: #a5b4fc; }
.tb-crop-grid {
  position: absolute; inset: 0; pointer-events: none;
  background-image:
    linear-gradient(to right, transparent calc(33.33% - 0.5px), rgba(255,255,255,0.45) calc(33.33% - 0.5px), rgba(255,255,255,0.45) calc(33.33% + 0.5px), transparent calc(33.33% + 0.5px), transparent calc(66.66% - 0.5px), rgba(255,255,255,0.45) calc(66.66% - 0.5px), rgba(255,255,255,0.45) calc(66.66% + 0.5px), transparent calc(66.66% + 0.5px)),
    linear-gradient(to bottom, transparent calc(33.33% - 0.5px), rgba(255,255,255,0.45) calc(33.33% - 0.5px), rgba(255,255,255,0.45) calc(33.33% + 0.5px), transparent calc(33.33% + 0.5px), transparent calc(66.66% - 0.5px), rgba(255,255,255,0.45) calc(66.66% - 0.5px), rgba(255,255,255,0.45) calc(66.66% + 0.5px), transparent calc(66.66% + 0.5px));
}
.tb-crop-corner { position: absolute; width: 14px; height: 14px; border: 0 solid #fff; pointer-events: none; }
.tb-crop-corner.is-tl { left: -2px; top: -2px; border-left-width: 4px; border-top-width: 4px; }
.tb-crop-corner.is-tr { right: -2px; top: -2px; border-right-width: 4px; border-top-width: 4px; }
.tb-crop-corner.is-bl { left: -2px; bottom: -2px; border-left-width: 4px; border-bottom-width: 4px; }
.tb-crop-corner.is-br { right: -2px; bottom: -2px; border-right-width: 4px; border-bottom-width: 4px; }
.tb-crop-presets { display: flex; justify-content: center; gap: 4px; margin-top: 8px; }
.tb-crop-presets button {
  padding: 4px 10px; border-radius: 999px; font-size: 0.7rem; cursor: pointer;
  border: 1px solid rgba(255,255,255,0.12); background: rgba(24,26,33,0.92); color: #d1d5db;
}
.tb-crop-presets button.is-on { border-color: rgba(129,140,248,0.7); background: rgba(99,102,241,0.18); color: #fff; }
.tb-aspect-option {
  display: flex; flex-direction: column; align-items: flex-start; gap: 2px; text-align: left;
  padding: 8px 10px; border-radius: 10px; cursor: pointer;
  border: 1px solid rgba(255,255,255,0.1); background: rgba(24,26,33,0.92); color: #d1d5db;
}
.tb-aspect-option strong { font-size: 0.78rem; font-weight: 600; }
.tb-aspect-option span { font-size: 0.66rem; color: var(--text-muted); line-height: 1.3; }
.tb-aspect-option.is-on { background: rgba(99,102,241,0.14); border-color: rgba(129,140,248,0.45); color: #e5e7eb; }
.tb-aspect-option:focus-visible { outline: 2px solid rgba(129,140,248,0.8); outline-offset: 2px; }
.tb-side {
  position: absolute; left: 84px; bottom: 60px; z-index: 9;
  display: flex; flex-direction: column; align-items: flex-start; gap: 10px;
  max-height: calc(100% - 120px);
  pointer-events: none;
}
.tb-side > * { pointer-events: auto; }
.tb-tray {
  position: relative; flex-shrink: 0;
  width: 300px; box-sizing: border-box; padding: 14px;
  border-radius: 18px; background: rgba(24,26,33,0.95);
  border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  backdrop-filter: blur(10px);
  animation: tb-bubble-in 0.2s ease-out;
}
.tb-tray-head { display: flex; align-items: center; gap: 8px; }
.tb-tray-head strong { font-size: 0.86rem; color: #f3f4f6; white-space: nowrap; }
.tb-tray-tabs {
  margin-left: auto; display: inline-flex; padding: 2px; gap: 2px;
  border-radius: 9px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
}
.tb-tray-tabs button {
  padding: 3px 9px; border: none; border-radius: 7px; cursor: pointer;
  font: inherit; font-size: 0.66rem; font-weight: 600; color: var(--text-muted); background: transparent;
}
.tb-tray-tabs button.is-active { background: rgba(99,102,241,0.3); color: #fff; }
.tb-tray-hint { margin: 4px 0 10px; font-size: 0.64rem; color: var(--text-muted); }
.tb-tray-collapse {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; padding: 0; border: none; border-radius: 8px; cursor: pointer;
  background: transparent; color: var(--text-muted);
}
.tb-tray-collapse:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-tray-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.tb-tray-thumb {
  position: relative; aspect-ratio: 3 / 2; padding: 0; overflow: hidden; cursor: grab;
  border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: #171a21;
  transition: transform 0.12s, border-color 0.12s;
}
.tb-tray-thumb:hover { transform: translateY(-2px); border-color: rgba(129,140,248,0.7); }
.tb-tray-thumb:active { cursor: grabbing; }
.tb-tray-thumb img, .tb-tray-thumb video { display: block; width: 100%; height: 100%; object-fit: cover; pointer-events: none; }
.tb-tray-thumb.is-on-board img, .tb-tray-thumb.is-on-board video { opacity: 0.35; }
.tb-tray-badge {
  position: absolute; left: 5px; bottom: 5px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 6px; background: rgba(15,17,21,0.8); color: #c4b5fd;
}
.tb-tray-check {
  position: absolute; right: 5px; top: 5px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 999px; background: #34d399; color: #062e22;
}
.tb-tray-empty {
  display: flex; align-items: center; justify-content: center; min-height: 64px; padding: 4px;
  text-align: center; font-size: 0.72rem; line-height: 1.4; color: var(--text-muted);
}
.tb-tray-more {
  display: block; width: 100%; margin-top: 12px; padding: 7px 10px; cursor: pointer;
  border-radius: 10px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04);
  font: inherit; font-size: 0.72rem; font-weight: 600; color: #c7d2fe;
}
.tb-tray-more:hover { background: rgba(99,102,241,0.18); border-color: rgba(129,140,248,0.45); color: #fff; }
.tb-tray-pill {
  position: relative; flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; cursor: pointer;
  border-radius: 999px; border: 1px solid rgba(255,255,255,0.1); background: rgba(24,26,33,0.95);
  font: inherit; font-size: 0.76rem; font-weight: 600; color: #e5e7eb;
  box-shadow: 0 12px 30px rgba(0,0,0,0.45);
}
.tb-tray-pill:hover { border-color: rgba(129,140,248,0.55); color: #fff; }
.tb-tray.tb-proj { flex-shrink: 1; min-height: 0; display: flex; flex-direction: column; }
.tb-proj-count {
  display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px; background: rgba(99,102,241,0.22); color: #c7d2fe; font-size: 0.62rem; font-weight: 700;
}
.tb-proj-new {
  margin-left: auto; display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 9px; cursor: pointer;
  border: 1px solid rgba(129,140,248,0.45); background: linear-gradient(135deg, rgba(99,102,241,0.35), rgba(139,92,246,0.3));
  font: inherit; font-size: 0.68rem; font-weight: 700; color: #fff;
}
.tb-proj-new:hover:not(:disabled) { border-color: rgba(165,180,252,0.8); }
.tb-proj-new:disabled { opacity: 0.45; cursor: not-allowed; }
.tb-proj-list {
  display: flex; flex-direction: column; gap: 6px;
  max-height: 212px; min-height: 0; overflow-y: auto; margin-right: -6px; padding-right: 6px;
  scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.15) transparent;
}
.tb-proj-row {
  position: relative; display: flex; align-items: center;
  border-radius: 12px; border: 1px solid rgba(255,255,255,0.07); background: rgba(255,255,255,0.03);
  transition: border-color 0.12s, background 0.12s;
}
.tb-proj-row:hover { border-color: rgba(129,140,248,0.45); background: rgba(99,102,241,0.08); }
.tb-proj-row.is-active { border-color: rgba(129,140,248,0.6); background: rgba(99,102,241,0.14); }
.tb-proj-open {
  flex: 1; min-width: 0; display: flex; align-items: center; gap: 10px; padding: 6px 8px;
  border: none; background: transparent; cursor: pointer; text-align: left; font: inherit; color: inherit;
}
.tb-proj-row.is-active .tb-proj-open { cursor: default; }
.tb-proj-open:disabled { cursor: not-allowed; opacity: 0.5; }
.tb-proj-thumb {
  position: relative; flex-shrink: 0; width: 52px; height: 34px; overflow: hidden;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 8px; background: #171a21; border: 1px solid rgba(255,255,255,0.1); color: var(--text-muted);
}
.tb-proj-thumb img, .tb-proj-thumb video { width: 100%; height: 100%; object-fit: cover; pointer-events: none; }
.tb-proj-thumb .tb-tray-badge { left: 2px; bottom: 2px; width: 14px; height: 14px; border-radius: 4px; }
.tb-proj-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.tb-proj-title { font-size: 0.74rem; font-weight: 600; color: #f3f4f6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tb-proj-meta { font-size: 0.62rem; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tb-proj-input {
  width: 100%; box-sizing: border-box; padding: 2px 6px; border-radius: 6px;
  border: 1px solid rgba(129,140,248,0.6); background: rgba(15,17,21,0.9);
  font: inherit; font-size: 0.74rem; color: #fff; outline: none;
}
.tb-proj-badge {
  flex-shrink: 0; margin-right: 5px; padding: 1px 6px; border-radius: 999px;
  background: rgba(52,211,153,0.16); color: #6ee7b7; font-size: 0.58rem; font-weight: 700;
}
.tb-proj-del {
  flex-shrink: 0; margin-right: 6px; width: 24px; height: 24px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; border-radius: 7px; background: transparent; color: var(--text-muted); cursor: pointer;
  opacity: 0; transition: opacity 0.12s;
}
.tb-proj-row:hover .tb-proj-del, .tb-proj-del:focus-visible { opacity: 1; }
.tb-proj-del:hover:not(:disabled) { background: rgba(248,113,113,0.15); color: #fca5a5; }
.tb-proj-del:disabled { cursor: not-allowed; }
.tb-proj-confirm { flex-shrink: 0; display: inline-flex; gap: 4px; margin-right: 6px; }
.tb-proj-confirm button {
  padding: 3px 8px; border-radius: 7px; cursor: pointer; font: inherit; font-size: 0.62rem; font-weight: 700;
  border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.05); color: #e5e7eb;
}
.tb-proj-confirm button.is-danger { border-color: rgba(248,113,113,0.5); background: rgba(127,29,29,0.5); color: #fecaca; }
.tb-gen-collapse { margin-left: auto; }
.tb-gen-preview { margin-bottom: 10px; border-radius: 12px; overflow: hidden; background: #000; border: 1px solid rgba(255,255,255,0.1); }
.tb-gen-preview video { display: block; width: 100%; max-height: 160px; background: #000; }
.tb-gen-list { max-height: 220px; }
.tb-gen-flow { margin: 4px 2px 0; font-size: 0.6rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #a5b4fc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tb-gen-flow:first-child { margin-top: 0; }
.tb-gen-model { font-weight: 500; color: var(--text-muted); }
.tb-proj-row.is-preview { border-color: rgba(167,139,250,0.7); }
.tb-gen-actions { flex-shrink: 0; display: inline-flex; align-items: center; gap: 4px; margin-right: 6px; }
.tb-gen-use {
  padding: 3px 8px; border-radius: 7px; cursor: pointer; font: inherit; font-size: 0.62rem; font-weight: 700;
  border: 1px solid rgba(129,140,248,0.45); background: rgba(99,102,241,0.18); color: #e0e7ff;
}
.tb-gen-use:hover:not(:disabled) { background: rgba(99,102,241,0.32); color: #fff; }
.tb-gen-use:disabled { opacity: 0.45; cursor: not-allowed; }
.tb-gen-active { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 999px; background: rgba(52,211,153,0.16); color: #6ee7b7; }
.tb-gen-open {
  display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px;
  border-radius: 7px; color: var(--text-muted);
}
button.tb-gen-open { padding: 0; border: none; background: transparent; cursor: pointer; }
.tb-gen-open:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-video-versions {
  flex-shrink: 0; display: inline-flex; align-items: center; gap: 2px; padding: 1px 2px;
  border-radius: 8px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
  font-size: 0.64rem; font-weight: 700; color: #e5e7eb; font-variant-numeric: tabular-nums;
}
.tb-video-versions button {
  display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; padding: 0;
  border: none; border-radius: 6px; background: transparent; color: var(--text-muted); cursor: pointer;
}
.tb-video-versions button:hover:not(:disabled) { background: rgba(255,255,255,0.1); color: #fff; }
.tb-video-versions button:disabled { opacity: 0.3; cursor: default; }
.tb-node-meta .tb-video-versions { margin-left: auto; }
.tb-viewer {
  position: absolute; inset: 0; z-index: 70; display: flex;
  background: rgba(6,7,11,0.94); backdrop-filter: blur(6px);
  animation: tb-tutorial-fade 0.16s ease-out;
}
.tb-viewer-stage {
  position: relative; flex: 1; min-width: 0; display: flex; align-items: center; justify-content: center;
  /* Extra left padding clears the fixed app sidebar (~56px) that paints over the stage. */
  padding: 28px 64px 64px 96px;
}
.tb-viewer-stage > img, .tb-viewer-stage > video {
  max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 10px;
  box-shadow: 0 30px 90px rgba(0,0,0,0.6); background: #000;
  position: relative; z-index: 0;
}
.tb-viewer-empty { color: var(--text-muted); font-size: 0.85rem; }
.tb-viewer-nav {
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 2;
  display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; padding: 0;
  border-radius: 999px; border: 1px solid rgba(255,255,255,0.12); background: rgba(22,24,32,0.85);
  color: #e5e7eb; cursor: pointer;
  box-shadow: 0 8px 24px rgba(0,0,0,0.45);
}
.tb-viewer-nav.is-prev { left: 80px; }
.tb-viewer-nav.is-next { right: 14px; }
.tb-viewer-nav:hover:not(:disabled) { background: rgba(99,102,241,0.3); color: #fff; }
.tb-viewer-nav:disabled { opacity: 0.35; cursor: default; }
.tb-viewer-caption {
  position: absolute; left: 50%; bottom: 18px; transform: translateX(-50%); z-index: 2;
  display: inline-flex; align-items: center; gap: 10px; white-space: nowrap;
  font-size: 0.75rem; font-weight: 600; color: #cbd5e1;
}
.tb-viewer-caption button {
  display: inline-flex; align-items: center; gap: 5px; padding: 5px 11px; border-radius: 999px; cursor: pointer;
  font: inherit; font-size: 0.72rem; font-weight: 700;
  border: 1px solid rgba(129,140,248,0.45); background: rgba(99,102,241,0.2); color: #e0e7ff;
}
.tb-viewer-caption button:hover:not(:disabled) { background: rgba(99,102,241,0.35); color: #fff; }
.tb-viewer-caption button:disabled { opacity: 0.45; cursor: not-allowed; }
.tb-viewer-side {
  width: 340px; flex-shrink: 0; display: flex; flex-direction: column; min-height: 0;
  background: rgba(18,20,27,0.98); border-left: 1px solid rgba(255,255,255,0.08);
}
.tb-viewer-side header {
  display: flex; align-items: center; gap: 8px; padding: 14px 14px 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.tb-viewer-side h3 {
  flex: 1; min-width: 0; margin: 0; font-size: 0.9rem; font-weight: 700; color: #f3f4f6;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.tb-viewer-kind { display: inline-flex; color: #a5b4fc; }
.tb-viewer-close {
  display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0;
  border: none; border-radius: 8px; background: transparent; color: var(--text-muted); cursor: pointer;
}
.tb-viewer-close:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-viewer-label {
  padding: 12px 14px 6px; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--text-muted);
}
.tb-viewer-hint { margin: 0 14px 8px; font-size: 0.68rem; color: var(--text-muted); }
.tb-viewer-list { list-style: none; margin: 0; padding: 0 8px 12px; overflow-y: auto; min-height: 0; }
.tb-viewer-item {
  display: flex; align-items: flex-start; gap: 4px; margin-bottom: 4px;
  border-radius: 10px; border: 1px solid transparent;
}
.tb-viewer-item.is-shown { background: rgba(99,102,241,0.12); border-color: rgba(129,140,248,0.4); }
.tb-viewer-open {
  flex: 1; min-width: 0; display: flex; gap: 10px; align-items: flex-start; padding: 8px;
  border: none; background: transparent; cursor: pointer; text-align: left; font: inherit; color: inherit;
}
.tb-viewer-open:hover { background: rgba(255,255,255,0.04); border-radius: 10px; }
.tb-viewer-use {
  flex-shrink: 0; margin: 8px 8px 0 0; padding: 3px 8px; border-radius: 7px; cursor: pointer;
  font: inherit; font-size: 0.62rem; font-weight: 700;
  border: 1px solid rgba(129,140,248,0.45); background: rgba(99,102,241,0.18); color: #e0e7ff;
}
.tb-viewer-use:hover:not(:disabled) { background: rgba(99,102,241,0.32); color: #fff; }
.tb-viewer-use:disabled { opacity: 0.45; cursor: not-allowed; }
.tb-viewer-thumb {
  flex-shrink: 0; width: 76px; height: 54px; border-radius: 7px; overflow: hidden;
  background: #0b0c10; border: 1px solid rgba(255,255,255,0.08);
}
.tb-viewer-thumb img, .tb-viewer-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; pointer-events: none; }
.tb-viewer-item-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.tb-viewer-item-title { display: flex; align-items: baseline; gap: 6px; font-size: 0.76rem; font-weight: 700; color: #f3f4f6; }
.tb-viewer-item-title em {
  font-style: normal; font-size: 0.58rem; font-weight: 700; padding: 1px 6px; border-radius: 999px;
  background: rgba(52,211,153,0.15); color: #6ee7b7;
}
.tb-viewer-item-title small { margin-left: auto; font-size: 0.6rem; font-weight: 500; color: var(--text-muted); }
.tb-viewer-item-meta { font-size: 0.64rem; color: #a5b4fc; }
.tb-viewer-item-text, .tb-viewer-scenes span { font-size: 0.68rem; line-height: 1.4; color: #cbd5e1; overflow-wrap: anywhere; }
.tb-viewer-item-text b, .tb-viewer-scenes b { color: #e5e7eb; font-weight: 700; }
.tb-viewer-scenes { display: flex; flex-direction: column; gap: 2px; margin-top: 2px; }
.tb-viewer-none { display: flex; flex-direction: column; gap: 8px; padding: 0 14px; font-size: 0.75rem; color: var(--text-muted); }
.tb-viewer-none p { margin: 0; }
@media (max-width: 760px) {
  .tb-viewer { flex-direction: column; }
  .tb-viewer-stage { padding: 16px 48px 52px 88px; }
  .tb-viewer-nav.is-prev { left: 72px; }
  .tb-viewer-side { width: auto; max-height: 42%; border-left: none; border-top: 1px solid rgba(255,255,255,0.08); }
}
.tb-edit-thumbs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
.tb-edit-thumbs img {
  width: 72px; height: 54px; object-fit: cover; border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.12); background: #12141b;
}
.tb-page .frag-credit-card {
  width: min(480px, 100%); padding: 1.5rem; border-radius: 22px;
  background: rgba(24,26,33,0.98); border: 1.5px solid rgba(255,255,255,0.1);
  box-shadow: 0 40px 120px rgba(0,0,0,0.7);
}
.tb-page .frag-modal-title { font-size: 1.2rem; font-weight: 800; }
.tb-page .frag-credit-summary { border-radius: 14px; background: rgba(99,102,241,0.08); border-color: rgba(129,140,248,0.25); }
.tb-page .frag-credit-actions { flex-direction: column-reverse; gap: 8px; margin-top: 1rem; }
.tb-page .frag-credit-actions > button { width: 100%; justify-content: center; padding: 0.7rem 1rem; border-radius: 12px; font-weight: 700; }
.tb-page .frag-credit-actions > .btn-primary {
  border: none; color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  box-shadow: 0 12px 28px rgba(99,102,241,0.35);
}
.tb-top-actions {
  position: absolute; top: 14px; right: 104px; z-index: 12;
  display: flex; align-items: center; gap: 8px;
  transition: right 0.45s cubic-bezier(.2,.8,.2,1);
}
.tb-help-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 999px; cursor: pointer;
  font: inherit; font-size: 0.75rem; font-weight: 600; color: #c7d2fe;
  background: rgba(22,24,32,0.9); border: 1px solid rgba(129,140,248,0.35);
}
.tb-help-btn:hover { background: rgba(99,102,241,0.2); color: #fff; }
.tb-tutorial-overlay {
  position: absolute; inset: 0; z-index: 60; padding: 1.25rem;
  display: flex; align-items: center; justify-content: center;
  background: rgba(6,7,11,0.78); backdrop-filter: blur(4px);
  animation: tb-tutorial-fade 0.2s ease-out;
}
.tb-tutorial-card {
  position: relative; width: min(920px, 100%); max-height: 100%; overflow: auto;
  padding: 1.25rem; border-radius: 18px;
  background: #15161d; border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.12);
  animation: tb-tutorial-rise 0.25s ease-out;
}
.tb-tutorial-close {
  position: absolute; top: 12px; right: 12px; z-index: 2; padding: 4px;
  display: inline-flex; border: none; border-radius: 8px; cursor: pointer;
  background: transparent; color: var(--text-muted);
}
.tb-tutorial-close:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-tutorial-head { padding-right: 32px; margin-bottom: 0.9rem; }
.tb-tutorial-badge {
  display: inline-block; margin-bottom: 6px; padding: 2px 9px; border-radius: 999px;
  font-size: 0.65rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: #fff; background: linear-gradient(135deg, #6366f1, #8b5cf6);
}
.tb-tutorial-title { margin: 0; font-size: 1.25rem; color: #fff; }
.tb-tutorial-sub { margin: 0.3rem 0 0; font-size: 0.85rem; line-height: 1.45; color: var(--text-muted); }
.tb-tutorial-video {
  aspect-ratio: 16 / 9; width: 100%; overflow: hidden; border-radius: 12px;
  background: #0b0c10; border: 1px solid rgba(255,255,255,0.08);
}
.tb-tutorial-video video { display: block; width: 100%; height: 100%; object-fit: contain; }
.tb-tutorial-actions { display: flex; justify-content: flex-end; margin-top: 1rem; }
.tb-tutorial-primary {
  padding: 0.6rem 1.2rem; border: none; border-radius: 10px; cursor: pointer;
  font: inherit; font-size: 0.85rem; font-weight: 700; color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6); box-shadow: 0 10px 24px rgba(99,102,241,0.35);
}
.tb-tutorial-primary:hover { filter: brightness(1.08); }
@keyframes tb-tutorial-fade { from { opacity: 0; } }
@keyframes tb-tutorial-rise { from { opacity: 0; transform: translateY(12px) scale(0.98); } }
.frag-credit-overlay {
  position: absolute; inset: 0; z-index: 45; background: rgba(8,9,12,0.72);
  display: flex; align-items: center; justify-content: center; padding: 1rem; overflow: auto;
}
.frag-credit-card {
  width: min(440px, 100%); max-height: min(90vh, 720px); overflow: auto; padding: 1.25rem; position: relative;
  border-radius: 14px; border: 1px solid rgba(255,255,255,0.1); background: #16161e; box-shadow: 0 18px 48px rgba(0,0,0,0.45);
}
.frag-credit-summary {
  display: flex; align-items: center; gap: 10px; padding: 0.85rem 1rem; border-radius: 10px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); margin-bottom: 0.75rem;
}
.frag-credit-summary-label { font-size: 0.7rem; color: var(--text-muted); }
.frag-credit-summary-value { font-size: 1.35rem; font-weight: 700; color: #fff; }
.frag-credit-summary-alt { font-size: 0.68rem; color: var(--text-muted); margin-top: 2px; }
.frag-credit-summary-balance { font-size: 0.95rem; font-weight: 600; }
.frag-credit-actions { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }
.frag-modal-close {
  position: absolute; top: 10px; right: 10px; background: transparent; border: none;
  color: var(--text-muted); cursor: pointer; z-index: 2;
}
.frag-modal-title { margin: 0 0 0.25rem; font-size: 1.05rem; color: #fff; padding-right: 28px; }
.frag-modal-sub { margin: 0 0 0.85rem; font-size: 0.8rem; color: var(--text-muted); line-height: 1.4; }
.frag-error { color: #f87171; font-size: 0.75rem; margin: 0.65rem 0 0; }
.tb-help-btn.is-on { color: #6ee7b7; border-color: rgba(52,211,153,0.45); }
.tb-help-btn.is-locked { color: var(--text-muted); border-color: rgba(255,255,255,0.14); }
.tb-share-chip {
  display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 999px;
  font-size: 0.72rem; font-weight: 600; color: #6ee7b7;
  background: rgba(22,24,32,0.9); border: 1px solid rgba(52,211,153,0.35);
}
.tb-proj-badge.is-shared { display: inline-flex; align-items: center; background: rgba(99,102,241,0.18); color: #c7d2fe; }
.tb-proj-section {
  display: flex; align-items: center; gap: 6px; margin: 10px 4px 4px;
  font-size: 0.62rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-muted);
}

.tb-readonly-bar {
  position: absolute; left: 50%; bottom: 20px; transform: translateX(-50%); z-index: 10;
  display: flex; align-items: center; gap: 12px; max-width: calc(100% - 32px);
  padding: 10px 12px 10px 16px; border-radius: 16px; color: #c7d2fe;
  background: rgba(22,24,32,0.95); border: 1px solid rgba(129,140,248,0.3); box-shadow: 0 18px 40px rgba(0,0,0,0.45);
}
.tb-readonly-bar > span { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.tb-readonly-bar strong { font-size: 0.8rem; color: #fff; }
.tb-readonly-bar small { font-size: 0.68rem; color: var(--text-muted); }
.tb-readonly-bar button {
  flex-shrink: 0; display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 10px; cursor: pointer;
  font: inherit; font-size: 0.72rem; font-weight: 700; color: #fff; border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
}

.tb-node-info {
  position: absolute; top: -10px; left: -10px; z-index: 2;
  width: 22px; height: 22px; border-radius: 999px; padding: 0;
  border: 1px solid rgba(129,140,248,0.45); background: #22252e; color: #c7d2fe;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.4);
  opacity: 0; transition: opacity 0.12s;
}
.tb-node:hover .tb-node-info, .tb-node.is-selected .tb-node-info, .tb-node-info.is-open, .is-readonly .tb-node-info { opacity: 1; }
.tb-node-info:hover, .tb-node-info.is-open { background: #6366f1; color: #fff; border-color: #6366f1; }
.tb-info {
  position: absolute; z-index: 15; box-sizing: border-box; overflow: auto;
  padding: 14px 16px 16px; border-radius: 14px; cursor: default;
  background: rgba(20,22,29,0.98); border: 1px solid rgba(129,140,248,0.3);
  box-shadow: 0 24px 60px rgba(0,0,0,0.55); color: #e5e7eb; font-size: 0.76rem; line-height: 1.45;
}
.tb-info h3 { margin: 0 24px 2px 0; font-size: 0.9rem; color: #fff; }
.tb-info-time { font-size: 0.68rem; color: #a5b4fc; margin-bottom: 10px; }
.tb-info-close {
  position: absolute; top: 10px; right: 10px; width: 22px; height: 22px; padding: 0; border-radius: 7px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  border: none; background: transparent; color: var(--text-muted);
}
.tb-info-close:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-info-field { margin-top: 9px; }
.tb-info-field > span {
  display: block; margin-bottom: 2px; font-size: 0.6rem; font-weight: 700;
  letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-muted);
}
.tb-info-field > div { white-space: pre-wrap; }
.tb-info-list { margin: 2px 0 0; padding-left: 18px; display: flex; flex-direction: column; gap: 6px; }
.tb-info-list em { font-style: normal; color: #a5b4fc; font-size: 0.66rem; }
.tb-info-list p { margin: 2px 0 0; color: #cbd5e1; }
.tb-info-note { margin: 6px 0 0; font-size: 0.68rem; color: #a5b4fc; }
.tb-info-prompt { margin-top: 10px; }
.tb-info-prompt summary { cursor: pointer; font-size: 0.68rem; color: #a5b4fc; }
.tb-info-prompt p { margin: 6px 0 0; color: #cbd5e1; font-size: 0.7rem; }

.tb-share-card {
  position: relative; width: min(520px, 100%); max-height: min(88vh, 720px); overflow: auto; box-sizing: border-box;
  padding: 1.35rem; border-radius: 20px; color: #e5e7eb;
  background: rgba(24,26,33,0.98); border: 1.5px solid rgba(255,255,255,0.1); box-shadow: 0 40px 120px rgba(0,0,0,0.7);
}
.tb-share-close {
  position: absolute; top: 12px; right: 12px; width: 30px; height: 30px; padding: 0; border-radius: 9px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  border: none; background: transparent; color: var(--text-muted);
}
.tb-share-close:hover { background: rgba(255,255,255,0.08); color: #fff; }
.tb-share-title { display: flex; align-items: center; gap: 8px; margin: 0 36px 4px 0; font-size: 1.05rem; color: #fff; }
.tb-share-sub { margin: 0 0 14px; font-size: 0.76rem; color: var(--text-muted); line-height: 1.45; }
.tb-share-locked {
  display: flex; align-items: center; gap: 12px; padding: 14px; border-radius: 14px;
  background: rgba(99,102,241,0.08); border: 1px solid rgba(129,140,248,0.25); color: #c7d2fe;
}
.tb-share-locked > div { flex: 1; display: flex; flex-direction: column; gap: 3px; }
.tb-share-locked strong { color: #fff; font-size: 0.85rem; }
.tb-share-locked span { font-size: 0.72rem; color: var(--text-muted); }
.tb-share-loading { display: flex; align-items: center; gap: 8px; padding: 16px 4px; font-size: 0.78rem; color: var(--text-muted); }
.tb-share-row {
  display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 12px; cursor: pointer;
  border: 1px solid transparent;
}
.tb-share-row:hover { background: rgba(255,255,255,0.04); }
.tb-share-row.is-on { background: rgba(99,102,241,0.08); border-color: rgba(129,140,248,0.25); }
.tb-share-row input[type="checkbox"] { accent-color: #6366f1; width: 15px; height: 15px; flex-shrink: 0; }
.tb-share-all { margin-bottom: 8px; border-color: rgba(255,255,255,0.08); }
.tb-share-chat { margin-top: 10px; border-color: rgba(255,255,255,0.08); }
.tb-share-chat.is-disabled { opacity: 0.55; }
.tb-share-avatar.is-chat { background: rgba(129,140,248,0.18); color: #a5b4fc; }
.tb-share-avatar.is-external { background: rgba(251,191,36,0.2); color: #fbbf24; }
.tb-share-email {
  display: flex; align-items: center; gap: 8px; margin: 0 0 10px; padding: 8px 10px;
  border-radius: 12px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.03);
  color: var(--text-muted);
}
.tb-share-email input[type="email"] {
  flex: 1; min-width: 0; border: none; background: transparent; color: #fff;
  font: inherit; font-size: 0.78rem; outline: none;
}
.tb-share-confirm {
  position: absolute; inset: 0; z-index: 3; display: flex; flex-direction: column; justify-content: center;
  gap: 12px; padding: 1.5rem; border-radius: 20px;
  background: rgba(12,14,20,0.94); backdrop-filter: blur(8px); color: #e5e7eb;
}
.tb-share-confirm h3 { margin: 0; font-size: 1rem; color: #fff; }
.tb-share-confirm p { margin: 0; font-size: 0.82rem; line-height: 1.5; color: #cbd5e1; }
.tb-share-avatar {
  flex-shrink: 0; width: 30px; height: 30px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.78rem; font-weight: 700; color: #fff; background: linear-gradient(135deg, #6366f1, #8b5cf6);
}
.tb-share-avatar.is-org { background: rgba(52,211,153,0.18); color: #6ee7b7; }
.tb-share-who { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.tb-share-who strong { font-size: 0.8rem; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-share-who small { font-size: 0.68rem; color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-share-access {
  flex-shrink: 0; padding: 5px 8px; border-radius: 8px; cursor: pointer;
  font: inherit; font-size: 0.72rem; color: #e5e7eb;
  background: #1b1d25; border: 1px solid rgba(255,255,255,0.14);
}
.tb-share-access:disabled { opacity: 0.4; cursor: default; }
.tb-share-search {
  display: flex; align-items: center; gap: 8px; margin-bottom: 6px; padding: 7px 10px; border-radius: 10px;
  background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.1); color: var(--text-muted);
}
.tb-share-search input { flex: 1; border: none; outline: none; background: transparent; color: #fff; font: inherit; font-size: 0.78rem; }
.tb-share-list { display: flex; flex-direction: column; gap: 4px; max-height: 320px; overflow: auto; }
.tb-share-empty { padding: 12px 4px; font-size: 0.74rem; color: var(--text-muted); }
.tb-share-note { margin: 10px 2px 0; font-size: 0.68rem; color: var(--text-muted); }
.tb-share-error { margin: 10px 0 0; font-size: 0.74rem; color: #f87171; }
.tb-share-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.tb-share-primary, .tb-share-ghost {
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 10px; cursor: pointer;
  font: inherit; font-size: 0.78rem; font-weight: 700;
}
.tb-share-primary { border: none; color: #fff; background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.tb-share-primary:disabled, .tb-share-ghost:disabled { opacity: 0.5; cursor: default; }
.tb-share-ghost { background: transparent; color: #e5e7eb; border: 1px solid rgba(255,255,255,0.14); }
.tb-share-ghost.is-danger { color: #fca5a5; border-color: rgba(248,113,113,0.35); margin-right: auto; }

.tb-viewport.is-space { cursor: grab; }
.tb-viewport.is-selecting { cursor: crosshair; }
.tb-marquee {
  position: absolute; z-index: 5; pointer-events: none;
  border: 1px solid rgba(129,140,248,0.85); background: rgba(99,102,241,0.12); border-radius: 3px;
}
.tb-role-chip {
  position: absolute; top: 6px; left: 6px; z-index: 2;
  padding: 2px 7px; border-radius: 999px; font-size: 0.6rem; font-weight: 700; letter-spacing: 0.02em;
  background: rgba(17,18,40,0.88); border: 1px solid rgba(255,255,255,0.18); color: #e5e7eb; white-space: nowrap;
}
.tb-role-chip.is-start { color: #6ee7b7; border-color: rgba(52,211,153,0.5); }
.tb-role-chip.is-end { color: #fca5a5; border-color: rgba(248,113,113,0.5); }
.tb-role-chip.is-keyframe { color: #fcd34d; border-color: rgba(251,191,36,0.5); }
.tb-role-chip.is-reference { color: #c4b5fd; border-color: rgba(167,139,250,0.5); border-style: dashed; }
.tb-mode-toggle {
  position: absolute; top: 6px; left: 6px; z-index: 2;
  display: inline-flex; padding: 2px; gap: 2px; border-radius: 999px;
  background: rgba(17,18,40,0.9); border: 1px solid rgba(255,255,255,0.14);
}
.tb-mode-toggle button {
  border: none; background: transparent; color: #9ca3af; cursor: pointer;
  padding: 2px 8px; border-radius: 999px; font: inherit; font-size: 0.6rem; font-weight: 700;
}
.tb-mode-toggle button.is-on { background: rgba(99,102,241,0.35); color: #fff; }
.tb-mode-toggle button:disabled { cursor: default; opacity: 0.6; }
.tb-mode-toggle button:focus-visible { outline: 2px solid rgba(129,140,248,0.8); outline-offset: 1px; }
.tb-edge-line.is-bundle { stroke: rgba(165,180,252,0.55); stroke-dasharray: 3 5; }
.tb-batch-anchor {
  position: absolute; z-index: 6; transform: translate(-8px, -50%);
  display: inline-flex; align-items: center; gap: 7px; padding: 5px 11px 5px 5px;
  border-radius: 999px; border: 1px solid rgba(129,140,248,0.7);
  background: rgba(30,27,75,0.95); color: #e0e7ff; box-shadow: 0 8px 22px rgba(0,0,0,0.45);
  font: inherit; font-size: 0.7rem; font-weight: 700; white-space: nowrap; cursor: crosshair;
}
.tb-batch-anchor:hover { background: #4338ca; border-color: #a5b4fc; }
.tb-batch-anchor:focus-visible { outline: 2px solid #a5b4fc; outline-offset: 2px; }
.tb-batch-dot {
  width: 14px; height: 14px; border-radius: 999px; box-sizing: border-box;
  background: #6366f1; border: 2.5px solid #c7d2fe;
}
.tb-video-refs {
  position: absolute; left: 18px; right: 18px; bottom: 100%; margin-bottom: 6px;
  display: flex; gap: 3px; pointer-events: auto;
}
.tb-video-ref {
  flex: 0 0 auto; width: 26px; height: 26px; border-radius: 6px; overflow: hidden; box-sizing: border-box;
  border: 1px solid rgba(167,139,250,0.55); background: #12141b;
  display: inline-flex; align-items: center; justify-content: center; color: #a5b4fc;
}
.tb-video-ref img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tb-video-ref.is-pending { border-style: dashed; }
.tb-video-ref.is-more { width: auto; padding: 0 6px; font-size: 0.6rem; font-weight: 700; color: #c4b5fd; }
.tb-video-download {
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; padding: 0; border-radius: 8px; cursor: pointer;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); color: #e5e7eb;
}
.tb-video-download:hover { background: rgba(99,102,241,0.25); color: #fff; }
.tb-video-title + .tb-video-download { margin-left: auto; }

.tb-ctx {
  position: absolute; z-index: 40; display: flex; flex-direction: column; overflow: hidden;
  border-radius: 12px; background: rgba(21,22,29,0.98); border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(99,102,241,0.1);
  animation: tb-bubble-in 0.14s ease-out;
}
.tb-ctx-search {
  display: flex; align-items: center; gap: 8px; padding: 9px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.08); color: var(--text-muted);
}
.tb-ctx-search input {
  flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  color: #f3f4f6; font: inherit; font-size: 0.8rem;
}
.tb-ctx-list { overflow-y: auto; padding: 4px; scrollbar-width: thin; }
.tb-ctx-group {
  padding: 8px 8px 4px; font-size: 0.58rem; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-muted);
}
.tb-ctx-item {
  width: 100%; display: flex; align-items: center; gap: 10px; padding: 7px 8px; border-radius: 8px;
  border: none; background: transparent; color: #e5e7eb; cursor: pointer; text-align: left; font: inherit;
}
.tb-ctx-item.is-active { background: rgba(99,102,241,0.2); }
.tb-ctx-icon {
  flex-shrink: 0; width: 26px; height: 26px; border-radius: 7px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.06); color: #a5b4fc;
}
.tb-ctx-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.tb-ctx-text strong { font-size: 0.76rem; font-weight: 600; }
.tb-ctx-text small { font-size: 0.64rem; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tb-ctx-item kbd {
  flex-shrink: 0; padding: 1px 6px; border-radius: 5px; font: inherit; font-size: 0.62rem; font-weight: 600;
  color: #9ca3af; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
}
.tb-ctx-empty { margin: 0; padding: 14px 10px; font-size: 0.74rem; color: var(--text-muted); text-align: center; }

.tb-context-break {
  align-self: center; display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px; border-radius: 999px; font-size: 0.62rem; font-weight: 600;
  color: #fcd34d; background: rgba(251,191,36,0.08); border: 1px dashed rgba(251,191,36,0.35);
}

.tb-aspect-select {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 4px 3px 12px; border-radius: 999px;
  font-size: 0.72rem; font-weight: 600; color: #c7d2fe;
  background: rgba(22,24,32,0.9); border: 1px solid rgba(129,140,248,0.35);
}
.tb-aspect-select select {
  font: inherit; font-size: 0.72rem; color: #f3f4f6; background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12); border-radius: 999px; padding: 3px 8px; cursor: pointer;
}
.tb-aspect-select select option { background: #15161d; }

.tb-done-card {
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); z-index: 30;
  width: min(380px, calc(100% - 32px)); box-sizing: border-box; padding: 1.4rem 1.4rem 1.2rem;
  display: flex; flex-direction: column; align-items: center; text-align: center; gap: 8px;
  border-radius: 18px; background: #15161d; border: 1px solid rgba(52,211,153,0.35);
  box-shadow: 0 30px 80px rgba(0,0,0,0.6), 0 0 0 6px rgba(52,211,153,0.06);
  animation: tb-done-in 0.24s ease-out;
}
@keyframes tb-done-in { from { opacity: 0; transform: translate(-50%, -46%) scale(0.97); } }
.tb-done-icon { color: #34d399; }
.tb-done-card h3 { margin: 0; font-size: 1rem; font-weight: 700; color: #f3f4f6; }
.tb-done-card p { margin: 0 0 6px; font-size: 0.78rem; line-height: 1.45; color: var(--text-muted); }
.tb-done-actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
.tb-done-primary, .tb-done-secondary {
  display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 10px;
  cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 700;
}
.tb-done-primary { border: none; color: #fff; background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.tb-done-secondary { background: transparent; color: #e5e7eb; border: 1px solid rgba(255,255,255,0.14); }
.tb-done-primary:focus-visible, .tb-done-secondary:focus-visible { outline: 2px solid rgba(129,140,248,0.8); outline-offset: 2px; }

.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .spin, .tb-rec-dot, .tb-done-card, .tb-ctx, .tb-edge-energy, .tb-edge-energy.is-loop, .tb-receive-ring rect, .tb-receive-ring.is-loop rect, .tb-node.is-receive, .tb-node.is-receive.is-generating, .tb-pixel-grid i { animation: none; }
  .tb-pixel-grid i { opacity: 0.85; }
  .tb-prompt, .tb-top-actions, .floating-user-menu, .tb-viewport { transition: none; }
}
`;
